(() => {
  // frontend/js/scission-lots.js
  function partitionner(recherches, chaudes) {
    const toutes = Array.from(recherches || []);
    if (toutes.length === 0) return [];
    const estChaude = new Set(
      Array.from(chaudes || []).map(Number).filter((i) => Number.isInteger(i) && i >= 0 && i < toutes.length)
    );
    const positionsChaudes2 = [];
    const positionsFroides = [];
    toutes.forEach((_, i) => (estChaude.has(i) ? positionsChaudes2 : positionsFroides).push(i));
    return [
      _lot(toutes, positionsChaudes2, true),
      _lot(toutes, positionsFroides, false)
    ].filter(Boolean);
  }
  function _lot(toutes, positions, chaud) {
    if (positions.length === 0) return null;
    return {
      chaud,
      positions,
      recherches: positions.map((i) => toutes[i]),
      /**
       * `critiques` dit quelles erreurs emportent le lot (la grille). Après
       * scission, la grille n'est plus à l'indice 0 : sans traduction, un échec
       * de grille cesserait d'être fatal et la page s'afficherait vide au lieu
       * de signaler la panne.
       *
       * `null` reste `null` : c'est « toutes les erreurs sont fatales ».
       */
      critiquesLocales(critiques) {
        if (critiques === null || critiques === void 0) return null;
        const voulues = new Set(Array.from(critiques).map(Number));
        const locales = [];
        positions.forEach((globale, locale) => {
          if (voulues.has(globale)) locales.push(locale);
        });
        return locales;
      }
    };
  }
  function recomposer(lots, reponses) {
    const total = (lots || []).reduce(
      (max, lot) => Math.max(max, ...lot.positions.map((p) => p + 1)),
      0
    );
    const resultats = new Array(total).fill(void 0);
    (lots || []).forEach((lot, i) => {
      const rendus = (reponses || [])[i] || [];
      lot.positions.forEach((globale, locale) => {
        resultats[globale] = rendus[locale];
      });
    });
    return resultats;
  }

  // frontend/js/moteur.js
  var PLAFOND_MINIMUM = 50;
  var MARGE_PLAFOND = 20;
  var TTL_CACHE_DEFAUT = 60;
  var TTL_CACHE_FROID_DEFAUT = 43200;
  function urlMultiSearch(base, {
    nombre: nombre2 = 0,
    cache = true,
    ttl = TTL_CACHE_DEFAUT
  } = {}) {
    const racine = String(base).replace(/\/+$/, "");
    const parametres = new URLSearchParams({
      limit_multi_searches: String(
        Math.max(PLAFOND_MINIMUM, Number(nombre2) + MARGE_PLAFOND)
      ),
      enable_analytics: "false"
    });
    if (cache && Number(ttl) > 0) {
      parametres.set("use_cache", "true");
      parametres.set("cache_ttl", String(Number(ttl)));
    }
    return `${racine}/multi_search?${parametres}`;
  }
  function estCleScopee(cle) {
    if (typeof cle !== "string" || !cle) return false;
    let decode;
    try {
      decode = typeof atob === "function" ? atob(cle) : Buffer.from(cle, "base64").toString("binary");
    } catch (e) {
      return false;
    }
    const debut = decode.indexOf("{");
    if (debut < 0) return false;
    try {
      return typeof JSON.parse(decode.slice(debut)) === "object";
    } catch (e) {
      return false;
    }
  }
  function creerMoteur({
    base,
    cle,
    cache = true,
    ttl = TTL_CACHE_DEFAUT,
    ttlFroid = TTL_CACHE_FROID_DEFAUT
  }) {
    async function envoyer(recherches, { critiques, ttlDuLot }) {
      const reponse = await fetch(
        urlMultiSearch(base, { nombre: recherches.length, cache, ttl: ttlDuLot }),
        {
          method: "POST",
          headers: { "X-TYPESENSE-API-KEY": cle, "Content-Type": "application/json" },
          body: JSON.stringify({ searches: recherches })
        }
      );
      if (!reponse.ok) {
        const corps = (await reponse.text()).slice(0, 300);
        throw new Error(`HTTP ${reponse.status} — ${corps}`);
      }
      const donnees = await reponse.json();
      const resultats = donnees.results || [];
      const fatale = (i) => critiques === null || critiques === void 0 || critiques.includes(i);
      const fautif = resultats.findIndex((r, i) => r && r.error && fatale(i));
      if (fautif !== -1) throw new Error(resultats[fautif].error);
      return resultats;
    }
    return {
      /**
       * Exécute un lot de recherches. Renvoie le tableau des résultats, dans
       * l'ordre d'envoi.
       *
       * `multi_search` répond 200 même quand une recherche du lot échoue : l'objet
       * fautif porte alors `error` et un `code`. Sans vérification, une erreur de
       * filtre passerait pour un résultat vide — donc pour un catalogue sans
       * produit.
       *
       * `critiques` dit LESQUELLES de ces erreurs doivent emporter le lot.
       *
       * ⛔ Par défaut, toutes — c'est le comportement d'origine, et il est faux
       * pour un cycle de page de catégorie. Mesuré le 03/09 sur le cluster : une
       * facette portant sur un champ absent du schéma renvoie `Could not find a
       * facet field named X`, en **erreur isolée** dans son propre résultat, la
       * grille du même lot répondant normalement à côté. Faire lever le tout
       * transforme donc un bloc de filtre manquant en page vide — et interdit
       * d'écarter le champ fautif pour recommencer, puisqu'on ne voit jamais son
       * nom.
       *
       * L'appelant passe donc les positions dont l'échec est vraiment fatal (la
       * grille) ; les autres reviennent telles quelles, et `valeursFacette`,
       * `champsNonVides` et `premierPrix` traitent déjà un résultat sans
       * `facet_counts` ni `hits` comme vide.
       */
      async multi(recherches, { critiques = null, chaudes = null } = {}) {
        const lots = chaudes ? partitionner(recherches, chaudes) : [];
        if (lots.length < 2) {
          return envoyer(recherches, { critiques, ttlDuLot: ttl });
        }
        const reponses = await Promise.all(lots.map((lot) => envoyer(lot.recherches, {
          critiques: lot.critiquesLocales(critiques),
          ttlDuLot: lot.chaud ? ttl : ttlFroid
        })));
        return recomposer(lots, reponses);
      },
      /**
       * La recherche témoin : la SEULE que les analytics du cluster comptent.
       *
       * `temoin.js` décide QUAND (requête posée, pas une frappe) ; ici, COMMENT :
       * - hors cache : une réponse servie par le cache du moteur repart avant
       *   l'exécution des recherches, donc n'est jamais comptée ;
       * - `x-typesense-user-id` : le cluster regroupe par visiteur, et sans lui il
       *   prend l'IP, que des visiteurs partagent derrière un même réseau ;
       * - requête SIMPLE (clé dans l'URL, corps en `text/plain`), avec
       *   `keepalive` : le témoin le plus précieux part au clic sur un produit,
       *   quand la page se ferme, et elle n'attendrait pas un pré-vol `OPTIONS`.
       *   Mesuré sur le cluster le 22/10/2026 : clé en URL et corps `text/plain`
       *   rendent la même réponse qu'en-tête et JSON.
       *
       * Ne lève jamais : un comptage perdu ne doit pas casser une page.
       */
      async temoin(recherche, { visiteur = "" } = {}) {
        const parametres = new URLSearchParams({ "x-typesense-api-key": cle });
        if (visiteur) parametres.set("x-typesense-user-id", visiteur);
        try {
          const reponse = await fetch(
            `${String(base).replace(/\/+$/, "")}/multi_search?${parametres}`,
            {
              method: "POST",
              keepalive: true,
              headers: { "Content-Type": "text/plain;charset=UTF-8" },
              body: JSON.stringify({ searches: [{ ...recherche, enable_analytics: true }] })
            }
          );
          return !!(reponse && reponse.ok);
        } catch (e) {
          return false;
        }
      },
      /**
       * Noms des alias existants, pour la bascule de langue du bandeau de recette.
       *
       * ⚠️ `/aliases` exige une clé d'ADMINISTRATION. En recette locale la clé est
       * la même, donc l'appel passe ; sur un déploiement réel le front porte une
       * clé de recherche restreinte et l'appel échouera — c'est voulu, et
       * l'appelant doit retomber sur la collection courante plutôt que de traiter
       * ça comme une panne. Ce n'est pas un besoin du site, seulement de la page
       * de recette.
       */
      async alias() {
        const reponse = await fetch(`${base}/aliases`, {
          headers: { "X-TYPESENSE-API-KEY": cle }
        });
        if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
        const donnees = await reponse.json();
        return (donnees.aliases || []).map((a) => a.name);
      },
      /**
       * Schéma d'une collection : ce qu'elle porte réellement comme champs.
       *
       * ⛔ Indispensable avant de facetter. Typesense refuse un champ absent du
       * schéma, et l'échec emporte la requête entière — 12 des 169 filtres du
       * périmètre n'ont aucune valeur côté catalogue français, donc n'existent
       * pas dans l'index, et un seul suffit à faire échouer la découverte.
       *
       * Renvoie `null` si l'appel échoue, plutôt que de lever : l'appelant doit
       * pouvoir se passer des filtres métier et servir la grille quand même.
       */
      async schema(collection) {
        if (estCleScopee(cle)) return null;
        try {
          const reponse = await fetch(`${base}/collections/${collection}`, {
            headers: { "X-TYPESENSE-API-KEY": cle }
          });
          if (!reponse.ok) return null;
          return await reponse.json();
        } catch (e) {
          return null;
        }
      }
    };
  }
  function valeursFacette(resultat, champ) {
    const blocs = resultat && resultat.facet_counts || [];
    const bloc2 = blocs.find((b) => b.field_name === champ);
    return bloc2 && bloc2.counts || [];
  }
  function nombreDeValeurs(resultat, champ) {
    const blocs = resultat && resultat.facet_counts || [];
    const bloc2 = blocs.find((b) => b && b.field_name === champ);
    if (!bloc2) return 0;
    const annonce = bloc2.stats && bloc2.stats.total_values;
    return Number.isFinite(annonce) ? annonce : (bloc2.counts || []).length;
  }
  function statsFacette(resultat, champ) {
    const blocs = resultat && resultat.facet_counts || [];
    const bloc2 = blocs.find((b) => b && b.field_name === champ);
    const stats = bloc2 && bloc2.stats;
    if (!stats || !Number.isFinite(stats.min) || !Number.isFinite(stats.max)) {
      return null;
    }
    return stats;
  }
  function moyennePrix(resultat, champ) {
    const blocs = resultat && resultat.facet_counts || [];
    const bloc2 = blocs.find((b) => b && b.field_name === champ);
    const moyenne = bloc2 && bloc2.stats && bloc2.stats.avg;
    return Number.isFinite(moyenne) ? moyenne : null;
  }
  function premierPrix(resultat, champ = "price") {
    const document2 = resultat && resultat.hits && resultat.hits[0] && resultat.hits[0].document;
    const prix = document2 && (Number.isFinite(document2[champ]) ? document2[champ] : document2.price);
    return Number.isFinite(prix) ? prix : null;
  }

  // frontend/js/arbre-facette.js
  var SEPARATEUR = " > ";
  var NIVEAU_MAX = 3;
  var CLE = "chemin";
  function niveauDe(valeur) {
    if (!valeur) return 0;
    return String(valeur).split(SEPARATEUR).length;
  }
  function feuille(valeur) {
    if (!valeur) return "";
    const segments = String(valeur).split(SEPARATEUR);
    return segments[segments.length - 1];
  }
  function champDuFiltre(valeur) {
    const niveau = niveauDe(valeur);
    return niveau ? `${CLE}_${Math.min(niveau, NIVEAU_MAX)}` : `${CLE}_1`;
  }
  function champAFacetter(valeur) {
    const niveau = niveauDe(valeur);
    if (niveau >= NIVEAU_MAX) return null;
    return `${CLE}_${niveau + 1}`;
  }
  function filtreArbre(valeur) {
    if (!valeur) return "";
    const texte = String(valeur);
    if (texte.includes("`")) {
      throw new Error(
        `Chemin de collection contenant un accent grave, non filtrable : ${texte}`
      );
    }
    return `${champDuFiltre(texte)}:=[\`${texte}\`]`;
  }
  function descendre(ouverte, cible) {
    if (!cible) return ouverte || "";
    if (ouverte === cible) return remonter(cible, niveauDe(cible) - 1);
    return String(cible);
  }
  function cranRetour(ouverte) {
    const niveau = niveauDe(ouverte) - 1;
    if (niveau < 0) return null;
    return { niveau, libelle: niveau ? feuille(remonter(ouverte, niveau)) : null };
  }
  function remonter(valeur, niveau) {
    if (!valeur || niveau <= 0) return "";
    return String(valeur).split(SEPARATEUR).slice(0, niveau).join(SEPARATEUR);
  }

  // frontend/js/mesures.js
  var UNITES = Object.freeze({
    mm: ["longueur", 0.1],
    cm: ["longueur", 1],
    m: ["longueur", 100],
    '"': ["longueur", 2.54],
    "”": ["longueur", 2.54],
    "″": ["longueur", 2.54],
    W: ["puissance", 1],
    V: ["tension", 1],
    "Ω": ["impedance", 1],
    "": ["nombre", 1]
  });
  var CANONIQUE = Object.freeze({
    longueur: "cm",
    puissance: "W",
    tension: "V",
    impedance: "Ω",
    nombre: ""
  });
  var MOTIF = /^\s*(-?\d+(?:[.,]\d+)?)\s*([^\d\s]*)\s*$/;
  function uniteDepuisValeur(valeur) {
    const trouve = MOTIF.exec(String(valeur != null ? valeur : ""));
    return trouve ? trouve[2] : "";
  }
  function uniteCanonique(unite) {
    const connue = UNITES[String(unite != null ? unite : "")];
    return connue ? CANONIQUE[connue[0]] : "";
  }
  var SUFFIXE_NUMERIQUE = "_val";
  function champMesure(champ, numeriques = {}) {
    return numeriques && numeriques[champ] ? champ : `${champ}${SUFFIXE_NUMERIQUE}`;
  }
  function fondreStats(liste2) {
    let min = null;
    let max = null;
    for (const stats of liste2 || []) {
      if (!stats || !Number.isFinite(stats.min) || !Number.isFinite(stats.max)) {
        continue;
      }
      min = min === null ? stats.min : Math.min(min, stats.min);
      max = max === null ? stats.max : Math.max(max, stats.max);
    }
    return min === null ? null : { min, max };
  }
  function mesureCanonique(valeur) {
    const trouve = MOTIF.exec(String(valeur != null ? valeur : ""));
    if (!trouve) return null;
    const connue = UNITES[trouve[2]];
    if (!connue) return null;
    const nombre2 = Number.parseFloat(trouve[1].replace(",", "."));
    return Number.isFinite(nombre2) ? nombre2 * connue[1] : null;
  }

  // frontend/js/requetes.js
  function filtreDe(champ, valeurs) {
    const liste2 = [...valeurs || []];
    if (!liste2.length) return "";
    return `${champ}:=[${liste2.map(entreAccents).join(",")}]`;
  }
  var BOOLEENS = /* @__PURE__ */ new Set(["true", "false"]);
  var ENTIER = /^\d+$/;
  function entreAccents(valeur) {
    const texte = String(valeur);
    if (BOOLEENS.has(texte) || ENTIER.test(texte)) return texte;
    if (texte.includes("`")) {
      throw new Error(
        `Valeur de facette contenant un accent grave, non filtrable : ${texte}`
      );
    }
    return `\`${texte}\``;
  }
  function filtreCombine(selection, { sauf, membres = {} } = {}) {
    return Object.entries(selection || {}).filter(([groupe]) => groupe !== sauf).map(([groupe, valeurs]) => {
      if (groupe === CLE) return filtreArbre((valeurs || [])[0]);
      const champs = (membres || {})[groupe] || [groupe];
      const morceaux = champs.map((champ) => filtreDe(champ, valeurs)).filter(Boolean);
      if (!morceaux.length) return "";
      return morceaux.length === 1 ? morceaux[0] : `(${morceaux.join(" || ")})`;
    }).filter(Boolean).join(" && ");
  }
  function filtrePrix({ de, a, inclusifHaut } = {}, champ = "price") {
    const morceaux = [];
    if (Number.isFinite(de)) morceaux.push(`${champ}:>=${de}`);
    if (Number.isFinite(a)) morceaux.push(`${champ}:${inclusifHaut ? "<=" : "<"}${a}`);
    return morceaux.join(" && ");
  }
  function joindre(...filtres) {
    return filtres.filter(Boolean).join(" && ");
  }
  function filtreNote(note) {
    return Number.isFinite(note) ? `review_rating:>=${note}` : "";
  }
  function champFacetteCurseur(champ, numeriques = {}) {
    const mesure = champMesure(champ, numeriques);
    return mesure === champ ? champ : `${champ},${mesure}`;
  }
  function bornesSur(champ, bornes) {
    const bouts = [];
    if (Number.isFinite(bornes.de)) bouts.push(`${champ}:>=${bornes.de}`);
    if (Number.isFinite(bornes.a)) bouts.push(`${champ}:<=${bornes.a}`);
    return bouts.join(" && ");
  }
  function filtreMesures(mesures = {}, {
    sauf = null,
    numeriques = {},
    membres = {}
  } = {}) {
    const morceaux = [];
    for (const [groupe, bornes] of Object.entries(mesures || {})) {
      if (!bornes || groupe === sauf) continue;
      const champs = (membres || {})[groupe] || [groupe];
      const parMembre = champs.map((champ) => bornesSur(champMesure(champ, numeriques), bornes)).filter(Boolean);
      if (!parMembre.length) continue;
      morceaux.push(parMembre.length === 1 ? parMembre[0] : `(${parMembre.map((m) => m.includes(" && ") ? `(${m})` : m).join(" || ")})`);
    }
    return morceaux.join(" && ");
  }
  function lotDeRecherches({
    collection,
    q,
    parametres = {},
    facettes = [],
    selection = {},
    prix = null,
    note = null,
    segments = [],
    notes = [],
    bornes = false,
    rechercheFacette = {},
    perimetre = "",
    decouverte = [],
    /* Plafond de valeurs PAR CHAMP, pour une facette dépliée par paliers
       (`facettes.PALIER_VALEURS`). Sans lui, une facette dépliée s'arrêterait aux
       100 valeurs du paramètre de parité : la liste paraîtrait complète, et les
       valeurs suivantes seraient inatteignables sans le moindre signal. */
    maxValeursParChamp = {},
    /* Tri des VALEURS par champ (`reference/reglages_facettes.csv`) : `alpha` est
       demandé au moteur, `compte` est son défaut, `manuel` se joue à l'affichage.
       ⛔ Seul le `facet_by` porte l'expression de tri ; la clé de lecture du
       résultat reste le nom NU du champ, celui que Typesense renvoie dans
       `field_name`. Les confondre rendrait la facette vide en cherchant la
       réponse sous un nom qui n'existe pas. */
    triParChamp = {},
    /* Style par champ : `curseur` change deux choses. La facette porte alors sur
       le champ COMPAGNON numérique (`ak_x_val`, en unité unique) et non sur le
       texte — « 419.1 mm » et « 43.18 cm » ne se comparent pas comme des chaînes
       — et on ne lui demande aucune valeur : seules les `stats` sont lues, où
       `min` et `max` viennent gratuitement. Zéro recherche de plus. */
    stylesParChamp = {},
    /* Champs dont l'attribut Akeneo est DÉJÀ numérique : leur curseur porte sur
       le champ nu, les autres sur le compagnon `_val`. Sans cette table, 16 des
       18 curseurs facettaient un champ absent du schéma. */
    numeriquesParChamp = {},
    /* Plages numériques choisies par le visiteur : `{champ: {de, a}}`, en unité
       canonique. Une borne nulle est un demi-filtre valide. */
    mesures = {},
    /* Le champ de prix du MARCHÉ (`price_de`…) : filtre, bornes et histogramme
       portent sur le prix que le visiteur voit, pas sur le prix de base. */
    champPrix = "price",
    /* Facettes fusionnées (`groupes-facettes.js`) : `{clé de groupe: [champs]}`
       et le chemin inverse. Douze libellés du périmètre sont portés par plusieurs
       champs Akeneo — trois « Matériau », trois « Catégorie » — et le visiteur
       n'en voit qu'un bloc. Les deux tables sont vides quand rien n'est fusionné,
       et le lot est alors mot pour mot celui d'avant la fusion. */
    membresParGroupe = {},
    groupeDe = {},
    /* ⚡ Champs renvoyés par la recherche de GRILLE (`carte.champsDeCarte`) :
       sans eux, chaque hit ramène le document entier. Vide = document entier,
       ce que veut la recette locale. Les autres recherches du lot ne renvoient
       aucun document (`per_page: 0`), ou déjà les leurs (bornes). */
    champsGrille = ""
  }) {
    const membres = membresParGroupe;
    const groupe = (champ) => (groupeDe || {})[champ] || champ;
    const base = { ...parametres };
    delete base.facet_by;
    delete base.per_page;
    delete base.page;
    const maxValeurs = parametres.max_facet_values;
    const filtrePrixActif = prix ? filtrePrix({ ...prix, inclusifHaut: true }, champPrix) : "";
    const filtreNoteActif = filtreNote(note);
    const recherches = [];
    const ajouter = (champs) => {
      const filtre = joindre(perimetre, champs.filter_by || "");
      recherches.push({
        collection,
        q,
        ...base,
        ...champs,
        ...filtre ? { filter_by: filtre } : {}
      });
      return recherches.length - 1;
    };
    const index = {
      hits: 0,
      facettes: {},
      segments: [],
      notes: [],
      bornes: null,
      statsPrix: null,
      decouverte: null
    };
    ajouter({
      filter_by: joindre(
        filtreCombine(selection, { membres }),
        filtreMesures(mesures, { numeriques: numeriquesParChamp, membres }),
        filtrePrixActif,
        filtreNoteActif
      ),
      per_page: parametres.per_page,
      page: parametres.page,
      ...champsGrille ? { include_fields: champsGrille } : {}
    });
    if (bornes) {
      const filtre = joindre(
        filtreCombine(selection, { membres }),
        filtreMesures(mesures, { numeriques: numeriquesParChamp, membres }),
        filtreNoteActif
      );
      const champs = champPrix === "price" ? "price" : `${champPrix},price`;
      index.bornes = [
        ajouter({
          filter_by: filtre,
          sort_by: `${champPrix}:asc`,
          per_page: 1,
          include_fields: champs
        }),
        ajouter({
          filter_by: filtre,
          sort_by: `${champPrix}:desc`,
          per_page: 1,
          include_fields: champs
        })
      ];
      index.statsPrix = ajouter({
        filter_by: filtre,
        per_page: 0,
        facet_by: champPrix,
        facet_strategy: "exhaustive",
        /* Seules les `stats` sont lues : rapatrier des valeurs serait payer pour
           rien sur un champ qui en compte 4 040 distinctes. ⛔ Et surtout pas
           `1` : la recherche de DÉCOUVERTE se reconnaît, elle, à son
           `max_facet_values: 1`, et deux recherches indiscernables dans le même
           lot ont déjà trompé un contrôle. Zéro est accepté (mesuré le 05/09) et
           rend les `stats` avec zéro valeur. */
        max_facet_values: 0
      });
    }
    for (const champ of facettes) {
      const enCurseur = stylesParChamp[champ] === "curseur";
      const enArbre = stylesParChamp[champ] === "arbre";
      if (enArbre) {
        const champNiveau = champAFacetter((selection[CLE] || [])[0]);
        if (!champNiveau) continue;
        index.facettes[champ] = ajouter({
          filter_by: joindre(
            filtreCombine(selection, { membres }),
            filtreMesures(mesures, { numeriques: numeriquesParChamp, membres }),
            filtrePrixActif,
            filtreNoteActif
          ),
          facet_by: champNiveau,
          ...maxValeursParChamp[champ] || maxValeurs ? { max_facet_values: maxValeursParChamp[champ] || maxValeurs } : {},
          ...rechercheFacette[champ] ? { facet_query: `${champNiveau}:${rechercheFacette[champ]}` } : {},
          per_page: 0
        });
        continue;
      }
      index.facettes[champ] = ajouter({
        filter_by: joindre(
          filtreCombine(selection, { sauf: groupe(champ), membres }),
          filtreMesures(mesures, { sauf: groupe(champ), numeriques: numeriquesParChamp, membres }),
          filtrePrixActif,
          filtreNoteActif
        ),
        /* Un curseur facette DEUX champs dans la même recherche : le compagnon
           numérique, dont les `stats` donnent le rail, et le champ texte pour
           UNE valeur — celle qui porte l'unité à afficher (« 43.18 cm » → cm).
           Sans elle, il faudrait une table champ → unité à tenir à la main, qui
           dériverait le jour où Akeneo passe un attribut au millimètre. */
        facet_by: enCurseur ? champFacetteCurseur(champ, numeriquesParChamp) : triParChamp[champ] === "alpha" ? `${champ}(sort_by: _alpha:asc)` : champ,
        /* `exhaustive` est indispensable sur les `stats` : sans lui le moteur
           échantillonne au-delà d'un certain volume, ce qui a déjà rendu 29 €
           pour une moyenne réelle de 362 €. */
        ...enCurseur ? { max_facet_values: 1, facet_strategy: "exhaustive" } : maxValeursParChamp[champ] || maxValeurs ? { max_facet_values: maxValeursParChamp[champ] || maxValeurs } : {},
        // Recherche dans les valeurs de la facette, côté moteur : la facette
        // Marque compte ~740 valeurs, les rapatrier pour filtrer au navigateur
        // serait absurde.
        ...rechercheFacette[champ] ? { facet_query: `${champ}:${rechercheFacette[champ]}` } : {},
        per_page: 0
      });
    }
    if (decouverte.length) {
      index.decouverte = ajouter({
        facet_by: decouverte.join(","),
        max_facet_values: 1,
        per_page: 0
      });
    }
    for (const segment of segments) {
      index.segments.push(ajouter({
        filter_by: joindre(
          filtreCombine(selection, { membres }),
          filtreMesures(mesures, { numeriques: numeriquesParChamp, membres }),
          filtreNoteActif,
          filtrePrix(segment, champPrix)
        ),
        per_page: 0
      }));
    }
    for (const seuil of notes) {
      index.notes.push(ajouter({
        filter_by: joindre(
          filtreCombine(selection, { membres }),
          filtreMesures(mesures, { numeriques: numeriquesParChamp, membres }),
          filtrePrixActif,
          filtreNote(seuil)
        ),
        per_page: 0
      }));
    }
    return { recherches, index };
  }
  var CHAMPS_CHAUDS_FIXES = ["disponibilites"];
  function positionsChaudes(index, {
    champPrix = "price",
    champCompare = "compare_at"
  } = {}) {
    const chaudes = /* @__PURE__ */ new Set();
    const ajouter = (position) => {
      if (Number.isInteger(position) && position >= 0) chaudes.add(position);
    };
    const source = index || {};
    ajouter(source.hits);
    ajouter(source.statsPrix);
    (source.bornes || []).forEach(ajouter);
    (source.segments || []).forEach(ajouter);
    const chaudsParChamp = new Set(
      [...CHAMPS_CHAUDS_FIXES, champPrix, champCompare].filter(Boolean)
    );
    Object.entries(source.facettes || {}).forEach(([champ, position]) => {
      if (chaudsParChamp.has(champ)) ajouter(position);
    });
    return [...chaudes].sort((a, b) => a - b);
  }

  // frontend/js/monnaie.js
  var FORMAT_DEFAUT = "{{amount_with_comma_separator}} €";
  var GABARITS = Object.freeze({
    amount: [2, ",", "."],
    amount_no_decimals: [0, ",", "."],
    amount_with_comma_separator: [2, ".", ","],
    amount_no_decimals_with_comma_separator: [0, ".", ","],
    amount_with_apostrophe_separator: [2, "'", "."],
    amount_no_decimals_with_space_separator: [0, " ", "."],
    amount_with_space_separator: [2, " ", ","],
    amount_with_period_and_space_separator: [2, " ", "."]
  });
  var ESPACE_MILLIERS = " ";
  function nombre(valeur, [decimales, milliers, decimal]) {
    const fixe = Math.abs(Number(valeur) || 0).toFixed(decimales);
    const [entier, fraction] = fixe.split(".");
    const groupes = entier.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      milliers === " " ? ESPACE_MILLIERS : milliers
    );
    const signe = Number(valeur) < 0 ? "-" : "";
    return signe + (fraction ? `${groupes}${decimal}${fraction}` : groupes);
  }
  function formaterMonnaie(valeur, format = FORMAT_DEFAUT) {
    const gabarit3 = typeof format === "string" && /\{\{\s*\w+\s*\}\}/.test(format) ? format : FORMAT_DEFAUT;
    return gabarit3.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, nom) => nombre(valeur, GABARITS[nom] || GABARITS.amount));
  }
  function formateurEntier(locale = "fr") {
    try {
      return new Intl.NumberFormat(String(locale || "fr"), { maximumFractionDigits: 0 });
    } catch (e) {
      return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
    }
  }

  // frontend/js/libelles.js
  var LANGUE_DEFAUT = "fr";
  var LIBELLES = Object.freeze({
    fr: {
      "facette.product_type": "Type de produit",
      "facette.vendor": "Marque",
      "facette.price": "Prix",
      "facette.review_rating": "Avis clients",
      "facette.disponibilites": "Disponibilités",
      "facette.univers": "Univers",
      "facette.forme_guitare": "Forme de guitare",
      "disponibilite.web": "Web",
      "disponibilite.magasin": "Magasin",
      "disponibilite.showroom_deluxe": "Showroom deluxe",
      "tri.pertinence": "Pertinence",
      "tri.price_asc": "Prix croissant",
      "tri.price_desc": "Prix décroissant",
      "tri.published_at_desc": "Nouveautés",
      "tri.review_rating_desc": "Mieux notés",
      "tri.recently_ordered_count_desc": "Les plus vendus",
      afficher_plus: "Afficher plus",
      afficher_moins: "Afficher moins",
      trouver_marque: "Trouver une marque",
      trouver_valeur: "Rechercher",
      etoiles_et_plus: "{n} étoiles et plus",
      et_plus: "et +",
      retirer: "Retirer {libelle} {valeur}",
      effacer_filtres: "Effacer les filtres",
      aucun_resultat: "Aucun résultat{pour}.",
      aucun_resultat_filtre: "Aucun résultat{pour} avec 1 filtre appliqué.",
      aucun_resultat_filtres: "Aucun résultat{pour} avec {n} filtres appliqués.",
      pour: "pour",
      resultat: "résultat",
      resultats: "résultats",
      tout_vu: "Vous avez vu tous les résultats",
      arbre_racine: "Tous les univers",
      filtres: "Filtres",
      fermer_filtres: "Fermer les filtres",
      voir_produit: "Voir le produit",
      voir_produits: "Voir les {n} produits",
      charger_plus: "Charger plus",
      afficher: "Afficher :",
      produits_par_page: "Produits par page",
      progression: "{montres} sur {total} produits",
      indisponible: "La recherche est momentanément indisponible.",
      prix_min: "Prix minimum",
      mesure_min: "Valeur minimum",
      mesure_max: "Valeur maximum",
      prix_max: "Prix maximum",
      prix_moyen: "Le prix moyen est de {prix}",
      stock_telechargement: "Produit en téléchargement",
      stock_web_disponible: "En stock internet",
      stock_fournisseur: "En stock fournisseur",
      stock_web_sur_commande: "Rupture temporaire",
      stock_web_date: "Dispo dès le {date}",
      stock_magasin_disponible: "En stock en magasin",
      stock_magasin_expo: "Magasin : En expo uniquement",
      stock_magasin_sur_commande: "Magasin : sur commande",
      oui: "Oui",
      non: "Non"
    },
    en: {
      "facette.product_type": "Product type",
      "facette.vendor": "Brand",
      "facette.price": "Price",
      "facette.review_rating": "Customer reviews",
      "facette.disponibilites": "Availability",
      "facette.univers": "Department",
      "facette.forme_guitare": "Guitar shape",
      "disponibilite.web": "Online",
      "disponibilite.magasin": "Store",
      "disponibilite.showroom_deluxe": "Deluxe showroom",
      "tri.pertinence": "Relevance",
      "tri.price_asc": "Price: low to high",
      "tri.price_desc": "Price: high to low",
      "tri.published_at_desc": "New arrivals",
      "tri.review_rating_desc": "Top rated",
      "tri.recently_ordered_count_desc": "Best sellers",
      afficher_plus: "Show more",
      afficher_moins: "Show less",
      trouver_marque: "Find a brand",
      trouver_valeur: "Search",
      etoiles_et_plus: "{n} stars and up",
      et_plus: "& up",
      retirer: "Remove {libelle} {valeur}",
      effacer_filtres: "Clear filters",
      aucun_resultat: "No results{pour}.",
      aucun_resultat_filtre: "No results{pour} with 1 filter applied.",
      aucun_resultat_filtres: "No results{pour} with {n} filters applied.",
      pour: "for",
      resultat: "result",
      resultats: "results",
      tout_vu: "You have seen all the results",
      arbre_racine: "All departments",
      filtres: "Filters",
      fermer_filtres: "Close filters",
      voir_produit: "Show 1 product",
      voir_produits: "Show {n} products",
      charger_plus: "Load more",
      afficher: "Show:",
      produits_par_page: "Products per page",
      progression: "{montres} of {total} products",
      indisponible: "Search is temporarily unavailable.",
      prix_min: "Minimum price",
      mesure_min: "Minimum value",
      mesure_max: "Maximum value",
      prix_max: "Maximum price",
      prix_moyen: "The average price is {prix}",
      stock_telechargement: "Downloadable product",
      stock_web_disponible: "In stock online",
      stock_fournisseur: "In stock at supplier",
      stock_web_sur_commande: "Temporarily out of stock",
      stock_web_date: "Available from {date}",
      stock_magasin_disponible: "In stock in store",
      stock_magasin_expo: "Store: display only",
      stock_magasin_sur_commande: "Store: on order",
      oui: "Yes",
      non: "No"
    },
    de: {
      "facette.product_type": "Produkttyp",
      "facette.vendor": "Marke",
      "facette.price": "Preis",
      "facette.review_rating": "Kundenbewertungen",
      "facette.disponibilites": "Verfügbarkeit",
      "facette.univers": "Bereich",
      "facette.forme_guitare": "Gitarrenform",
      "disponibilite.web": "Online",
      "disponibilite.magasin": "Geschäft",
      "disponibilite.showroom_deluxe": "Deluxe-Showroom",
      "tri.pertinence": "Relevanz",
      "tri.price_asc": "Preis aufsteigend",
      "tri.price_desc": "Preis absteigend",
      "tri.published_at_desc": "Neuheiten",
      "tri.review_rating_desc": "Bestbewertet",
      "tri.recently_ordered_count_desc": "Bestseller",
      afficher_plus: "Mehr anzeigen",
      afficher_moins: "Weniger anzeigen",
      trouver_marque: "Marke suchen",
      trouver_valeur: "Suchen",
      etoiles_et_plus: "{n} Sterne und mehr",
      et_plus: "u. mehr",
      retirer: "{libelle} {valeur} entfernen",
      effacer_filtres: "Filter zurücksetzen",
      aucun_resultat: "Keine Ergebnisse{pour}.",
      aucun_resultat_filtre: "Keine Ergebnisse{pour} mit 1 aktiven Filter.",
      aucun_resultat_filtres: "Keine Ergebnisse{pour} mit {n} aktiven Filtern.",
      pour: "für",
      resultat: "Ergebnis",
      resultats: "Ergebnisse",
      tout_vu: "Sie haben alle Ergebnisse gesehen",
      arbre_racine: "Alle Bereiche",
      filtres: "Filter",
      fermer_filtres: "Filter schließen",
      voir_produit: "1 Produkt anzeigen",
      voir_produits: "{n} Produkte anzeigen",
      charger_plus: "Mehr laden",
      afficher: "Anzeigen:",
      produits_par_page: "Produkte pro Seite",
      progression: "{montres} von {total} Produkten",
      indisponible: "Die Suche ist vorübergehend nicht verfügbar.",
      prix_min: "Mindestpreis",
      mesure_min: "Mindestwert",
      mesure_max: "Maximalwert",
      prix_max: "Höchstpreis",
      prix_moyen: "Der Durchschnittspreis beträgt {prix}",
      stock_telechargement: "Herunterladbares Produkt",
      stock_web_disponible: "Online auf Lager",
      stock_fournisseur: "Beim Lieferanten auf Lager",
      stock_web_sur_commande: "Vorübergehend nicht auf Lager",
      stock_web_date: "Verfügbar ab {date}",
      stock_magasin_disponible: "Im Geschäft auf Lager",
      stock_magasin_expo: "Geschäft: nur Ausstellungsstück",
      stock_magasin_sur_commande: "Geschäft: auf Bestellung",
      oui: "Ja",
      non: "Nein"
    },
    es: {
      "facette.product_type": "Tipo de producto",
      "facette.vendor": "Marca",
      "facette.price": "Precio",
      "facette.review_rating": "Opiniones de clientes",
      "facette.disponibilites": "Disponibilidad",
      "facette.univers": "Sección",
      "facette.forme_guitare": "Forma de guitarra",
      "disponibilite.web": "En línea",
      "disponibilite.magasin": "Tienda",
      "disponibilite.showroom_deluxe": "Showroom deluxe",
      "tri.pertinence": "Relevancia",
      "tri.price_asc": "Precio: de menor a mayor",
      "tri.price_desc": "Precio: de mayor a menor",
      "tri.published_at_desc": "Novedades",
      "tri.review_rating_desc": "Mejor valorados",
      "tri.recently_ordered_count_desc": "Los más vendidos",
      afficher_plus: "Mostrar más",
      afficher_moins: "Mostrar menos",
      trouver_marque: "Buscar una marca",
      trouver_valeur: "Buscar",
      etoiles_et_plus: "{n} estrellas o más",
      et_plus: "o más",
      retirer: "Quitar {libelle} {valeur}",
      effacer_filtres: "Borrar filtros",
      aucun_resultat: "Sin resultados{pour}.",
      aucun_resultat_filtre: "Sin resultados{pour} con 1 filtro aplicado.",
      aucun_resultat_filtres: "Sin resultados{pour} con {n} filtros aplicados.",
      pour: "para",
      resultat: "resultado",
      resultats: "resultados",
      tout_vu: "Has visto todos los resultados",
      arbre_racine: "Todas las secciones",
      filtres: "Filtros",
      fermer_filtres: "Cerrar filtros",
      voir_produit: "Ver 1 producto",
      voir_produits: "Ver los {n} productos",
      charger_plus: "Cargar más",
      afficher: "Mostrar:",
      produits_par_page: "Productos por página",
      progression: "{montres} de {total} productos",
      indisponible: "La búsqueda no está disponible temporalmente.",
      prix_min: "Precio mínimo",
      mesure_min: "Valor mínimo",
      mesure_max: "Valor máximo",
      prix_max: "Precio máximo",
      prix_moyen: "El precio medio es de {prix}",
      stock_telechargement: "Producto descargable",
      stock_web_disponible: "En stock en internet",
      stock_fournisseur: "En stock en proveedor",
      stock_web_sur_commande: "Temporalmente sin stock",
      stock_web_date: "Disponible desde el {date}",
      stock_magasin_disponible: "En stock en tienda",
      stock_magasin_expo: "Tienda: solo exposición",
      stock_magasin_sur_commande: "Tienda: bajo pedido",
      oui: "Sí",
      non: "No"
    },
    it: {
      "facette.product_type": "Tipo di prodotto",
      "facette.vendor": "Marca",
      "facette.price": "Prezzo",
      "facette.review_rating": "Recensioni dei clienti",
      "facette.disponibilites": "Disponibilità",
      "facette.univers": "Reparto",
      "facette.forme_guitare": "Forma della chitarra",
      "disponibilite.web": "Online",
      "disponibilite.magasin": "Negozio",
      "disponibilite.showroom_deluxe": "Showroom deluxe",
      "tri.pertinence": "Rilevanza",
      "tri.price_asc": "Prezzo crescente",
      "tri.price_desc": "Prezzo decrescente",
      "tri.published_at_desc": "Novità",
      "tri.review_rating_desc": "Più votati",
      "tri.recently_ordered_count_desc": "I più venduti",
      afficher_plus: "Mostra di più",
      afficher_moins: "Mostra meno",
      trouver_marque: "Cerca un marchio",
      trouver_valeur: "Cerca",
      etoiles_et_plus: "{n} stelle e più",
      et_plus: "e più",
      retirer: "Rimuovi {libelle} {valeur}",
      effacer_filtres: "Cancella i filtri",
      aucun_resultat: "Nessun risultato{pour}.",
      aucun_resultat_filtre: "Nessun risultato{pour} con 1 filtro applicato.",
      aucun_resultat_filtres: "Nessun risultato{pour} con {n} filtri applicati.",
      pour: "per",
      resultat: "risultato",
      resultats: "risultati",
      tout_vu: "Hai visto tutti i risultati",
      arbre_racine: "Tutti i reparti",
      filtres: "Filtri",
      fermer_filtres: "Chiudi i filtri",
      voir_produit: "Vedi 1 prodotto",
      voir_produits: "Vedi i {n} prodotti",
      charger_plus: "Carica altri",
      afficher: "Mostra:",
      produits_par_page: "Prodotti per pagina",
      progression: "{montres} di {total} prodotti",
      indisponible: "La ricerca è momentaneamente non disponibile.",
      prix_min: "Prezzo minimo",
      mesure_min: "Valore minimo",
      mesure_max: "Valore massimo",
      prix_max: "Prezzo massimo",
      prix_moyen: "Il prezzo medio è di {prix}",
      stock_telechargement: "Prodotto scaricabile",
      stock_web_disponible: "Disponibile online",
      stock_fournisseur: "Disponibile presso il fornitore",
      stock_web_sur_commande: "Temporaneamente esaurito",
      stock_web_date: "Disponibile dal {date}",
      stock_magasin_disponible: "Disponibile in negozio",
      stock_magasin_expo: "Negozio: solo in esposizione",
      stock_magasin_sur_commande: "Negozio: su ordinazione",
      oui: "Sì",
      non: "No"
    },
    nl: {
      "facette.product_type": "Producttype",
      "facette.vendor": "Merk",
      "facette.price": "Prijs",
      "facette.review_rating": "Klantbeoordelingen",
      "facette.disponibilites": "Beschikbaarheid",
      "facette.univers": "Afdeling",
      "facette.forme_guitare": "Gitaarvorm",
      "disponibilite.web": "Online",
      "disponibilite.magasin": "Winkel",
      "disponibilite.showroom_deluxe": "Luxe showroom",
      "tri.pertinence": "Relevantie",
      "tri.price_asc": "Prijs oplopend",
      "tri.price_desc": "Prijs aflopend",
      "tri.published_at_desc": "Nieuw",
      "tri.review_rating_desc": "Best beoordeeld",
      "tri.recently_ordered_count_desc": "Bestsellers",
      afficher_plus: "Meer tonen",
      afficher_moins: "Minder tonen",
      trouver_marque: "Zoek een merk",
      trouver_valeur: "Zoeken",
      etoiles_et_plus: "{n} sterren en meer",
      et_plus: "en meer",
      retirer: "{libelle} {valeur} verwijderen",
      effacer_filtres: "Filters wissen",
      aucun_resultat: "Geen resultaten{pour}.",
      aucun_resultat_filtre: "Geen resultaten{pour} met 1 toegepast filter.",
      aucun_resultat_filtres: "Geen resultaten{pour} met {n} toegepaste filters.",
      pour: "voor",
      resultat: "resultaat",
      resultats: "resultaten",
      tout_vu: "Je hebt alle resultaten gezien",
      arbre_racine: "Alle afdelingen",
      filtres: "Filters",
      fermer_filtres: "Filters sluiten",
      voir_produit: "1 product bekijken",
      voir_produits: "{n} producten bekijken",
      charger_plus: "Meer laden",
      afficher: "Tonen:",
      produits_par_page: "Producten per pagina",
      progression: "{montres} van {total} producten",
      indisponible: "Zoeken is tijdelijk niet beschikbaar.",
      prix_min: "Minimumprijs",
      mesure_min: "Minimumwaarde",
      mesure_max: "Maximumwaarde",
      prix_max: "Maximumprijs",
      prix_moyen: "De gemiddelde prijs is {prix}",
      stock_telechargement: "Downloadbaar product",
      stock_web_disponible: "Online op voorraad",
      stock_fournisseur: "Op voorraad bij leverancier",
      stock_web_sur_commande: "Tijdelijk niet op voorraad",
      stock_web_date: "Beschikbaar vanaf {date}",
      stock_magasin_disponible: "Op voorraad in de winkel",
      stock_magasin_expo: "Winkel: alleen showmodel",
      stock_magasin_sur_commande: "Winkel: op bestelling",
      oui: "Ja",
      non: "Nee"
    },
    pt: {
      "facette.product_type": "Tipo de produto",
      "facette.vendor": "Marca",
      "facette.price": "Preço",
      "facette.review_rating": "Avaliações de clientes",
      "facette.disponibilites": "Disponibilidade",
      "facette.univers": "Secção",
      "facette.forme_guitare": "Forma da guitarra",
      "disponibilite.web": "Online",
      "disponibilite.magasin": "Loja",
      "disponibilite.showroom_deluxe": "Showroom deluxe",
      "tri.pertinence": "Relevância",
      "tri.price_asc": "Preço crescente",
      "tri.price_desc": "Preço decrescente",
      "tri.published_at_desc": "Novidades",
      "tri.review_rating_desc": "Melhor avaliados",
      "tri.recently_ordered_count_desc": "Os mais vendidos",
      afficher_plus: "Mostrar mais",
      afficher_moins: "Mostrar menos",
      trouver_marque: "Procurar uma marca",
      trouver_valeur: "Procurar",
      etoiles_et_plus: "{n} estrelas ou mais",
      et_plus: "ou mais",
      retirer: "Remover {libelle} {valeur}",
      effacer_filtres: "Limpar filtros",
      aucun_resultat: "Sem resultados{pour}.",
      aucun_resultat_filtre: "Sem resultados{pour} com 1 filtro aplicado.",
      aucun_resultat_filtres: "Sem resultados{pour} com {n} filtros aplicados.",
      pour: "para",
      resultat: "resultado",
      resultats: "resultados",
      tout_vu: "Viu todos os resultados",
      arbre_racine: "Todas as secções",
      filtres: "Filtros",
      fermer_filtres: "Fechar filtros",
      voir_produit: "Ver 1 produto",
      voir_produits: "Ver os {n} produtos",
      charger_plus: "Carregar mais",
      afficher: "Mostrar:",
      produits_par_page: "Produtos por página",
      progression: "{montres} de {total} produtos",
      indisponible: "A pesquisa está temporariamente indisponível.",
      prix_min: "Preço mínimo",
      mesure_min: "Valor mínimo",
      mesure_max: "Valor máximo",
      prix_max: "Preço máximo",
      prix_moyen: "O preço médio é de {prix}",
      stock_telechargement: "Produto para download",
      // Repris de `locales/pt-PT.json` du thème live (30/10/2026), qui porte
      // désormais ces états en phrases, comme les six autres locales.
      stock_web_disponible: "Em stock online",
      stock_fournisseur: "Em stock no fornecedor",
      stock_web_sur_commande: "Temporariamente sem stock",
      stock_web_date: "Disponível a partir de {date}",
      stock_magasin_disponible: "Em stock na loja",
      stock_magasin_expo: "Loja: apenas exposição",
      stock_magasin_sur_commande: "Loja: por encomenda",
      oui: "Sim",
      non: "Não"
    }
  });
  var LANGUES_LIBELLES = Object.freeze(Object.keys(LIBELLES));
  var CLES_DE_FACETTE = Object.freeze({
    product_type: "facette.product_type",
    vendor: "facette.vendor",
    price: "facette.price",
    review_rating: "facette.review_rating",
    disponibilites: "facette.disponibilites",
    /* Le filtre de premier niveau des pages transverses (`univers.py`). */
    univers: "facette.univers",
    /* La DESCENTE par niveau porte le MÊME titre que le filtre plat qu'elle
       remplace : c'est le même rayon, vu plus finement, et le visiteur doit
       retrouver le mot du bloc dans son fil d'Ariane. */
    chemin: "facette.univers"
  });
  var langueCourante = LANGUE_DEFAUT;
  function langueDeLocale(locale) {
    const code = String(locale || "").toLowerCase().split("-")[0];
    return LIBELLES[code] ? code : LANGUE_DEFAUT;
  }
  function definirLangue(locale) {
    langueCourante = langueDeLocale(locale);
    return langueCourante;
  }
  function langue() {
    return langueCourante;
  }
  var PREFIXE_STOCK = "stock_";
  var libellesTheme = {};
  function definirLibellesTheme(table = null) {
    libellesTheme = {};
    if (!table || typeof table !== "object") return 0;
    for (const [cle, valeur] of Object.entries(table)) {
      if (!cle.startsWith(PREFIXE_STOCK) || !(cle in LIBELLES[LANGUE_DEFAUT])) continue;
      const texte = (typeof valeur === "string" ? valeur.trim() : "").replace(/%%([A-Z]+)%%/g, (tout, nom) => `{${nom.toLowerCase()}}`);
      if (!texte || /^translation missing/i.test(texte)) continue;
      const variables = String(LIBELLES[LANGUE_DEFAUT][cle]).match(/\{\w+\}/g) || [];
      if (variables.every((v) => texte.includes(v))) libellesTheme[cle] = texte;
    }
    return Object.keys(libellesTheme).length;
  }
  function t(cle, variables = {}) {
    var _a, _b, _c;
    const gabarit3 = (_c = (_b = (_a = libellesTheme[cle]) != null ? _a : LIBELLES[langueCourante][cle]) != null ? _b : LIBELLES[LANGUE_DEFAUT][cle]) != null ? _c : cle;
    return String(gabarit3).replace(/\{(\w+)\}/g, (tout, nom) => variables[nom] === void 0 || variables[nom] === null ? tout : String(variables[nom]));
  }
  function libelleFiltre(entree, langue2) {
    if (!entree) return "";
    const code = String(langue2 || langueCourante || "").toLowerCase().split("-")[0];
    const traduit = entree.libelles && code ? entree.libelles[code] : "";
    return typeof traduit === "string" && traduit.trim() ? traduit : entree.libelle || "";
  }

  // frontend/js/specs-carte.js
  var SEPARATEUR2 = " · ";
  var VALEURS_PAR_LIGNE = 3;
  function valeursDe(brut) {
    if (brut === null || brut === void 0 || brut === "") return [];
    const liste2 = Array.isArray(brut) ? brut : [brut];
    return liste2.filter((v) => v !== null && v !== void 0 && String(v) !== "").map((v) => String(v));
  }
  var CACHE = /* @__PURE__ */ new WeakMap();
  function indexDesFiltres(filtres) {
    if (!Array.isArray(filtres)) return {};
    const memo = CACHE.get(filtres);
    if (memo) return memo;
    const index = {};
    for (const entree of filtres) {
      if (entree && entree.cle) index[entree.cle] = entree;
    }
    CACHE.set(filtres, index);
    return index;
  }
  function specsDuProduit(document_, { table, filtres, langue: langue2 } = {}) {
    if (!document_ || !table) return [];
    const cles = table[document_.product_type];
    if (!Array.isArray(cles) || !cles.length) return [];
    const index = indexDesFiltres(filtres);
    const lignes = [];
    for (const cle of cles) {
      const valeurs = valeursDe(document_[cle]);
      if (!valeurs.length) continue;
      const entree = index[cle];
      if (!entree) continue;
      lignes.push({
        LIBELLE: libelleFiltre(entree, langue2),
        VALEUR: valeurs.slice(0, VALEURS_PAR_LIGNE).join(SEPARATEUR2)
      });
    }
    return lignes;
  }

  // frontend/js/carte.js
  var ID_GABARIT = "algolia-card-tpl";
  var reglages = {
    racineProduits: "https://preprod.woodbrass.com",
    /* Le préfixe du CDN de la BOUTIQUE (`//host/cdn/shop/`), pour aligner l'URL
       d'image sur celle que la carte serveur demande — voir `alignerImage`.
       Vide sur la page de recette, qui n'est pas servie par une boutique. */
    prefixeImages: "",
    config: null,
    // "recette" : étoiles stylées par la page de recette (classes rec-*).
    // "judgeme" : le markup du badge Judge.me tel que la prod le stockait dans le
    // metafield `judgeme.badge`, masqué en display:none jusqu'à ce que le script
    // de l'app l'habille (`jdgm.customizeBadges()`, appelé après chaque lot par le
    // thème). Sans Judge.me sur la page, rien ne s'affiche — pas de chiffre nu.
    avis: "recette",
    // Le champ de prix du MARCHÉ (`price_de`, `price_ch`…, voir `marches.py`) :
    // 43 % des prix allemands diffèrent du prix de base. `price` hors table.
    champPrix: "price",
    // Le champ de prix BARRÉ du même marché. Il suit `champPrix` : l'index ne
    // porte `compare_at_ch` que là où il porte `price_ch` (`marches.py`), donc le
    // couple ne se dépareille pas et un prix en euros n'est jamais barré d'un
    // montant en francs.
    champCompare: "compare_at",
    // Langue du visiteur, pour les nombres (« 3 364 » / « 3.364 » / « 3,364 »).
    locale: "fr"
  };
  function jointure(racine, chemin) {
    return `${String(racine || "").replace(/\/+$/, "")}/${String(chemin).replace(/^\/+/, "")}`;
  }
  function configurerCarte({
    racineProduits,
    config,
    avis,
    champPrix,
    champCompare,
    locale,
    prefixeImages
  } = {}) {
    if (avis !== void 0 && !["recette", "judgeme"].includes(avis)) {
      throw new Error(`configurerCarte : mode d'avis inconnu « ${avis} »`);
    }
    if (champPrix !== void 0 && !/^[a-z_][a-z0-9_]*$/.test(String(champPrix))) {
      throw new Error(`configurerCarte : champ de prix invalide « ${champPrix} »`);
    }
    if (champCompare !== void 0 && !/^[a-z_][a-z0-9_]*$/.test(String(champCompare))) {
      throw new Error(
        `configurerCarte : champ de prix comparé invalide « ${champCompare} »`
      );
    }
    const suivant = {
      racineProduits: racineProduits != null ? racineProduits : reglages.racineProduits,
      prefixeImages: prefixeImages != null ? prefixeImages : reglages.prefixeImages,
      config: config === void 0 ? reglages.config : config,
      avis: avis != null ? avis : reglages.avis,
      champPrix: champPrix != null ? champPrix : reglages.champPrix,
      champCompare: champCompare != null ? champCompare : reglages.champCompare,
      locale: typeof locale === "string" && locale ? locale : reglages.locale
    };
    Object.assign(reglages, suivant);
    return { ...suivant };
  }
  var PREFIXE_INDEX = /^https?:\/\/cdn\.shopify\.com\/s\/files\/(?:\d+\/)+/;
  function alignerImage(url, prefixe) {
    if (typeof url !== "string" || !url) return "";
    if (!prefixe || typeof prefixe !== "string") return url;
    const trouve = PREFIXE_INDEX.exec(url);
    return trouve ? prefixe + url.slice(trouve[0].length) : url;
  }
  var formaterPrix = (v) => formaterMonnaie(v != null ? v : 0, reglages.config && reglages.config.moneyFormat || FORMAT_DEFAUT);
  var entiers = { locale: null, formateur: null };
  function formaterEntier(v) {
    if (entiers.locale !== reglages.locale) {
      entiers = { locale: reglages.locale, formateur: formateurEntier(reglages.locale) };
    }
    return entiers.formateur.format(v != null ? v : 0);
  }
  function prixDe(d) {
    const prix = d ? d[reglages.champPrix] : void 0;
    return Number.isFinite(prix) ? prix : d && d.price;
  }
  function compareDe(d) {
    if (!d) return 0;
    const compare = d[reglages.champCompare];
    const valeur = Number.isFinite(compare) ? compare : d.compare_at;
    return Number.isFinite(valeur) ? valeur : 0;
  }
  function enPromotion(d) {
    const prix = prixDe(d);
    const compare = compareDe(d);
    return Number.isFinite(prix) && prix > 0 && compare > prix;
  }
  function estBundle(d) {
    return Array.isArray(d && d.tags) && d.tags.includes("bundle");
  }
  function estPackDeMarque(d) {
    return estBundle(d) && String(d && d.vendor || "").trim().toLowerCase() !== "woodbrass";
  }
  function etiquetteRemise(prix, compare) {
    const config = reglages.config || {};
    if (config.saleBadge !== "percentage") return config.saleLabel || "";
    const c = Math.round(compare * 100);
    const p = Math.round(prix * 100);
    if (!(c > 0)) return "";
    const remise = Math.floor((c - p) * 100 / c);
    return remise > 0 ? `-${remise}%` : "";
  }
  function esc(valeur) {
    return String(valeur != null ? valeur : "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[c]);
  }
  async function chargerGabarit() {
    if (document.getElementById(ID_GABARIT)) return true;
    const reponse = await fetch("vendor/algolia-card-tpl.html");
    if (!reponse.ok) {
      throw new Error(
        `Gabarit de carte absent (HTTP ${reponse.status}). Lancer : python scripts/pull_card_template.py`
      );
    }
    const gabarit3 = document.createElement("template");
    gabarit3.id = ID_GABARIT;
    gabarit3.innerHTML = (await reponse.text()).trim();
    document.body.appendChild(gabarit3);
    return true;
  }
  function badgeStock(etat, texte) {
    return `<div class="product__inventory font-body-bolder product__inventory--${etat}"><span class="product__inventory-icon"></span><span class="product__inventory-text">${esc(texte)}</span></div>`;
  }
  function htmlStock(disponibilites, { telechargeable = false, fournisseur = false, date = "" } = {}) {
    if (telechargeable) {
      return badgeStock("available", t("stock_telechargement"));
    }
    const canaux = (disponibilites || []).map((x) => String(x).toLowerCase());
    const magasin = canaux.includes("magasin") ? badgeStock("available", t("stock_magasin_disponible")) : badgeStock("reappro", t(canaux.includes("non vendable") ? "stock_magasin_expo" : "stock_magasin_sur_commande"));
    const retour = dateDeRetour(date);
    return (canaux.includes("web") ? badgeStock("available", t(fournisseur ? "stock_fournisseur" : "stock_web_disponible")) : badgeStock("reappro", retour ? t("stock_web_date", { date: retour }) : t("stock_web_sur_commande"))) + magasin;
  }
  function dateDeRetour(valeur) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valeur || "").trim().slice(0, 10));
    if (!m) return "";
    const instant = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (instant.getUTCDate() !== Number(m[3])) return "";
    return `${m[3]}/${m[2]}/${m[1]}`;
  }
  var CHAMP_TELECHARGEABLE = "ak_o_telechargeable";
  var CHAMP_FOURNISSEUR = "stock_fournisseur";
  var CHAMP_DATE_RETOUR = "availability_date";
  function stockDeCarte(d) {
    return htmlStock(d.disponibilites, {
      telechargeable: !!d[CHAMP_TELECHARGEABLE],
      fournisseur: d[CHAMP_FOURNISSEUR] === true,
      date: d[CHAMP_DATE_RETOUR]
    });
  }
  function etatsEtoiles(note) {
    const demis = Math.round(Number(note) * 2) / 2;
    return [1, 2, 3, 4, 5].map((i) => demis >= i ? "on" : demis >= i - 0.5 ? "half" : "off");
  }
  function badgeJudgeme(note, nombre2) {
    const etoiles2 = etatsEtoiles(note).map((e) => `<span class='jdgm-star jdgm--${e}'></span>`).join("");
    const n = Number(nombre2) || 0;
    return `<div style='display:none' class='jdgm-prev-badge' data-average-rating='${Number(note).toFixed(2)}' data-number-of-reviews='${n}' data-number-of-questions='0'> <span class='jdgm-prev-badge__stars' data-score='${Number(note).toFixed(2)}' tabindex='0' aria-label='${Number(note).toFixed(2)} stars' role='button'> ${etoiles2} </span> <span class='jdgm-prev-badge__text'> ${formaterEntier(n)} avis </span> </div>`;
  }
  function avisHtml(document_) {
    const note = document_.review_rating;
    if (note == null || !document_.review_count) return "";
    if (reglages.avis === "judgeme") return badgeJudgeme(note, document_.review_count);
    const demis = Math.round(note * 2) / 2;
    let etoiles2 = "";
    for (let i = 1; i <= 5; i++) {
      const etat = demis >= i ? "on" : demis >= i - 0.5 ? "half" : "off";
      etoiles2 += `<span class="rec-star rec-star--${etat}"></span>`;
    }
    return `<span class="rec-stars" aria-label="${note.toFixed(1)} sur 5">` + etoiles2 + `<span class="rec-stars-count">${formaterEntier(document_.review_count)}</span></span>`;
  }
  function badgesDeTags(tags) {
    const source = reglages.config || globalThis.WB_CARD_CONFIG || {};
    const config = source.customBadges || [];
    if (!Array.isArray(tags) || !config.length) return [];
    return config.filter((b) => tags.indexOf(`badge-${b.handle}`) !== -1).map((b) => ({ HANDLE: b.handle, LABEL: b.label, COLOR: b.color, BG: b.bg }));
  }
  var ALMA_4X_FIRST = "alma_4x_first_cents";
  var ALMA_4X_MONTHLY = "alma_4x_monthly_cents";
  var ALMA_4X_FEES = "alma_4x_fees_cents";
  var ALMA_12X_FIRST = "alma_12x_first_cents";
  var ALMA_12X_MONTHLY = "alma_12x_monthly_cents";
  var ALMA_12X_COST = "alma_12x_cost_cents";
  var CHAMPS_CARTE = Object.freeze([
    "id",
    "produit_id",
    "variante_carte",
    "title",
    "handle",
    "variant_id",
    "image",
    "vendor",
    "product_type",
    "price",
    "compare_at",
    "stock_bloquant",
    "inventory_quantity",
    "review_rating",
    "review_count",
    "disponibilites",
    "tags",
    ALMA_4X_FIRST,
    ALMA_4X_MONTHLY,
    ALMA_4X_FEES,
    ALMA_12X_FIRST,
    ALMA_12X_MONTHLY,
    ALMA_12X_COST,
    CHAMP_TELECHARGEABLE,
    CHAMP_FOURNISSEUR,
    CHAMP_DATE_RETOUR
  ]);
  function champsDeCarte({ specs } = {}) {
    const champs = /* @__PURE__ */ new Set([...CHAMPS_CARTE, reglages.champPrix, reglages.champCompare]);
    for (const cles of Object.values(specs || {})) {
      if (Array.isArray(cles)) cles.forEach((c) => champs.add(c));
    }
    return [...champs].filter(Boolean).join(",");
  }
  function donneesDeCarte(d, surlignage) {
    const url = d.handle ? jointure(reglages.racineProduits, `products/${encodeURIComponent(d.handle)}`) + (d.variant_id ? `?variant=${encodeURIComponent(d.variant_id)}` : "") : "#";
    const epuise = d.stock_bloquant === true && Number(d.inventory_quantity || 0) <= 0;
    const promo = enPromotion(d);
    const specs = specsDuProduit(d, {
      table: globalThis.WB_SPECS,
      filtres: globalThis.WB_FILTRES,
      langue: reglages.locale
    });
    return {
      URL: url,
      TITLE: d.title || "",
      TITLE_HTML: surlignage || esc(d.title || ""),
      /* `&width=N&height=N&quality=60` fait partie du gabarit, pas de la donnée — et
         l'hôte est réaligné sur celui de la carte serveur : voir `alignerImage`. */
      IMAGE: alignerImage(d.image, reglages.prefixeImages),
      VENDOR: d.vendor || "",
      PRODUCT_ID: d.id || "",
      PRICE: formaterPrix(prixDe(d)),
      COMPARE_AT: promo ? formaterPrix(compareDe(d)) : "",
      PRICE_MODIFIERS: [
        promo ? "f-price--on-sale" : "",
        epuise ? "f-price--sold-out" : ""
      ].filter(Boolean).join(" "),
      SOLD_OUT: epuise,
      // ⛔ Pas de badge de remise sur un produit épuisé : les deux se peignent
      // dans la même pastille et se chevaucheraient. La production tranche déjà
      // ainsi (`app_algolia.js` : `enPromo && !epuise`). Le prix barré, lui,
      // reste : il informe sans occuper la pastille. Pas de badge non plus sur
      // le bundle fixe d'une marque (`estPackDeMarque`) ; un pack Woodbrass
      // garde le sien.
      SALE_LABEL: promo && !epuise && !estPackDeMarque(d) ? etiquetteRemise(prixDe(d), compareDe(d)) : "",
      REVIEWS_HTML: avisHtml(d),
      // Le thème reconnaît les libellés autant que les références de métaobjets :
      // `channelKeys` compare aussi le champ `label`. Nos libellés résolus
      // (« Web », « Magasin ») matchent donc les canaux configurés.
      AVAILABILITIES: JSON.stringify(d.disponibilites || []),
      // La date brute, comme la carte Globo la posait (`data-gf-availability`) :
      // `wbStockDebug()` du thème la relit. Le badge, lui, est peint par
      // `stockDeCarte` (`data-stock-done`), le script du thème n'y retouche pas.
      AVAILABILITY_DATE: d[CHAMP_DATE_RETOUR] || "",
      DIGITAL: "",
      CUSTOM_BADGES: badgesDeTags(d.tags),
      // Les lignes de caractéristiques du mode liste. `undefined` et non `[]`
      // quand il n'y en a pas : `data-wb-if` de `wb-card-render.js` teste `!v`, et
      // un tableau VIDE est vrai en JS — il laisserait la liste de définitions à
      // l'écran, vide, avec sa marge.
      SPECS: specs.length ? specs : void 0
    };
  }
  function almaEligible(d) {
    return htmlAlma(d) !== "";
  }
  function htmlAlma(d) {
    const montant = (champ) => {
      const valeur = Number(d ? Reflect.get(d, champ) : 0);
      return Number.isInteger(valeur) && valeur > 0 ? valeur : 0;
    };
    const euros = (centimes) => esc(formaterPrix(centimes / 100));
    const bloc2 = (detail, contenu) => `<div class="wb-card-alma" title="${esc(detail)}" aria-label="${esc(detail)}">${contenu}</div>`;
    const premier12 = montant(ALMA_12X_FIRST);
    const mensuel12 = montant(ALMA_12X_MONTHLY);
    if (premier12 && mensuel12) {
      const detail = `Paiement en 12 fois avec Alma : premier versement ${formaterPrix(premier12 / 100)}, puis 11 fois ${formaterPrix(mensuel12 / 100)}, coût du crédit ${formaterPrix(montant(ALMA_12X_COST) / 100)}`;
      return bloc2(detail, `<span>ou <strong>${euros(mensuel12)}</strong>/mois</span> <span>en 12×*</span>`);
    }
    const premier4 = montant(ALMA_4X_FIRST);
    const mensuel4 = montant(ALMA_4X_MONTHLY);
    if (premier4 && mensuel4) {
      const detail = `Paiement en 4 fois avec Alma : premier versement ${formaterPrix(premier4 / 100)}, puis 3 fois ${formaterPrix(mensuel4 / 100)}, frais ${formaterPrix(montant(ALMA_4X_FEES) / 100)}`;
      return bloc2(detail, `<span>ou <strong>4× ${euros(mensuel4)}</strong>*</span>`);
    }
    return "";
  }
  var CLASSES_ITEM = "wb-grid-item f-column product-column";
  function attributsDeMesure(d) {
    const produit = String(d.produit_id || d.id || "");
    const variante = String(d.variante_carte || d.variant_id || "");
    if (!/^\d+$/.test(produit) || !/^\d+$/.test(variante)) return "";
    return ` data-wb-product-id="${produit}" data-wb-variant-id="${variante}"`;
  }
  function carte(d, surlignage, balise = "li", classes = CLASSES_ITEM, { alma: afficherAlma = false } = {}) {
    const moteur = globalThis.WBCardRender;
    if (!moteur) {
      return `<${balise} class="${classes} rec-error">Moteur de carte absent : lancer <code>python scripts/pull_card_template.py</code></${balise}>`;
    }
    const noeud = moteur.render(ID_GABARIT, donneesDeCarte(d, surlignage));
    if (!noeud) return "";
    const stock = noeud.querySelector(".js-gf-stock");
    if (stock) {
      stock.innerHTML = stockDeCarte(d);
      stock.dataset.stockDone = "1";
    }
    if (afficherAlma) {
      const prix = noeud.querySelector(".f-price");
      const alma = htmlAlma(d);
      if (prix && alma && typeof prix.insertAdjacentHTML === "function") {
        prix.insertAdjacentHTML("afterend", alma);
      }
    }
    return `<${balise} class="${classes}" data-wb-document-id="${esc(d.id || "")}"${attributsDeMesure(d)}>${noeud.outerHTML}</${balise}>`;
  }

  // frontend/js/couleurs.js
  var NOIR = "#161616";
  var BRUN = "#5a3418";
  var GRIS = "linear-gradient(135deg, #d9d9d9 0%, #8e8e8e 100%)";
  var TRANSPARENT = "repeating-conic-gradient(#e6e6e6 0% 25%, #ffffff 0% 50%) 50% / 10px 10px";
  var MULTICOLORE = "conic-gradient(#e63946, #f4a261, #e9c46a, #2a9d8f, #457b9d, #9b5de5, #e63946)";
  var DEGRADE = `linear-gradient(135deg, #f0b35c 0%, #b8631f 55%, ${BRUN} 100%)`;
  var BRONZE = "linear-gradient(135deg, #c9925a 0%, #7d4b1e 100%)";
  var DORE = "linear-gradient(135deg, #f6e27a 0%, #c9a227 60%, #f1d873 100%)";
  var SUNBURST = `radial-gradient(circle at 45% 40%, #f6c35a 0%, #d4731a 45%, ${BRUN} 80%, #2b1608 100%)`;
  var ROSE_DORE = "linear-gradient(135deg, #f3cdbf 0%, #d39a86 100%)";
  var BASE = {
    noir: NOIR,
    gris: GRIS,
    blanc: "#ffffff",
    naturel: "#d9b27c",
    marron: BRUN,
    bleu: "#1f4fc4",
    jaune: "#f2c100",
    rouge: "#cf2727",
    transparent: TRANSPARENT,
    multicolore: MULTICOLORE,
    vert: "#2d7a46",
    degrade: DEGRADE,
    bronze: BRONZE,
    beige: "#e7d6b8",
    orange: "#ef8a2b",
    violet: "#7a3fb5",
    dore: DORE,
    rose: "#f2a9c4",
    sunburst: SUNBURST,
    "rose dore": ROSE_DORE
  };
  var TRADUCTIONS = {
    blanc: ["white", "weiß", "blanco", "bianco", "wit", "branco"],
    bleu: ["blue", "blau", "azul", "blu", "blauw"],
    bronze: ["bronce", "bronzo", "brons"],
    dore: ["gold", "dorado", "oro", "goud", "dourado"],
    gris: ["grey", "grau", "grigio", "grijs", "cinza"],
    jaune: ["yellow", "gelb", "amarillo", "giallo", "geel", "amarelo"],
    marron: ["brown", "braun", "marrone", "bruin", "marrom"],
    multicolore: ["multicolor", "mehrfarbig", "multicor"],
    naturel: ["natural", "natur", "naturale", "natuurlijk"],
    noir: ["black", "schwarz", "negro", "nero", "zwart", "preto"],
    rose: ["pink", "rosa", "roze"],
    "rose dore": ["rose gold", "rosegold", "oro rosa", "rosegoud", "dourado rosa"],
    rouge: ["red", "rot", "rojo", "rosso", "rood", "vermelho"],
    transparent: ["transparente", "trasparente", "transparant"],
    vert: ["green", "grun", "verde", "groen"],
    violet: ["purple", "lila", "violeta", "viola", "paars"]
  };
  var ALIAS = Object.fromEntries(
    Object.entries(TRADUCTIONS).flatMap(([base, mots]) => mots.map((mot) => [mot, BASE[base]]))
  );
  var PASTILLES = Object.freeze({ ...BASE, ...ALIAS });
  function cleCouleur(valeur) {
    return String(valeur != null ? valeur : "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim().replace(/\s+/g, " ");
  }
  var IMPOSEES = {};
  function imposerCouleurs(couleurs) {
    for (const [valeur, fond] of Object.entries(couleurs || {})) {
      const cle = cleCouleur(valeur);
      if (cle && fond) IMPOSEES[cle] = String(fond);
    }
  }
  function oublierCouleursImposees() {
    for (const cle of Object.keys(IMPOSEES)) delete IMPOSEES[cle];
  }
  function stylePastille(valeur) {
    const cle = cleCouleur(valeur);
    if (!cle) return null;
    return IMPOSEES[cle] || PASTILLES[cle] || null;
  }

  // frontend/js/pictogrammes.js
  var BOITE = "0 0 512 512";
  var FAMILLES = Object.freeze({
    /* Strat : deux cornes, la gauche plus longue — « ST ». */
    "s-style": '<path d="M236.805,479.938 C206.378,478.478 192.996,476.829 177.667,472.655 C139.819,462.351 114.143,437.169 106.896,403.241 C105.425,396.354 105.081,392.379 105.085,382.276 C105.093,371.216 105.595,366.275 107.854,354.982 C111.271,337.925 117.201,321.117 129.626,293.273 C140.955,267.893 142.141,265.073 145.088,256.493 C149.040,245.006 150.622,235.472 150.255,225.433 C149.895,215.738 148.494,209.183 143.993,196.161 C132.134,161.845 128.862,151.679 125.844,139.792 C122.383,126.169 121.251,116.699 121.615,104.429 C121.991,91.632 124.107,81.944 128.589,72.510 C135.033,58.942 145.144,50.789 154.669,51.470 C159.891,51.846 162.952,54.599 164.475,60.283 C164.768,61.371 165.282,65.908 165.614,70.370 C167.042,89.338 169.297,97.993 175.108,106.778 C182.620,118.143 196.655,125.766 210.041,125.746 C220.282,125.730 227.893,120.979 230.682,112.866 C231.378,110.853 231.477,108.669 231.841,87.451 C232.058,74.686 232.327,59.077 232.442,52.767 C232.553,46.458 232.644,39.203 232.647,36.648 L232.651,32.000 L256.144,32.000 L279.637,32.000 L279.866,40.406 C279.993,45.030 280.302,72.210 280.551,100.813 C280.923,143.780 281.101,153.087 281.563,154.397 C283.565,160.069 289.107,164.733 296.168,166.683 C297.457,167.039 300.472,167.431 302.865,167.549 C318.035,168.313 332.093,160.038 339.356,146.074 C341.943,141.090 343.648,136.216 345.610,128.186 C347.659,119.792 348.596,117.447 350.899,114.947 C353.296,112.348 355.875,111.323 360.032,111.315 C363.058,111.311 363.680,111.446 366.077,112.629 C371.413,115.255 376.539,121.802 379.028,129.160 C381.575,136.695 381.979,139.618 381.975,150.473 C381.971,161.098 381.812,162.431 379.399,172.031 C377.307,180.370 373.501,190.469 367.928,202.490 C361.195,216.999 359.530,221.359 358.015,228.400 C356.346,236.157 356.654,245.904 358.842,254.586 C360.835,262.486 363.676,268.787 376.207,293.075 C395.262,330.021 402.730,350.520 406.179,375.353 C407.236,382.932 407.137,398.898 406.001,405.021 C402.995,421.235 396.243,434.384 384.680,446.544 C369.399,462.620 349.823,472.192 323.632,476.401 C307.133,479.052 294.325,479.819 264.297,479.957 C250.697,480.021 238.328,480.013 236.805,479.938 Z"/>',
    /* Telecaster : une corne courte, épaule pleine — « TL ». */
    "t-style": '<path d="M381.847,263.984 C357.199,224.739 371.163,202.948 371.163,202.948 C370.532,203.814 389.477,169.995 394.661,150.354 C399.846,130.713 399.573,108.798 389.472,91.185 C386.492,85.979 382.548,81.089 376.706,79.035 C374.817,78.376 372.795,78.104 370.804,78.296 C363.277,79.051 360.430,85.375 358.023,91.185 C354.272,100.249 350.521,109.307 346.771,118.359 C344.235,124.496 341.667,130.686 337.814,136.080 C333.962,141.494 328.660,146.122 322.266,147.888 C312.588,150.584 302.331,146.395 293.819,141.055 C283.225,131.232 277.329,117.370 277.618,102.934 L279.037,32.000 L229.560,32.000 L227.218,80.142 C220.124,90.254 204.966,82.732 200.515,80.212 C199.402,79.580 198.278,78.965 197.128,78.403 C158.194,59.666 136.488,78.735 136.488,78.735 C132.245,81.143 129.120,85.118 126.638,89.307 C116.285,106.787 115.702,128.701 120.608,148.407 C125.514,168.117 143.978,202.209 143.358,201.332 C143.358,201.332 157.012,223.316 131.812,262.213 C131.812,262.213 42.060,397.688 132.250,456.204 C142.785,463.053 154.508,467.847 166.744,470.693 C185.588,475.091 222.205,479.746 254.885,479.981 L255.693,479.992 C287.431,480.211 325.054,476.076 343.962,471.956 C356.230,469.270 368.022,464.642 378.653,457.954 C469.667,400.716 381.847,263.984 381.847,263.984 Z"/>',
    /* Le pan coupé unique, épaule droite pleine. */
    "single-cut": '<path d="M241.253,479.351 C176.598,475.456 129.054,445.953 109.290,397.440 C97.743,369.103 97.088,341.352 107.233,310.872 C113.543,291.925 123.147,276.231 143.101,252.276 C153.580,239.693 158.184,233.176 161.149,226.728 C164.891,218.581 166.194,212.521 165.745,205.395 C165.265,197.758 163.191,191.569 155.417,174.527 C145.539,152.858 142.986,142.157 144.610,129.132 C148.238,99.948 168.161,77.281 198.107,68.280 C205.972,65.917 214.623,64.309 223.297,63.607 L229.966,63.066 L229.973,54.202 C229.973,51.182 230.105,42.586 230.303,32.000 L282.188,32.000 C283.234,75.605 284.018,93.763 284.835,98.446 C288.333,118.461 298.699,129.795 314.498,130.877 C328.507,131.837 339.589,125.588 347.493,112.265 C352.020,104.643 355.549,102.600 360.572,104.696 C366.197,107.044 369.604,116.258 369.627,129.177 C369.649,142.272 367.066,151.761 358.331,170.769 C351.266,186.127 349.017,191.645 347.638,196.950 C346.228,202.392 346.114,212.338 347.394,217.628 C348.918,223.885 355.328,235.425 361.616,243.222 C364.871,247.261 371.120,254.982 375.495,260.371 C398.498,288.716 409.496,314.698 411.752,346.016 C412.316,353.813 411.264,367.197 409.374,376.381 C404.870,398.263 392.202,421.586 376.258,437.363 C350.740,462.614 315.405,476.531 269.599,479.374 C255.994,480.212 255.620,480.212 241.253,479.351 Z"/>',
    /* SG : deux cornes longues et symétriques. */
    "double-cut": '<path d="M385.210,313.779 C383.546,311.600 381.858,309.432 380.358,307.152 C351.646,263.893 367.950,223.966 375.755,201.940 C380.593,188.262 383.436,173.878 383.041,159.378 C381.738,113.046 351.425,86.811 339.272,78.121 C337.724,77.010 335.656,78.501 336.218,80.323 C341.032,95.949 344.038,123.241 318.001,131.922 C306.622,135.712 297.893,130.705 291.815,124.251 C285.101,117.124 281.494,107.621 281.234,97.829 L279.507,32.000 L231.376,32.000 L230.857,97.103 C230.771,107.448 226.894,117.494 219.628,124.857 C213.558,131.013 205.017,135.591 194.009,131.922 C167.957,123.241 170.963,95.949 175.786,80.323 C176.340,78.501 174.286,77.010 172.733,78.121 C160.580,86.811 130.257,113.046 128.964,159.378 C128.560,173.878 131.416,188.262 136.254,201.940 C144.045,223.966 160.358,263.893 131.647,307.152 C130.137,309.432 128.459,311.600 126.799,313.779 C115.262,328.933 60.970,409.219 162.465,459.966 C180.932,469.200 200.852,475.125 221.215,478.496 C233.281,480.502 278.724,480.502 290.790,478.496 C311.153,475.125 331.072,469.200 349.530,459.966 C451.025,409.219 396.743,328.933 385.210,313.779 Z"/>',
    /* Jazzmaster : le corps décalé, les lobes désaxés. */
    offset: '<path d="M365.957,319.357 C353.138,291.852 362.499,256.721 368.701,239.282 C372.900,227.502 375.667,215.271 376.593,202.800 C381.154,140.602 343.985,151.175 331.161,155.180 C324.209,157.345 316.963,158.502 309.686,158.353 C280.793,157.783 280.793,139.707 280.793,139.707 L280.170,32.000 L238.764,32.000 L237.245,95.310 C237.060,103.396 231.776,110.674 223.974,112.853 C213.293,115.813 205.956,106.651 205.956,106.651 C164.194,54.438 153.744,68.360 153.744,68.360 C129.389,85.763 157.229,165.816 157.229,165.816 C178.546,223.683 141.236,286.880 131.586,301.769 C129.235,305.394 126.948,309.060 124.909,312.866 C105.504,349.168 109.138,377.808 118.070,397.710 C125.533,414.317 138.542,427.869 154.372,436.859 C245.820,488.814 319.047,481.966 348.532,475.832 C358.657,473.726 368.471,470.114 377.113,464.455 C436.463,425.667 367.521,322.716 365.957,319.357 Z"/>',
    /* Caisse creuse — sert à « Hollow body » ET à « Caisse & Demi-caisse »,
       les deux valeurs décrivant le même corps. */
    "semi-acoustique": '<path d="M234.775,479.728 C199.971,476.110 175.513,467.969 151.604,452.023 C116.559,428.648 93.589,393.631 87.353,354.077 C85.832,344.428 85.853,324.864 87.401,314.674 C90.704,292.944 98.366,274.216 111.790,255.028 C115.559,249.642 122.090,241.425 137.001,223.314 C147.013,211.157 152.488,201.611 154.928,192.072 C156.607,185.507 156.675,180.861 155.195,174.331 C153.763,168.006 152.269,164.668 143.888,149.030 C131.759,126.396 129.360,119.022 129.312,104.296 C129.285,94.175 130.272,89.398 133.993,81.682 C138.728,71.869 146.293,64.975 155.428,62.159 C160.759,60.507 164.912,60.418 170.333,61.830 C176.658,63.481 181.509,66.469 191.713,75.008 C196.928,79.373 202.828,83.690 204.836,84.608 C211.894,87.836 220.063,86.335 224.805,80.928 C229.259,75.857 230.582,71.197 231.308,58.040 C231.464,55.196 231.726,45.990 232.058,32.000 L279.755,32.000 C280.379,56.237 281.240,68.857 282.498,72.931 C285.979,84.183 297.245,89.432 307.332,84.492 C309.333,83.512 314.027,80.079 317.762,76.865 C330.405,65.969 337.943,61.796 346.433,61.001 C360.262,59.712 374.571,70.704 380.053,86.828 C383.609,97.272 383.829,107.976 380.752,120.037 C378.682,128.130 374.941,136.606 367.786,149.394 C360.488,162.434 358.809,166.114 357.096,172.823 C354.554,182.759 355.808,192.586 360.913,202.728 C365.121,211.095 367.588,214.453 385.240,235.868 C406.148,261.230 415.844,278.403 421.525,300.105 C426.528,319.245 427.158,342.736 423.177,361.903 C415.776,397.544 391.860,431.533 359.152,452.907 C338.566,466.352 315.767,474.623 288.569,478.501 C280.997,479.577 241.936,480.468 234.775,479.728 Z"/>',
    /* Flying V. */
    "v-style": '<path d="M438.243,474.552 L406.501,389.514 L377.822,312.680 L289.597,76.274 C288.534,73.432 285.808,71.541 282.758,71.541 L275.607,71.550 L274.547,32.000 L235.246,32.000 L235.176,71.612 L227.954,71.637 C224.900,71.637 222.186,73.528 221.135,76.391 L73.752,475.134 C72.381,478.848 77.135,481.732 79.782,478.786 C110.622,444.509 190.914,359.688 246.258,340.815 C252.428,338.708 259.118,338.700 265.301,340.773 C289.938,349.092 319.553,370.442 347.559,394.567 C356.888,402.608 366.037,410.972 374.772,419.245 C398.781,442.033 419.620,464.297 432.222,478.221 C434.882,481.155 439.627,478.254 438.243,474.552 Z"/>',
    /* Explorer : les arêtes vives des corps taillés en pointes. */
    extreme: '<path d="M382.582,108.027 C399.548,40.743 368.449,85.597 368.449,85.597 C302.401,185.383 288.197,147.294 285.569,135.195 C285.218,133.620 285.028,132.018 284.957,130.408 L280.843,32.000 L248.682,32.000 L249.666,138.638 C249.676,139.658 249.458,140.673 249.044,141.608 C243.421,154.346 230.324,160.489 220.871,163.360 C214.788,165.205 210.954,171.998 211.928,179.040 L223.271,261.308 C223.893,265.789 223.162,270.385 221.212,274.352 L125.207,469.012 C122.273,474.947 127.400,481.831 133.068,479.554 L368.846,384.761 C374.883,382.333 378.049,374.841 375.934,367.985 L343.109,261.740 C341.193,255.535 341.200,248.932 343.137,242.733 C351.061,217.284 372.824,146.752 382.582,108.027 Z"/>',
    /* Superstrat : le corps effilé des modèles modernes. */
    superstrat: '<path d="M386.388,412.536 C392.897,390.723 389.817,364.643 377.236,335.040 C372.766,324.513 364.712,308.126 351.735,289.776 L351.288,289.149 C343.719,278.330 342.357,265.745 347.232,251.709 C349.771,244.413 353.544,237.503 357.199,230.810 C359.983,225.723 362.861,220.443 365.187,215.026 C367.071,210.613 369.186,204.791 368.776,198.738 C368.362,192.578 365.046,187.137 360.326,184.872 C351.698,180.746 341.853,187.331 336.046,194.226 C334.788,195.733 333.545,197.325 332.339,198.870 C327.874,204.621 323.253,210.551 316.527,213.024 C309.825,215.488 301.610,213.990 295.091,209.129 C289.971,205.309 285.760,199.412 282.854,192.022 L284.211,32.000 L241.791,32.000 L236.775,167.204 C227.515,168.763 219.192,166.639 212.047,160.874 C191.120,143.993 185.251,99.694 184.846,85.502 L184.846,85.356 C184.846,85.224 184.846,85.078 184.822,84.947 C183.701,78.277 180.682,74.010 176.321,72.926 C172.242,71.923 167.668,73.963 164.381,78.253 C148.507,99.044 150.523,130.960 155.007,154.086 C159.859,179.093 168.568,199.483 168.648,199.666 C185.647,247.055 152.666,307.486 137.693,331.267 C130.067,343.382 125.211,356.419 123.252,369.994 C118.104,405.701 134.702,430.034 149.539,444.150 C165.596,459.439 183.108,466.387 183.282,466.457 C209.438,476.504 234.500,479.782 255.512,479.989 C266.567,480.098 276.505,479.363 284.889,478.299 C311.238,474.997 328.382,467.955 328.547,467.880 C359.629,455.629 379.092,437.014 386.388,412.536 Z"/>',
    /* Sans tête : le manche s'arrête net. */
    headless: '<path d="M189.341,479.476 C186.733,478.974 183.150,477.938 181.375,477.166 C165.604,470.290 147.707,448.124 130.703,414.402 C122.872,398.868 117.209,385.232 115.055,376.689 C113.049,368.770 113.503,358.338 116.199,350.392 C118.963,342.242 127.844,324.724 137.843,307.714 C152.855,282.161 159.284,267.942 162.359,253.519 C164.067,245.526 164.080,225.995 162.393,216.938 C159.643,202.218 154.427,186.935 142.247,157.981 C127.282,122.369 123.759,109.593 123.773,90.936 C123.780,81.296 124.986,75.383 127.899,70.634 C131.760,64.354 137.721,64.754 141.610,71.555 C142.700,73.459 145.159,78.980 147.077,83.823 C156.506,107.655 167.271,123.460 181.050,133.696 C190.459,140.687 198.351,143.755 206.955,143.762 C214.948,143.769 219.446,141.777 223.003,136.656 C226.566,131.521 226.824,128.886 227.589,88.904 C227.998,67.581 228.357,48.814 228.676,32.000 L278.009,32.000 C278.982,77.847 280.034,126.379 280.808,160.711 C281.262,180.790 281.932,187.619 283.978,193.099 C289.384,207.556 309.063,210.848 329.230,200.680 C337.292,196.615 341.539,193.397 355.034,181.116 C363.596,173.325 368.968,170.195 373.798,170.195 C379.360,170.195 382.070,173.698 382.070,180.879 C382.070,186.508 380.187,191.453 371.536,208.551 C367.356,216.809 362.648,226.869 361.063,230.920 C350.095,258.966 350.881,284.078 363.630,312.842 C367.458,321.472 372.030,329.540 382.605,346.327 C394.778,365.661 396.770,369.136 397.725,372.774 C399.358,378.986 396.614,384.148 390.233,386.891 C388.377,387.691 381.745,389.418 375.492,390.739 C348.232,396.477 331.032,402.310 309.314,413.189 C291.599,422.063 283.043,427.645 253.115,449.865 C242.364,457.852 230.935,466.042 227.711,468.068 C212.293,477.782 199.862,481.474 189.341,479.476 Z"/>'
  });
  var GENERIQUE = '<path d="M366.792,262.806 C364.233,259.298 361.367,255.998 358.368,252.782 C355.469,249.652 352.999,246.497 350.922,243.343 C338.980,225.259 339.802,202.788 351.271,184.462 C353.897,180.255 355.889,175.916 357.377,171.567 C372.198,128.004 341.157,82.127 291.630,73.014 C290.143,72.735 288.684,72.494 287.244,72.277 C283.750,71.767 281.153,69.099 281.115,65.907 L280.704,32.000 L234.029,32.000 L233.368,65.690 C233.307,68.882 230.686,71.540 227.192,72.013 C225.610,72.230 224.000,72.480 222.371,72.773 C172.782,81.536 141.415,127.206 155.935,170.864 C157.385,175.226 159.354,179.594 161.955,183.811 C173.292,202.207 173.958,224.678 161.894,242.692 C159.788,245.822 157.314,248.976 154.377,252.069 C151.364,255.275 148.475,258.552 145.887,262.032 C130.149,283.280 87.648,350.259 123.742,413.347 C123.742,413.347 150.722,474.740 255.164,480.000 L256.009,480.000 C357.957,477.205 387.889,414.254 387.889,414.254 C424.417,351.421 382.388,284.154 366.792,262.806 Z"/>';
  function entrees(parFamille) {
    const table = {};
    for (const [famille, valeurs] of Object.entries(parFamille)) {
      for (const valeur of valeurs) table[valeur.trim().toLowerCase()] = famille;
    }
    return table;
  }
  var FORMES = Object.freeze(entrees({
    /* Pan coupé unique */
    "single-cut": [
      "Single cut",
      "Single Cut",
      "Type LP",
      "LP type",
      "LP-Typ",
      "Tipo LP",
      "LP-type",
      "Les Paul",
      "Single Cutaway",
      "175"
    ],
    /* Double pan coupé pointu */
    "double-cut": [
      "double cut",
      "Double cut",
      "Double Cut",
      "SG",
      "Double Cutaway",
      "Snakebyte"
    ],
    /* Double pan coupé arrondi */
    "s-style": [
      "ST",
      "Type Strat",
      "Stratocaster type",
      "Stratocaster-Typ",
      "Tipo Stratocaster",
      "Strat-type",
      "Stratocaster",
      "Super-Strat",
      "Stingray",
      "Sterling"
    ],
    /* Épaule franche */
    "t-style": [
      "TL",
      "Type Tele",
      "Telecaster type",
      "Telecaster-Typ",
      "Tipo Telecaster",
      "Tipo de telecaster",
      "Tele-type",
      "Telecaster"
    ],
    /* Corps décalé */
    offset: [
      "Asymétrique",
      "Asymmetric",
      "Asymmetrisch",
      "Asimétrico",
      "Asimmetrico",
      "Assimétrico",
      "Jaguar",
      "Jazz",
      "Mariposa"
    ],
    /* Caisse creuse */
    "semi-acoustique": [
      "Hollow body",
      "Hollowbody",
      "Caisse & Demi-caisse",
      "Hollow and Semi-hollow",
      "Hollow & Semi-Hollow",
      "Hueca y semi hueca",
      "Cavo e semi-cavo",
      "hollow en semi-hollow",
      "Oco e semi-oco",
      "335"
    ],
    /* Sans tête */
    headless: [
      "sans tête",
      "Headless",
      "Sin cabeza",
      "Senza paletta",
      "Sem cabeça"
    ],
    /* Ailes en pointe */
    "v-style": ["Flying"],
    /* Arêtes vives */
    extreme: ["Extreme"],
    /* Corps effilé des modèles modernes */
    superstrat: [
      "Metal / Moderne",
      "Metal / Modern",
      "Metal / Moderno",
      "Metallo / Moderno",
      "metaal / modern",
      "Super-Strat"
    ]
  }));
  var IMPOSEES2 = {};
  function imposerFormes(formes) {
    for (const [valeur, famille] of Object.entries(formes || {})) {
      const cle = String(valeur == null ? "" : valeur).trim().toLowerCase();
      if (cle && famille) IMPOSEES2[cle] = String(famille);
    }
  }
  function oublierFormesImposees() {
    for (const cle of Object.keys(IMPOSEES2)) delete IMPOSEES2[cle];
  }
  function familleDe(valeur) {
    const cle = String(valeur == null ? "" : valeur).trim().toLowerCase();
    return IMPOSEES2[cle] || FORMES[cle] || null;
  }
  function silhouette(valeur) {
    const famille = familleDe(valeur);
    const dessin = famille ? FAMILLES[famille] : GENERIQUE;
    const classes = `wb-vignette-svg${famille ? "" : " wb-vignette-svg--generique"}`;
    return `<svg class="${classes}" viewBox="${BOITE}" fill="currentColor" aria-hidden="true" focusable="false">${dessin}</svg>`;
  }

  // frontend/js/facettes.js
  var TITRES = Object.freeze(Object.fromEntries(
    Object.entries(CLES_DE_FACETTE).map(([champ, cle]) => [champ, t(cle)])
  ));
  var LIMITE_VISIBLE = 5;
  var LIMITE_VIGNETTES = 9;
  var SEUIL_FORMES = 0.6;
  var PALIER_VALEURS = 45;
  var MIN_OPTIONS = 2;
  var TITRES_ENREGISTRES = {};
  function enregistrerTitres(libelles) {
    Object.assign(TITRES_ENREGISTRES, libelles || {});
  }
  var TITRES_IMPOSES = {};
  function imposerTitres(libelles) {
    Object.assign(TITRES_IMPOSES, libelles || {});
  }
  function titreFacette(champ) {
    if (TITRES_IMPOSES[champ]) return TITRES_IMPOSES[champ];
    if (CLES_DE_FACETTE[champ]) return t(CLES_DE_FACETTE[champ]);
    if (TITRES_ENREGISTRES[champ]) return TITRES_ENREGISTRES[champ];
    const queue = String(champ).split(".").pop().replace(/_/g, " ");
    return queue.charAt(0).toUpperCase() + queue.slice(1);
  }
  function libelleValeurFacette(champ, valeur) {
    const brute = String(valeur != null ? valeur : "");
    if (champ !== "disponibilites") return brute;
    const cle = {
      web: "web",
      magasin: "magasin",
      "showroom deluxe": "showroom_deluxe"
    }[brute.trim().toLowerCase()];
    return cle ? t(`disponibilite.${cle}`) : brute;
  }
  var VALEURS_NON_PROPOSEES = { disponibilites: /* @__PURE__ */ new Set(["non vendable"]) };
  function estProposee(champ, valeur) {
    const exclues = VALEURS_NON_PROPOSEES[champ];
    return !exclues || !exclues.has(String(valeur != null ? valeur : "").trim().toLowerCase());
  }
  function bloc(champ, corps, {
    masque = false,
    pliage = null,
    titre = null
  } = {}) {
    return `<div class="wb-facet-block" data-champ="${esc(champ)}"${masque ? " hidden" : ""}>` + interieurBloc(champ, corps, pliage, titre) + `</div>`;
  }
  function interieurBloc(champ, corps, pliage = null, libelle = null) {
    const titre = esc(libelle || titreFacette(champ));
    if (!pliage) return `<p class="wb-facet-title">${titre}</p>${corps}`;
    const replie = !!pliage.replie;
    const idCorps = `wb-facette-corps-${esc(champ)}`;
    return `<button type="button" class="wb-facet-toggle" data-basculer-facette="${esc(champ)}" aria-expanded="${replie ? "false" : "true"}" aria-controls="${idCorps}"><span class="wb-facet-title">${titre}</span><span class="wb-facet-toggle-icone" aria-hidden="true"></span></button><div class="wb-facet-corps" id="${idCorps}"${replie ? " hidden" : ""}>${corps}</div>`;
  }
  function estRepliee(champ, replies, ouvertes, impose = void 0) {
    const explicite = (replies || {})[champ];
    if (typeof explicite === "boolean") return explicite;
    if (typeof impose === "boolean") return impose;
    return !(ouvertes || []).includes(champ);
  }
  function appliquerRepli(blocDom, replie) {
    if (!blocDom) return;
    const bouton = blocDom.querySelector("[data-basculer-facette]");
    const corps = blocDom.querySelector(".wb-facet-corps");
    if (bouton) bouton.setAttribute("aria-expanded", replie ? "false" : "true");
    if (corps) corps.hidden = !!replie;
  }
  function ordonnerValeurs(valeurs, ordre = [], {
    alpha = false,
    mesure = false,
    langue: langue2 = "fr"
  } = {}) {
    const liste2 = [...valeurs || []];
    if (!ordre || !ordre.length) {
      if (mesure) {
        return liste2.map((v, recu) => ({ v, recu, taille: mesureCanonique(v.value) })).sort((a, b) => {
          if (a.taille === null || b.taille === null) {
            return (a.taille === null) - (b.taille === null) || a.recu - b.recu;
          }
          return a.taille - b.taille || a.recu - b.recu;
        }).map((x) => x.v);
      }
      return alpha ? liste2.sort((a, b) => String(a.value).localeCompare(String(b.value), langue2)) : liste2;
    }
    const rang = new Map(ordre.map((valeur, i) => [valeur, i]));
    return liste2.map((v, i) => ({
      v,
      place: rang.has(v.value) ? rang.get(v.value) : Infinity,
      recu: i
    })).sort((a, b) => a.place - b.place || a.recu - b.recu).map((x) => x.v);
  }
  function estBooleen(valeurs, booleen = false) {
    if (booleen) return true;
    if (!valeurs || !valeurs.length) return false;
    return valeurs.every((v) => v.value === "true" || v.value === "false");
  }
  function partDesFormes(valeurs) {
    const total = (valeurs || []).reduce((somme, v) => somme + (Number(v.count) || 0), 0);
    if (!total) return 0;
    const formes = (valeurs || []).reduce(
      (somme, v) => somme + (familleDe(v.value) ? Number(v.count) || 0 : 0),
      0
    );
    return formes / total;
  }
  function offreUnChoix(valeurs, total = null) {
    const liste2 = valeurs || [];
    if (liste2.length >= MIN_OPTIONS) return true;
    if (!Number.isFinite(total) || total <= 0) return false;
    return liste2.some((v) => Number(v.count) > 0 && Number(v.count) < total);
  }
  function listeAcocher({
    champ,
    valeurs,
    cochees,
    deplie = false,
    recherchable = false,
    terme = "",
    pastilles = false,
    vignettes = false,
    pliage = null,
    booleen = false,
    total = null
  }) {
    const selection = new Set(cochees || []);
    const esBooleen = estBooleen(valeurs, booleen);
    if (esBooleen) {
      const vTrue = (valeurs || []).find((v) => v.value === "true") || (selection.has("true") ? { value: "true", count: 0 } : null);
      const coche = selection.has("true");
      const aFalse = (valeurs || []).some((v) => v.value === "false");
      if (!vTrue && !coche || !aFalse && !coche) {
        return bloc(champ, "", { masque: true, pliage });
      }
      const libelleAffiche = t("oui");
      const compte = vTrue ? vTrue.count : 0;
      const corps2 = `<ul class="wb-facet-list">
      <li class="wb-facet-item">
        <label class="wb-facet-label">
          <input type="checkbox" class="wb-facet-cb"
                 data-facette="${esc(champ)}" data-valeur="true"
                 ${coche ? "checked" : ""}>
          <span class="wb-facet-labeltext" title="${esc(libelleAffiche)}">${esc(libelleAffiche)}</span>
          <span class="wb-facet-count">${formaterEntier(compte)}</span>
        </label>
      </li>
    </ul>`;
      return bloc(champ, corps2, { pliage });
    }
    const toutes = valeurs.filter((v) => estProposee(champ, v.value));
    for (const valeur of selection) {
      if (!toutes.some((v) => v.value === valeur)) {
        toutes.unshift({ value: valeur, count: 0 });
      }
    }
    const paliers = Number(deplie) || 0;
    const base = vignettes ? LIMITE_VIGNETTES : LIMITE_VISIBLE;
    const limite = pastilles ? Infinity : base + paliers * PALIER_VALEURS;
    const visibles = toutes.slice(0, limite);
    const reste = toutes.length > limite;
    const enRecherche = recherchable && !!String(terme || "").trim();
    if (!offreUnChoix(toutes, total) && !selection.size && !enRecherche) {
      return bloc(champ, "", { masque: true, pliage });
    }
    if (pastilles) {
      return bloc(champ, listePastilles(champ, visibles, selection), { pliage });
    }
    const enFormes = vignettes && partDesFormes(toutes) >= SEUIL_FORMES;
    if (enFormes) {
      const suite = reste ? `<button type="button" class="wb-facet-showmore" data-deplier="${esc(champ)}">${esc(t("afficher_plus"))}</button>` : "";
      const retour = paliers ? `<button type="button" class="wb-facet-showmore" data-replier="${esc(champ)}">${esc(t("afficher_moins"))}</button>` : "";
      return bloc(
        champ,
        listeVignettes(champ, visibles, selection) + suite + retour,
        { pliage, titre: t("facette.forme_guitare") }
      );
    }
    const options = visibles.map((v) => {
      const coche = selection.has(v.value);
      const libelle = libelleValeurFacette(champ, v.value);
      return `<li class="wb-facet-item">
      <label class="wb-facet-label">
        <input type="checkbox" class="wb-facet-cb"
               data-facette="${esc(champ)}" data-valeur="${esc(v.value)}"
               ${coche ? "checked" : ""}>
        <span class="wb-facet-labeltext" title="${esc(libelle)}">${esc(libelle)}</span>
        <span class="wb-facet-count">${formaterEntier(v.count)}</span>
      </label>
    </li>`;
    }).join("");
    const invite = t(champ === "vendor" ? "trouver_marque" : "trouver_valeur");
    const recherche = recherchable ? `<div class="wb-facet-searchbox"><form onsubmit="return false;">
         <input type="search" data-recherche-facette="${esc(champ)}"
                value="${esc(terme)}" placeholder="${esc(invite)}"
                autocomplete="off" aria-label="${esc(invite)}">
       </form></div>` : "";
    const plus = reste ? `<button type="button" class="wb-facet-showmore" data-deplier="${esc(champ)}">${esc(t("afficher_plus"))}</button>` : "";
    const moins = paliers ? `<button type="button" class="wb-facet-showmore" data-replier="${esc(champ)}">${esc(t("afficher_moins"))}</button>` : "";
    const classes = paliers ? "wb-facet-list wb-facet-list--defilante" : "wb-facet-list";
    const corps = recherche + `<ul class="${classes}">${options}</ul>` + plus + moins;
    return bloc(champ, corps, { pliage });
  }
  function listePastilles(champ, valeurs, selection) {
    const items = valeurs.map((v) => {
      const coche = selection.has(v.value);
      const fond = stylePastille(v.value);
      const classes = ["wb-pastille", ...fond ? [] : ["wb-pastille--inconnue"]].join(" ");
      const style = fond ? ` style="background:${fond}"` : "";
      const legende = `${v.value} (${formaterEntier(v.count)})`;
      return `<li class="wb-facet-item wb-facet-item--pastille">
      <label class="wb-facet-pastille" title="${esc(legende)}">
        <input type="checkbox" class="wb-facet-cb"
               data-facette="${esc(champ)}" data-valeur="${esc(v.value)}"
               ${coche ? "checked" : ""} aria-label="${esc(legende)}">
        <span class="${classes}"${style}></span>
      </label>
    </li>`;
    }).join("");
    return `<ul class="wb-facet-list wb-facet-list--pastilles">${items}</ul>`;
  }
  function listeVignettes(champ, valeurs, selection) {
    const items = valeurs.map((v) => {
      const coche = selection.has(v.value);
      const legende = `${v.value} (${formaterEntier(v.count)})`;
      return `<li class="wb-facet-item wb-facet-item--vignette">
      <label class="wb-vignette" title="${esc(legende)}">
        <input type="checkbox" class="wb-facet-cb"
               data-facette="${esc(champ)}" data-valeur="${esc(v.value)}"
               ${coche ? "checked" : ""} aria-label="${esc(legende)}">
        <span class="wb-vignette-cadre">${silhouette(v.value)}</span>
        <span class="wb-vignette-nom">${esc(v.value)}</span>
        <span class="wb-vignette-count">${formaterEntier(v.count)}</span>
      </label>
    </li>`;
    }).join("");
    return `<ul class="wb-facet-list wb-facet-list--vignettes">${items}</ul>`;
  }
  function jetons({
    selection,
    prix,
    note,
    formaterPrix: formaterPrix2,
    mesures = {},
    unites = {}
  }) {
    const groupes = [];
    for (const [champ, valeurs] of Object.entries(selection || {})) {
      for (const valeur of valeurs) {
        const estBool = valeur === "true" || valeur === "false";
        groupes.push({
          libelle: titreFacette(champ),
          /* ⛔ La descente porte un chemin CUMULÉ : l'écrire entier fait déborder
             la barre des filtres actifs dès le second niveau, et le visiteur
             vient de cliquer sur la seule feuille. L'ACTION, elle, garde le
             chemin — `basculerValeur` le compare à la branche ouverte pour la
             refermer, et la feuille seule ne correspondrait à rien. */
          valeur: champ === CLE ? feuille(valeur) : estBool ? valeur === "true" ? "" : t("non") : libelleValeurFacette(champ, valeur),
          action: `facette:${champ}:${valeur}`
        });
      }
    }
    if (prix) {
      const de = Number.isFinite(prix.de) ? formaterPrix2(prix.de) : null;
      const a = Number.isFinite(prix.a) ? formaterPrix2(prix.a) : null;
      groupes.push({
        libelle: titreFacette("price"),
        valeur: de && a ? `${de} – ${a}` : de ? `≥ ${de}` : `≤ ${a}`,
        action: "prix"
      });
    }
    for (const [champ, bornes] of Object.entries(mesures || {})) {
      if (!bornes) continue;
      const unite = unites[champ] || "";
      const ecrire = (n) => unite ? `${n} ${unite}` : `${n}`;
      const de = Number.isFinite(bornes.de) ? ecrire(bornes.de) : null;
      const a = Number.isFinite(bornes.a) ? ecrire(bornes.a) : null;
      groupes.push({
        libelle: titreFacette(champ),
        valeur: de && a ? `${de} – ${a}` : de ? `≥ ${de}` : `≤ ${a}`,
        action: `mesure:${champ}`
      });
    }
    if (Number.isFinite(note)) {
      groupes.push({
        libelle: titreFacette("review_rating"),
        valeur: t("etoiles_et_plus", { n: note }),
        action: "note"
      });
    }
    if (!groupes.length) return { jetonsHtml: "", effacerHtml: "" };
    const jetonsHtml = `<ul class="wb-chips-list">${groupes.map((g) => `
    <li class="wb-chip">
      <span class="wb-chip-label">${esc(g.libelle)}</span>
      ${g.valeur ? `<span>${esc(g.valeur)}</span>` : ""}
      <button type="button" class="wb-chip-delete" data-retirer="${esc(g.action)}"
              aria-label="${esc(t("retirer", { libelle: g.libelle, valeur: g.valeur }).trim())}">✕</button>
    </li>`).join("")}</ul>`;
    const effacerHtml = `<button type="button" class="wb-clear-btn" data-effacer>${esc(t("effacer_filtres"))}</button>`;
    return { jetonsHtml, effacerHtml };
  }
  function messageVide({
    q = "",
    selection = {},
    prix = null,
    note = null
  } = {}) {
    const cochees = Object.values(selection || {}).reduce((total, valeurs) => total + (valeurs || []).length, 0);
    const actifs = cochees + (prix ? 1 : 0) + (Number.isFinite(note) ? 1 : 0);
    const pour = q ? ` ${esc(t("pour"))} <b>${esc(q)}</b>` : "";
    if (!actifs) {
      return `<li class="rec-empty">${t("aucun_resultat", { pour })}</li>`;
    }
    const message = actifs > 1 ? t("aucun_resultat_filtres", { pour, n: actifs }) : t("aucun_resultat_filtre", { pour });
    return `<li class="rec-empty">${message}<br><button type="button" class="wb-clear-btn" data-effacer>${esc(t("effacer_filtres"))}</button></li>`;
  }
  function blocArbreUtile({ ouverte, valeurs, total = null }) {
    if (ouverte) return true;
    const liste2 = valeurs || [];
    if (liste2.length < MIN_OPTIONS) return false;
    if (!Number.isFinite(total) || total <= 0) return true;
    return !liste2.some((v) => Number(v.count) >= total);
  }
  function listeArbre({
    ouverte,
    valeurs,
    deplie = 0,
    total = null,
    pliage = null
  }) {
    const cran = cranRetour(ouverte);
    const retour = cran ? `<button type="button" class="wb-facet-arbre-retour" data-arbre-remonter="${cran.niveau}">${esc(cran.libelle || t("arbre_racine"))}</button>` : "";
    const courant = ouverte ? `<p class="wb-facet-arbre-courant"><span class="wb-facet-arbre-courant-nom">${esc(feuille(ouverte))}</span>` + (Number.isFinite(total) ? `<span class="wb-facet-count">${formaterEntier(total)}</span>` : "") + `</p>` : "";
    const paliers = Number(deplie) || 0;
    const limite = LIMITE_VISIBLE + paliers * PALIER_VALEURS;
    const toutes = valeurs || [];
    const visibles = toutes.slice(0, limite);
    const reste = toutes.length > limite;
    const items = visibles.map((v) => `<li class="wb-facet-item">
        <button type="button" class="wb-facet-label wb-facet-arbre-ligne"
                data-arbre-descendre="${esc(v.value)}">
          <span class="wb-facet-labeltext" title="${esc(feuille(v.value))}">${esc(feuille(v.value))}</span>
          <span class="wb-facet-count">${formaterEntier(v.count)}</span>
        </button>
      </li>`).join("");
    const liste2 = items ? `<ul class="wb-facet-list">${items}</ul>` : "";
    const suite = reste ? `<button type="button" class="wb-facet-showmore" data-deplier="${esc(CLE)}">${esc(t("afficher_plus"))}</button>` : "";
    const moins = paliers ? `<button type="button" class="wb-facet-showmore" data-replier="${esc(CLE)}">${esc(t("afficher_moins"))}</button>` : "";
    return bloc(CLE, retour + courant + liste2 + suite + moins, { pliage });
  }

  // frontend/js/colonne-facettes.js
  var ORDRE_COLONNE = Object.freeze([
    /* ⛔ La DESCENTE par niveau ouvre la colonne (demandé par Simon le
       22/09/2026). Sur une page transverse elle remplace le filtre
       « Univers » plat ; sur un rayon, le bloc se masque de lui-même
       (`facettes.blocArbreUtile`) : une valeur unique, ou une valeur qui
       couvre toute la grille — un produit rangé dans deux univers suffit
       à en montrer deux (29/09, `accessoires-dj`). */
    "chemin",
    "price",
    "vendor",
    "disponibilites",
    "review_rating",
    "ak_filtre_categories5",
    "ak_couleur_unique",
    "product_type"
  ]);
  var OUVERTES_PAR_DEFAUT = Object.freeze([
    "chemin",
    "price",
    "vendor",
    "disponibilites",
    "ak_filtre_categories5",
    "ak_couleur_unique",
    "review_rating"
  ]);
  var STYLES_NATIFS = Object.freeze({ chemin: "arbre" });
  function stylesDeColonne(releve = {}, surcharge = {}) {
    return { ...STYLES_NATIFS, ...releve || {}, ...surcharge || {} };
  }
  var MASQUES_OVERLAY = Object.freeze(["product_type"]);
  var SEUILS_COLONNE = Object.freeze([4, 3]);
  var PASTILLES_COLONNE = Object.freeze(["ak_couleur_unique"]);
  function optionsColonne() {
    return {
      ordre: ORDRE_COLONNE,
      seuils: SEUILS_COLONNE,
      pliage: { ouvertes: OUVERTES_PAR_DEFAUT },
      pastilles: PASTILLES_COLONNE
    };
  }
  function optionsOverlay() {
    return {
      ...optionsColonne(),
      surcharge: { masques: [...MASQUES_OVERLAY] }
    };
  }

  // frontend/js/surcharges.js
  function surchargeDe(table, collectionId) {
    const cle = String(collectionId == null ? "" : collectionId);
    if (!cle) return {};
    const entree = (table || {})[cle];
    return entree && typeof entree === "object" ? entree : {};
  }
  function titresDansLaLangue(surcharge, langue2) {
    const code = String(langue2 || LANGUE_DEFAUT).toLowerCase().split("-")[0];
    const libelles = (surcharge || {}).libelles || {};
    const rendu = {};
    for (const [champ, traductions] of Object.entries(libelles)) {
      const texte = String((traductions || {})[code] || (traductions || {})[LANGUE_DEFAUT] || "").trim();
      if (texte) rendu[champ] = texte;
    }
    return rendu;
  }

  // frontend/js/etoiles.js
  var SEUILS = [4, 3, 2, 1];
  var ETOILE = "M8 1.2l2.1 4.3 4.7.7-3.4 3.3.8 4.7L8 12l-4.2 2.2.8-4.7L1.2 6.2l4.7-.7z";
  function etoiles(remplies) {
    let html = "";
    for (let i = 1; i <= 5; i++) {
      const etat = i <= remplies ? "full" : "empty";
      html += `<svg class="wb-rating-star wb-rating-star--${etat}" viewBox="0 0 16 16" aria-hidden="true"><path d="${ETOILE}"/></svg>`;
    }
    return html;
  }
  function facetteAvis({ comptes, selection, seuils = SEUILS, pliage = null }) {
    const total = seuils.reduce((somme, seuil) => somme + (comptes[seuil] || 0), 0);
    if (!total) return bloc("review_rating", "", { masque: true, pliage });
    const lignes = seuils.map((seuil) => {
      const compte = comptes[seuil] || 0;
      const classes = ["wb-rating-item"];
      if (selection === seuil) classes.push("wb-rating-item--selected");
      if (!compte) classes.push("wb-rating-item--disabled");
      return `<li class="${classes.join(" ")}">
      <a class="wb-rating-link" href="#" data-note="${seuil}"
         aria-label="${esc(t("etoiles_et_plus", { n: seuil }))}">
        ${etoiles(seuil)}
        <span class="wb-rating-label" aria-hidden="true">${esc(t("et_plus"))}</span>
        <span class="wb-rating-count">${esc(formaterEntier(compte))}</span>
      </a>
    </li>`;
    }).join("");
    return bloc("review_rating", `<ul class="wb-rating-list">${lignes}</ul>`, { pliage });
  }

  // frontend/js/echelle-prix.js
  var PALIERS = [100, 500, 2e3];
  function echellePrix(min, max) {
    if (!(max > min)) {
      return { min, max, versPrix: () => min, versPosition: () => 0 };
    }
    const internes = PALIERS.filter((p) => p > min && p < max);
    const prix = [min, ...internes, max];
    const pas = 1 / (prix.length - 1);
    const points = prix.map((p, i) => [i * pas, p]);
    return {
      min,
      max,
      versPrix(position) {
        if (position <= 0) return min;
        for (let i = 1; i < points.length; i++) {
          const [p1, v1] = points[i];
          if (position <= p1) {
            const [p0, v0] = points[i - 1];
            return v0 + (position - p0) / (p1 - p0) * (v1 - v0);
          }
        }
        return max;
      },
      versPosition(valeur) {
        if (valeur <= min) return 0;
        for (let i = 1; i < points.length; i++) {
          const [p1, v1] = points[i];
          if (valeur <= v1) {
            const [p0, v0] = points[i - 1];
            return v1 === v0 ? p0 : p0 + (valeur - v0) / (v1 - v0) * (p1 - p0);
          }
        }
        return 1;
      }
    };
  }
  function arrondiPrix(valeur) {
    if (valeur < 100) return Math.round(valeur);
    if (valeur < 1e3) return Math.round(valeur / 10) * 10;
    return Math.round(valeur / 100) * 100;
  }
  function intervallesSegments(echelle, n) {
    const bornes = [];
    for (let i = 0; i <= n; i++) bornes.push(echelle.versPrix(i / n));
    return bornes.slice(0, -1).map((de, i) => ({
      de,
      a: bornes[i + 1],
      inclusifHaut: i === n - 1
    }));
  }

  // frontend/js/curseur-prix.js
  var NOMBRE_DE_BARRES = 24;
  function gabarit(devise = "EUR") {
    const [min, max] = [t("prix_min"), t("prix_max")];
    const symbole = symboleDevise(devise);
    return `
  <div class="wb-pslider-hist" hidden></div>
  <div class="wb-pslider-track">
    <div class="wb-pslider-rail">&#8203;</div>
    <div class="wb-pslider-fill">&#8203;</div>
    <input type="range" min="0" max="1000" step="1" value="0"
           class="wb-pslider-input" data-borne="bas" aria-label="${min}">
    <input type="range" min="0" max="1000" step="1" value="1000"
           class="wb-pslider-input" data-borne="haut" aria-label="${max}">
  </div>
  <div class="wb-pslider-bounds">
    <label class="wb-pslider-bound" data-symbole="${symbole}"><span>Min (${devise})</span>
      <input type="number" inputmode="numeric" min="0" data-champ="bas"
             aria-label="${min}"></label>
    <label class="wb-pslider-bound" data-symbole="${symbole}"><span>Max (${devise})</span>
      <input type="number" inputmode="numeric" min="0" data-champ="haut"
             aria-label="${max}"></label>
  </div>
  <p class="wb-pslider-avg" hidden></p>`;
  }
  function ligneMoyenne(moyenne, formater = formaterPrix) {
    if (typeof moyenne !== "number" || !Number.isFinite(moyenne)) return null;
    return t("prix_moyen", { prix: formater(moyenne) });
  }
  var SYMBOLES = Object.freeze({ EUR: "€", GBP: "£", USD: "$" });
  function symboleDevise(devise) {
    const code = String(devise || "EUR").toUpperCase();
    return SYMBOLES[code] || code;
  }
  function creerCurseurPrix(noeud, { surChangement, devise = "EUR" }) {
    noeud.classList.add("wb-pslider");
    noeud.innerHTML = gabarit(/^[A-Z]{3}$/.test(String(devise)) ? devise : "EUR");
    const elements = {
      histogramme: noeud.querySelector(".wb-pslider-hist"),
      remplissage: noeud.querySelector(".wb-pslider-fill"),
      bas: noeud.querySelector('[data-borne="bas"]'),
      haut: noeud.querySelector('[data-borne="haut"]'),
      champBas: noeud.querySelector('[data-champ="bas"]'),
      champHaut: noeud.querySelector('[data-champ="haut"]'),
      moyenne: noeud.querySelector(".wb-pslider-avg")
    };
    const barres = [];
    for (let i = 0; i < NOMBRE_DE_BARRES; i++) {
      const barre = document.createElement("div");
      barre.className = "wb-pslider-bar";
      barre.textContent = "​";
      elements.histogramme.appendChild(barre);
      barres.push(barre);
    }
    let etendue = { min: 0, max: 0 };
    let echelle = echellePrix(0, 0);
    let manipulation = false;
    function lecture() {
      let posBas = Number(elements.bas.value);
      let posHaut = Number(elements.haut.value);
      if (posBas > posHaut) {
        if (document.activeElement === elements.bas) {
          posHaut = posBas;
          elements.haut.value = posHaut;
        } else {
          posBas = posHaut;
          elements.bas.value = posBas;
        }
      }
      return {
        posBas,
        posHaut,
        valeurBas: posBas === 0 ? etendue.min : arrondiPrix(echelle.versPrix(posBas / 1e3)),
        valeurHaut: posHaut === 1e3 ? etendue.max : arrondiPrix(echelle.versPrix(posHaut / 1e3))
      };
    }
    function peindre() {
      const c = lecture();
      elements.bas.style.zIndex = c.posBas > 900 ? 3 : "";
      elements.remplissage.style.left = `${c.posBas / 10}%`;
      elements.remplissage.style.width = `${(c.posHaut - c.posBas) / 10}%`;
      if (document.activeElement !== elements.champBas) {
        elements.champBas.value = Math.floor(c.valeurBas);
      }
      if (document.activeElement !== elements.champHaut) {
        elements.champHaut.value = Math.ceil(c.valeurHaut);
      }
      barres.forEach((barre, i) => {
        const milieu = (i + 0.5) / NOMBRE_DE_BARRES * 1e3;
        barre.classList.toggle(
          "wb-pslider-bar--in",
          milieu >= c.posBas && milieu <= c.posHaut
        );
      });
      return {
        de: c.posBas === 0 ? null : Math.max(etendue.min, c.valeurBas),
        a: c.posHaut === 1e3 ? null : Math.min(etendue.max, c.valeurHaut)
      };
    }
    elements.bas.addEventListener("input", () => {
      manipulation = true;
      peindre();
    });
    elements.haut.addEventListener("input", () => {
      manipulation = true;
      peindre();
    });
    const relacher = () => {
      const bornes = peindre();
      manipulation = false;
      surChangement(bornes);
    };
    elements.bas.addEventListener("change", relacher);
    elements.haut.addEventListener("change", relacher);
    const saisie = () => {
      const lu = (champ, defaut) => {
        if (champ.value === "") return defaut;
        const v = Number(champ.value);
        return Number.isFinite(v) ? v : defaut;
      };
      let de = Math.min(Math.max(lu(elements.champBas, etendue.min), etendue.min), etendue.max);
      let a = Math.min(Math.max(lu(elements.champHaut, etendue.max), etendue.min), etendue.max);
      if (de > a) [de, a] = [a, de];
      surChangement({
        de: de <= etendue.min ? null : de,
        a: a >= etendue.max ? null : a
      });
    };
    elements.champBas.addEventListener("change", saisie);
    elements.champHaut.addEventListener("change", saisie);
    return {
      /** Intervalles à faire compter par le moteur pour l'histogramme. */
      segments() {
        return intervallesSegments(echelle, NOMBRE_DE_BARRES);
      },
      /**
       * Le prix moyen de la sélection, sous l'histogramme et le curseur.
       *
       * `null` MASQUE la ligne, il ne la vide pas : un `<p>` vide garderait sa
       * gouttière et ferait sauter le bloc d'un cycle à l'autre. Et le cycle
       * appelle cette méthode à chaque passe structurelle, `null` compris — sans
       * quoi la moyenne d'une sélection précédente resterait sous une autre.
       */
      definirMoyenne(moyenne) {
        const ligne = ligneMoyenne(moyenne);
        elements.moyenne.hidden = ligne === null;
        elements.moyenne.textContent = ligne || "";
      },
      /** L'étendue du rail a changé (nouvelle requête, autres filtres). */
      definirEtendue(min, max) {
        etendue = { min, max };
        echelle = echellePrix(min, max);
      },
      /** Le curseur est-il utilisable ? Un seul prix ne se filtre pas. */
      exploitable() {
        return etendue.max > etendue.min;
      },
      /**
       * Place les poignées selon la sélection. Ignoré pendant une manipulation :
       * la réponse du moteur ne doit pas reprendre la main sur les doigts.
       */
      positionner(selection) {
        if (manipulation) return;
        const de = Number.isFinite(selection && selection.de) ? Math.max(selection.de, etendue.min) : etendue.min;
        const a = Number.isFinite(selection && selection.a) ? Math.min(selection.a, etendue.max) : etendue.max;
        elements.bas.value = Math.round(echelle.versPosition(de) * 1e3);
        elements.haut.value = Math.round(echelle.versPosition(a) * 1e3);
        peindre();
        if (document.activeElement !== elements.champBas) {
          elements.champBas.value = Math.floor(de);
        }
        if (document.activeElement !== elements.champHaut) {
          elements.champHaut.value = Math.ceil(a);
        }
      },
      /**
       * Dessine l'histogramme. Échelle en racine carrée : la distribution est très
       * asymétrique — plus de la moitié du catalogue sous 50 € — et en linéaire
       * toute la queue haute collerait au minimum sans plus rien montrer.
       */
      dessinerHistogramme(compteurs) {
        const valeurs = compteurs || [];
        const maximum = valeurs.length ? Math.max(...valeurs) : 0;
        elements.histogramme.hidden = !(maximum > 0);
        barres.forEach((barre, i) => {
          barre.style.height = maximum > 0 ? `${Math.max(3, Math.round(Math.sqrt((valeurs[i] || 0) / maximum) * 100))}%` : "3%";
          const compte = Math.round(valeurs[i] || 0);
          barre.title = `${compte} produit${compte > 1 ? "s" : ""}`;
        });
      }
    };
  }

  // frontend/js/curseur-nombre.js
  var POSITIONS = 1e3;
  var PAS_PAR_DEFAUT = 1;
  function bornesArrondies(bas, haut, pas = PAS_PAR_DEFAUT) {
    const min = Math.floor((Number(bas) || 0) / pas) * pas;
    let max = Math.ceil((Number(haut) || 0) / pas) * pas;
    if (max <= min) max = min + pas;
    return { min, max };
  }
  function positionVersValeur(position, { min, max }, pas = PAS_PAR_DEFAUT) {
    const borne = Math.min(POSITIONS, Math.max(0, Number(position) || 0));
    const brute = min + (max - min) * (borne / POSITIONS);
    return Math.round(brute / pas) * pas;
  }
  function valeurVersPosition(valeur, { min, max }) {
    if (max === min) return 0;
    const part = ((Number(valeur) || 0) - min) / (max - min);
    return Math.round(Math.min(1, Math.max(0, part)) * POSITIONS);
  }
  function formaterMesure(valeur, unite = "") {
    const nombre2 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(Number(valeur) || 0)).replace(/ | /g, "");
    return unite ? `${nombre2} ${unite}` : nombre2;
  }
  function gabarit2(champ) {
    const [bas, haut] = [t("mesure_min"), t("mesure_max")];
    return `
  <div class="wb-pslider-track">
    <div class="wb-pslider-rail">&#8203;</div>
    <div class="wb-pslider-fill">&#8203;</div>
    <input type="range" min="0" max="${POSITIONS}" step="1" value="0"
           class="wb-pslider-input" data-borne="bas" data-curseur-champ="${champ}"
           aria-label="${bas}">
    <input type="range" min="0" max="${POSITIONS}" step="1" value="${POSITIONS}"
           class="wb-pslider-input" data-borne="haut" data-curseur-champ="${champ}"
           aria-label="${haut}">
  </div>
  <div class="wb-pslider-scale">
    <output class="wb-pslider-scale-end" data-sortie="bas"></output>
    <output class="wb-pslider-scale-end" data-sortie="haut"></output>
  </div>`;
  }
  var RAYON_POIGNEE = 9;
  function positionCss(part) {
    const p = Math.min(1, Math.max(0, Number(part) || 0));
    const decalage = RAYON_POIGNEE - 2 * RAYON_POIGNEE * p;
    const signe = decalage < 0 ? "-" : "+";
    return `calc(${(p * 100).toFixed(4)}% ${signe} ${Math.abs(decalage).toFixed(4)}px)`;
  }
  function creerCurseurNombre(noeud, {
    champ,
    surChangement,
    pas = PAS_PAR_DEFAUT
  } = {}) {
    if (noeud.classList) noeud.classList.add("wb-pslider", "wb-pslider--mesure");
    noeud.innerHTML = gabarit2(champ);
    const bas = noeud.querySelector('[data-borne="bas"]');
    const haut = noeud.querySelector('[data-borne="haut"]');
    const sortieBas = noeud.querySelector('[data-sortie="bas"]');
    const sortieHaut = noeud.querySelector('[data-sortie="haut"]');
    const remplissage = noeud.querySelector(".wb-pslider-fill");
    bas.value = "0";
    haut.value = String(POSITIONS);
    let etendue = { min: 0, max: 1 };
    let unite = "";
    const valeurs = () => {
      const positions = [Number(bas.value) || 0, Number(haut.value) || 0].sort((a, b) => a - b);
      return {
        de: positionVersValeur(positions[0], etendue, pas),
        a: positionVersValeur(positions[1], etendue, pas)
      };
    };
    function peindre() {
      const { de, a } = valeurs();
      if (sortieBas) sortieBas.textContent = formaterMesure(de, unite);
      if (sortieHaut) sortieHaut.textContent = formaterMesure(a, unite);
      if (remplissage && remplissage.style) {
        const gauche = valeurVersPosition(de, etendue) / POSITIONS;
        const droite = valeurVersPosition(a, etendue) / POSITIONS;
        remplissage.style.left = positionCss(gauche);
        remplissage.style.right = positionCss(1 - droite);
      }
    }
    noeud.addEventListener("change", () => {
      peindre();
      const { de, a } = valeurs();
      const toutePlage = de <= etendue.min && a >= etendue.max;
      if (typeof surChangement === "function") {
        surChangement(toutePlage ? null : { de, a });
      }
    });
    return {
      /** Rail du contexte courant : les bornes viennent des `stats` du moteur. */
      definirEtendue(bas_, haut_, uniteMesure = "") {
        etendue = bornesArrondies(bas_, haut_, pas);
        unite = uniteMesure || "";
        peindre();
      },
      /** Replace les poignées, sans interaction. */
      positionner(plage) {
        const { min, max } = etendue;
        const de = plage && Number.isFinite(plage.de) ? plage.de : min;
        const a = plage && Number.isFinite(plage.a) ? plage.a : max;
        bas.value = String(valeurVersPosition(de, etendue));
        haut.value = String(valeurVersPosition(a, etendue));
        peindre();
      },
      valeurs
    };
  }

  // frontend/js/perimetre.js
  var IDENTIFIANT = /^\d+$/;
  function lirePerimetre(hote) {
    const brut = String(hote && hote.dataset && hote.dataset.wbCollectionId || "").trim();
    return { collectionId: IDENTIFIANT.test(brut) ? brut : "" };
  }
  function filtrePerimetre(perimetre) {
    const id = perimetre && perimetre.collectionId || "";
    return id ? `collection_ids:=\`${id}\`` : "";
  }

  // frontend/js/decouverte.js
  var MAX_FACETTES = 80;
  function champAbsent(message) {
    const trouve = /facet field named [`'"]?([A-Za-z0-9_.]+)[`'"]?/.exec(
      String(message || "")
    );
    return trouve ? trouve[1] : null;
  }
  function champsNonVides(resultat, candidats, plafond = MAX_FACETTES) {
    const pleins = new Set(
      ((resultat || {}).facet_counts || []).filter((facette) => ((facette || {}).counts || []).length > 0).map((facette) => facette.field_name)
    );
    return candidats.filter((champ) => pleins.has(champ)).slice(0, plafond);
  }
  function ecarterCreuses(candidats, table, collectionId) {
    const champs = (table || {}).champs;
    const indices = ((table || {}).collections || {})[collectionId];
    if (!collectionId || !Array.isArray(champs) || !Array.isArray(indices)) {
      return candidats;
    }
    const creux = new Set(indices.map((i) => champs[i]).filter(Boolean));
    return creux.size ? candidats.filter((champ) => !creux.has(champ)) : candidats;
  }
  function planDeCycle({
    candidats,
    parite,
    utiles,
    retenues,
    structurel,
    suite,
    decouvrir,
    jeuImpose = []
  }) {
    if (suite) return { facettes: [], decouverte: [] };
    if (jeuImpose && jeuImpose.length) {
      return { facettes: [...jeuImpose], decouverte: [] };
    }
    const decouverte = decouvrir && utiles === void 0 && candidats.length ? [...candidats] : [];
    const metier = utiles || retenues || [];
    const facettes = structurel ? [.../* @__PURE__ */ new Set([...parite, ...metier])] : retenues || [...parite, ...metier];
    return { facettes, decouverte };
  }
  function champsFacettables(schema, candidats) {
    const facettables = new Set(
      ((schema || {}).fields || []).filter((champ) => champ && champ.facet && !MOTIF2.test(champ.name)).map((champ) => champ.name)
    );
    return candidats.filter((champ) => facettables.has(champ));
  }
  var MOTIF2 = /[.*+?^${}()|[\]\\]/;

  // frontend/js/tiroir-facettes.js
  var CLASSE_OUVERT = "wb-facets-open";
  var POINT_DE_RUPTURE = 700;
  var SELECTEUR_BASCULE = "[data-wb-toggle-facets]";
  var ID_TIROIR = "wb-facets-tiroir";
  function prochainEtat(ouvert, evenement) {
    const type = (evenement || {}).type;
    if (type === "bascule") return { ouvert: !ouvert, consomme: true };
    if (type === "echappe") {
      return ouvert ? { ouvert: false, consomme: true } : { ouvert: false, consomme: false };
    }
    if (type === "largeur") {
      const largeur = (evenement || {}).largeur;
      const bureau = Number.isFinite(largeur) && largeur > POINT_DE_RUPTURE;
      return { ouvert: bureau ? false : ouvert, consomme: false };
    }
    return { ouvert, consomme: false };
  }
  function cibleDuPiege(focusables, actuel, arriere) {
    const liste2 = [...focusables || []];
    if (!liste2.length) return null;
    const premier = liste2[0];
    const dernier = liste2[liste2.length - 1];
    const position = liste2.indexOf(actuel);
    if (position === -1) return arriere ? dernier : premier;
    if (arriere && actuel === premier) return dernier;
    if (!arriere && actuel === dernier) return premier;
    return null;
  }
  function attributsBascule(ouvert) {
    return { "aria-expanded": ouvert ? "true" : "false", "aria-controls": ID_TIROIR };
  }
  function brancherTiroirFacettes(racine = document) {
    const corps = racine.body;
    if (!corps) return;
    let ouvert = corps.classList.contains(CLASSE_OUVERT);
    const boutonOuverture = () => racine.querySelector(`.wb-overlay-mobile-filter${SELECTEUR_BASCULE}`);
    const focusables = () => {
      const tiroir = racine.getElementById(ID_TIROIR);
      if (!tiroir) return [];
      return [...tiroir.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter((el) => el.offsetParent !== null);
    };
    const appliquer = (suite) => {
      ouvert = suite.ouvert;
      corps.classList.toggle(CLASSE_OUVERT, ouvert);
      const attributs = attributsBascule(ouvert);
      racine.querySelectorAll(`.wb-overlay-mobile-filter${SELECTEUR_BASCULE}`).forEach((bouton) => {
        Object.entries(attributs).forEach(([cle, valeur]) => bouton.setAttribute(cle, valeur));
      });
    };
    racine.addEventListener("click", (e) => {
      var _a;
      if (!e.target.closest(SELECTEUR_BASCULE)) return;
      appliquer(prochainEtat(ouvert, { type: "bascule" }));
      (_a = ouvert ? focusables()[0] : boutonOuverture()) == null ? void 0 : _a.focus();
    });
    racine.addEventListener("keydown", (e) => {
      var _a;
      if (e.key === "Tab" && ouvert) {
        const cible = cibleDuPiege(focusables(), racine.activeElement, e.shiftKey);
        if (cible) {
          e.preventDefault();
          cible.focus();
        }
        return;
      }
      if (e.key !== "Escape") return;
      const suite = prochainEtat(ouvert, { type: "echappe" });
      if (suite.consomme) {
        e.stopPropagation();
        appliquer(suite);
        (_a = boutonOuverture()) == null ? void 0 : _a.focus();
        return;
      }
      appliquer(suite);
    });
    const fenetre = racine.defaultView;
    const largeur = () => fenetre ? fenetre.innerWidth : void 0;
    if (fenetre) {
      fenetre.addEventListener("resize", () => appliquer(
        prochainEtat(ouvert, { type: "largeur", largeur: largeur() })
      ));
    }
    appliquer(prochainEtat(ouvert, { type: "largeur", largeur: largeur() }));
  }

  // frontend/js/barre-appliquer.js
  var CLASSE_BARRE = "wb-barre-appliquer";
  var CLASSE_BOUTON = "wb-appliquer";
  function libelleAppliquer(total) {
    const n = Number(total);
    if (!Number.isFinite(n) || n <= 0) return t("fermer_filtres");
    if (n === 1) return t("voir_produit");
    return t("voir_produits", { n: formaterEntier(n) });
  }
  function barreHtml(attribut, libelle) {
    return `<div class="${CLASSE_BARRE}"><button type="button" class="btn btn--primary ${CLASSE_BOUTON}" ${attribut}>${esc(libelle)}</button></div>`;
  }
  function poserBarre(colonne, attribut) {
    if (!colonne || colonne.querySelector(`.${CLASSE_BARRE}`)) return;
    colonne.insertAdjacentHTML("beforeend", barreHtml(attribut, libelleAppliquer(0)));
  }
  function majBarre(colonne, total) {
    const bouton = colonne && colonne.querySelector(`.${CLASSE_BOUTON}`);
    if (!bouton) return;
    bouton.textContent = libelleAppliquer(total);
  }

  // frontend/js/groupes-facettes.js
  function grouperFacettes(champs, { libelles = {}, styles = {} } = {}) {
    const ordre = [];
    const membres = {};
    const groupeDe = {};
    const titres = {};
    const parLibelle = /* @__PURE__ */ new Map();
    const appariement = (champ, libelle) => `${(styles || {})[champ] === "curseur" ? "curseur" : "valeurs"}\0${normaliserLibelle(libelle)}`;
    for (const champ of [...new Set(champs || [])]) {
      const libelle = String((libelles || {})[champ] || "").trim();
      const cle = libelle ? appariement(champ, libelle) : "";
      const existant = cle ? parLibelle.get(cle) : void 0;
      if (existant) {
        membres[existant] = [...membres[existant], champ];
        groupeDe[champ] = existant;
        titres[existant] = titreRetenu(titres[existant], libelle);
        continue;
      }
      ordre.push(champ);
      membres[champ] = [champ];
      groupeDe[champ] = champ;
      if (libelle) titres[champ] = libelle;
      if (cle) parLibelle.set(cle, champ);
    }
    return { ordre, membres, groupeDe, titres };
  }
  function normaliserLibelle(libelle) {
    return String(libelle || "").trim().toLocaleLowerCase("fr").replace(/[sx]$/, "");
  }
  function titreRetenu(actuel, candidat) {
    if (!actuel) return candidat;
    if (!candidat) return actuel;
    return candidat.length < actuel.length ? candidat : actuel;
  }
  function styleDuGroupe(membres, styles) {
    for (const champ of membres || []) {
      const style = (styles || {})[champ];
      if (style) return style;
    }
    return "";
  }
  function fusionnerValeurs(listes) {
    const fondu = /* @__PURE__ */ new Map();
    for (const { valeurs } of listes || []) {
      for (const v of valeurs || []) {
        const cle = String(v.value);
        const vue = fondu.get(cle);
        if (vue) vue.count += Number(v.count) || 0;
        else fondu.set(cle, { value: v.value, count: Number(v.count) || 0 });
      }
    }
    return [...fondu.values()].map((v, recu) => ({ v, recu })).sort((a, b) => b.v.count - a.v.count || a.recu - b.recu).map((x) => x.v);
  }

  // frontend/js/analytics.js
  function nombreFiltresActifs(etat = {}) {
    const valeurs = Object.values(etat.selection || {}).reduce((total, liste2) => total + (Array.isArray(liste2) ? liste2.length : 0), 0);
    return valeurs + (etat.prix ? 1 : 0) + (etat.note != null ? 1 : 0) + Object.keys(etat.mesures || {}).length;
  }
  function combinaisonFiltres(etat) {
    const combinaison = {};
    const selection = Object.fromEntries(Object.entries(etat.selection || {}).filter(([, valeurs]) => Array.isArray(valeurs) && valeurs.length).sort(([a], [b]) => a.localeCompare(b)));
    if (Object.keys(selection).length) combinaison.selection = selection;
    if (etat.prix) combinaison.prix = etat.prix;
    if (etat.note != null) combinaison.note = etat.note;
    if (Object.keys(etat.mesures || {}).length) combinaison.mesures = etat.mesures;
    return JSON.stringify(combinaison).slice(0, 500);
  }
  function contexteAnalytics(etat, total, extra = {}) {
    return {
      provider: "typesense",
      search_term: String((etat || {}).q || ""),
      results_count: Math.max(0, Number(total) || 0),
      active_filters_count: nombreFiltresActifs(etat),
      filter_combination: combinaisonFiltres(etat || {}),
      ...extra
    };
  }
  function pousserDataLayer(event, custom, cible = globalThis) {
    if (!cible) return;
    if (!Array.isArray(cible.dataLayer)) cible.dataLayer = [];
    cible.dataLayer.push({ event, custom });
  }

  // frontend/js/frontieres.js
  var CHIFFRES = "0123456789";
  var LETTRES = "abcdefghijklmnopqrstuvwxyzàâäçéèêëîïôöùûüÿñæœ";
  function estChiffre(caractere) {
    return CHIFFRES.includes(caractere);
  }
  function estLettre(caractere) {
    return caractere !== void 0 && LETTRES.includes(caractere.toLowerCase());
  }
  function estFrontiere(precedent, courant) {
    return estChiffre(precedent) && estLettre(courant) || estLettre(precedent) && estChiffre(courant);
  }
  function separerFrontieres(requete) {
    if (typeof requete !== "string" || !requete) return "";
    let sortie = "";
    for (let i = 0; i < requete.length; i += 1) {
      if (i > 0 && estFrontiere(requete[i - 1], requete[i])) sortie += " ";
      sortie += requete[i];
    }
    return sortie === requete ? "" : sortie;
  }

  // frontend/js/temoin.js
  var DELAI_TEMOIN_MS = 3e3;
  var DUREE_MIN_MS = 1e3;
  function normaliser(q) {
    return String(q || "").trim().replace(/\s+/g, " ").toLowerCase();
  }
  function estSuite(a, b) {
    const x = normaliser(a);
    const y = normaliser(b);
    if (!x || !y) return false;
    return x.startsWith(y) || y.startsWith(x);
  }
  var SANS_OBJET = [
    "facet_by",
    "max_facet_values",
    "per_page",
    "page",
    "include_fields",
    "exclude_fields",
    "sort_by"
  ];
  function rechercheTemoin({ collection, q, parametres = {}, perimetre = "" }) {
    const base = { ...parametres };
    for (const cle of SANS_OBJET) delete base[cle];
    return {
      ...base,
      collection,
      q,
      per_page: 0,
      ...perimetre ? { filter_by: perimetre } : {}
    };
  }
  var CLE_VISITEUR = "wb_recherche_visiteur";
  var visiteurDeLaPage = "";
  var tirage = () => Math.random().toString(36).slice(2, 10).padEnd(8, "0");
  function idVisiteur(stockage = globalThis.sessionStorage) {
    try {
      const connu = stockage && stockage.getItem(CLE_VISITEUR);
      if (connu) return connu;
      const neuf = `v${tirage()}${tirage()}`;
      if (stockage) stockage.setItem(CLE_VISITEUR, neuf);
      return neuf;
    } catch (e) {
      if (!visiteurDeLaPage) visiteurDeLaPage = `v${tirage()}${tirage()}`;
      return visiteurDeLaPage;
    }
  }
  function creerTemoin({
    envoyer,
    delai = DELAI_TEMOIN_MS,
    dureeMin = DUREE_MIN_MS,
    maintenant = () => Date.now(),
    planifier = (f, ms) => setTimeout(f, ms),
    annuler = (id) => clearTimeout(id)
  }) {
    let attente = null;
    let dernier = "";
    function abandonner() {
      if (attente) annuler(attente.minuteur);
      attente = null;
    }
    function emettre() {
      const { q, recherche } = attente;
      abandonner();
      const cle = normaliser(q);
      if (cle === dernier) return false;
      dernier = cle;
      try {
        const envoi = envoyer(recherche);
        if (envoi && typeof envoi.catch === "function") envoi.catch(() => {
        });
      } catch (e) {
      }
      return true;
    }
    function partir() {
      if (!attente) return false;
      if (maintenant() - attente.depuis >= dureeMin) return emettre();
      abandonner();
      return false;
    }
    return {
      resultat(q, recherche) {
        if (!normaliser(q)) return;
        if (attente && normaliser(attente.q) === normaliser(q)) {
          attente.recherche = recherche;
          return;
        }
        if (attente && !estSuite(attente.q, q)) partir();
        else abandonner();
        const nouvelle = { q, recherche, depuis: maintenant(), minuteur: null };
        nouvelle.minuteur = planifier(() => {
          if (attente === nouvelle) emettre();
        }, delai);
        attente = nouvelle;
      },
      saisie(q) {
        if (!attente || normaliser(q) === normaliser(attente.q)) return;
        if (estSuite(attente.q, q)) abandonner();
        else partir();
      },
      quitter() {
        return partir();
      },
      /* Un geste dans les résultats (carte, filtre) confirme la requête
         affichée : elle part tout de suite, sans délai minimal — et AVANT le
         `select_item` qui suit le clic, sinon l'entonnoir GA4 s'inverse. */
      confirmer() {
        return attente ? emettre() : false;
      }
    };
  }

  // frontend/js/recherche.js
  var PAR_PAGE_DEFAUT = 24;
  var PRECHARGE_PX_DEFAUT = 600;
  var DELAI_FRAPPE_MS = 130;
  var DELAI_DECOUVERTE_MS = 700;
  function cederAuNavigateur(fenetre = globalThis) {
    const planificateur = fenetre && fenetre.scheduler;
    if (planificateur && typeof planificateur.yield === "function") {
      return planificateur.yield();
    }
    return new Promise((suite) => setTimeout(suite, 0));
  }
  var MAX_ELAGAGES = 30;
  var SELECTEURS_DEFAUT = Object.freeze({
    colonne: ".wb-overlay-facets",
    defilant: ".wb-overlay-results",
    grille: ".wb-grid",
    reset: "[data-wb-reset]",
    /* L'attribut — sans crochets — que porte tout ce qui ouvre et referme le
       tiroir mobile de CET hôte. Il est ici parce que les deux hôtes n'ont pas le
       même tiroir et COHABITENT sur une PLP : `barre-appliquer.js` le pose sur son
       bouton, et c'est `brancherTiroirFacettes` qui l'écoute. Écrit en dur, il
       fermerait l'autre tiroir, ou aucun, sans la moindre erreur. */
    bascule: "data-wb-toggle-facets"
  });
  var FACETTES_PARITE = [
    "chemin",
    "disponibilites",
    "product_type",
    "vendor"
  ];
  var ORDRE_PARITE = ["product_type", "vendor", "price", "review_rating", "disponibilites"];
  var LIBELLES_TRIS = Object.freeze({
    price_asc: t("tri.price_asc"),
    price_desc: t("tri.price_desc"),
    published_at_desc: t("tri.published_at_desc"),
    review_rating_desc: t("tri.review_rating_desc"),
    recently_ordered_count_desc: t("tri.recently_ordered_count_desc")
  });
  function traduireTri(tri, champPrix = "price") {
    const chaine = String(tri || "");
    if (!chaine || champPrix === "price") return chaine;
    return chaine.split(",").map((critere) => critere.replace(/^\s*price(?=[:(])/, champPrix)).join(",");
  }
  var JETON_TERME = "%TERME%";
  function triMarqueExacte(modele, terme) {
    if (!modele || !String(modele).includes(JETON_TERME)) return "";
    const propre = String(terme || "").replace(/[^\p{L}\p{N}\s]/gu, "").replace(/\s+/g, " ").trim();
    return propre ? String(modele).replace(JETON_TERME, propre) : "";
  }
  function optionsDeTri(tris, libelles = null) {
    const libelle = (cle) => (libelles ? libelles[cle] : LIBELLES_TRIS[cle] ? t(`tri.${cle}`) : void 0) || cle;
    return [{ valeur: "", libelle: t("tri.pertinence") }].concat(Object.entries(tris || {}).map(([cle, tri]) => ({ valeur: tri, libelle: libelle(cle) })));
  }
  function lireFiltres(filtres, langue2) {
    const liste2 = Array.isArray(filtres) ? filtres : [];
    const code = langue2 || langue();
    return {
      cles: liste2.map((f) => f.cle),
      libelles: Object.fromEntries(liste2.map((f) => [f.cle, libelleFiltre(f, code)]))
    };
  }
  function lireReglages(filtres) {
    const liste2 = Array.isArray(filtres) ? filtres : [];
    const tris = {};
    const ordres = {};
    const styles = {};
    const numeriques = {};
    for (const f of liste2) {
      if (f && f.tri) tris[f.cle] = f.tri;
      if (f && Array.isArray(f.ordre) && f.ordre.length) ordres[f.cle] = f.ordre;
      if (f && f.style) styles[f.cle] = f.style;
      if (f && f.numerique) numeriques[f.cle] = true;
    }
    return { tris, ordres, styles, numeriques };
  }
  var SEUIL_RECHERCHE_FACETTE = 15;
  function estRecherchable({
    champ,
    nombreValeurs = 0,
    terme = "",
    pastilles = false,
    vignettes = false,
    impose = void 0
  } = {}) {
    if (pastilles) return false;
    if (vignettes) return false;
    if (String(terme || "").trim()) return true;
    if (typeof impose === "boolean") return impose;
    if (champ === "vendor") return true;
    return nombreValeurs > SEUIL_RECHERCHE_FACETTE;
  }
  function verifierConfig({ moteur, collection, parametres }) {
    if (!moteur || typeof moteur.multi !== "function") {
      throw new Error("creerRecherche : `moteur` doit exposer multi()");
    }
    if (!collection) throw new Error("creerRecherche : `collection` manquante");
    if (!parametres || typeof parametres !== "object") {
      throw new Error("creerRecherche : `parametres` de parité manquants");
    }
  }
  function creerRecherche(config) {
    var _a;
    verifierConfig(config);
    const {
      moteur,
      collection,
      parametres,
      tris = null,
      filtres = [],
      /* `window.WB_TRI_MARQUE` : absent, le tri « pertinence » reste celui du
         preset (`triMarqueExacte`). */
      triMarque = "",
      facettesCreuses = void 0,
      racine = document,
      noeuds = {},
      selecteurs = {},
      baliseItem = "li",
      alma = false,
      surAlmaEligible = null,
      perimetre: perimetreInitial = "",
      etatInitial = {},
      parPage = PAR_PAGE_DEFAUT,
      prechargePx = PRECHARGE_PX_DEFAUT,
      chargementAuto = true,
      champPrix = "price",
      devise = "EUR",
      ordre = null,
      seuils = SEUILS,
      pliage = null,
      pastilles = [],
      jeuxParCollection = {},
      /* La surcharge de colonne de la collection COURANTE, déjà résolue par
         l'hôte (`surcharges.surchargeDe`) : `{champs, ordre, libelles, styles}`,
         tous facultatifs. Objet vide partout ailleurs — l'overlay de recherche
         n'a pas de collection, et lire la table avec un périmètre vide y
         appliquerait le réglage d'une autre page. */
      surcharge = {},
      /* Les réglages du FILTRE lui-même (`window.WB_FILTRES_REGLAGES`), GLOBAUX :
         tri des valeurs, box de recherche, repli par défaut, couleurs et formes.
         Demandés par le CDP le 23/09/2026. Une collection y déroge par
         `surcharge.reglages`, qui a le dernier mot — sinon l'exception serait
         saisie, enregistrée, publiée, et sans effet. */
      reglages: reglages2 = {},
      gabarit: gabarit3 = chargerGabarit,
      creerCurseur = creerCurseurPrix,
      /* ⛔ Injectable parce que le tiroir se branche sur le DOCUMENT, pas sur
         l'hôte : deux hôtes sur la même page le brancheraient deux fois, et les
         deux écouteurs de clic basculeraient `body.wb-facets-open` l'un après
         l'autre — un clic sur « Filtres » ne ferait alors plus rien. La PLP passe
         son propre tiroir. */
      tiroir = brancherTiroirFacettes,
      messagePanne = (erreur) => `<li class="rec-error">
      <strong>Le moteur ne répond pas.</strong><br>${esc(erreur.message)}</li>`,
      surRendu: surRenduHote = () => {
      },
      analytics = pousserDataLayer,
      fenetre = globalThis,
      ceder = () => cederAuNavigateur(fenetre),
      temoin: temoinInjecte = null,
      reglagesTemoin = {}
    } = config;
    const temoin = temoinInjecte || creerTemoin({
      ...reglagesTemoin,
      envoyer: ({ recherche, contexte, zero }) => {
        analytics("view_search_results", contexte);
        if (zero) analytics("search_no_results", { ...contexte, zero_context: "query" });
        if (typeof moteur.temoin === "function") {
          moteur.temoin(recherche, { visiteur: idVisiteur() });
        }
      }
    });
    const { cles: FILTRES_AKENEO, libelles: LIBELLES_AKENEO } = lireFiltres(filtres);
    const {
      tris: TRI_VALEURS,
      ordres: ORDRE_VALEURS,
      styles: RELEVE_STYLES,
      numeriques: NUMERIQUES
    } = lireReglages(filtres);
    const STYLE_VALEURS = stylesDeColonne(RELEVE_STYLES, surcharge.styles);
    const REGLAGES_GLOBAUX = (reglages2 || {}).champs || {};
    const REGLAGES_COLLECTION = surcharge.reglages || {};
    const reglageDe = (champ) => ({
      ...REGLAGES_GLOBAUX[champ] || {},
      ...REGLAGES_COLLECTION[champ] || {}
    });
    for (const champ of /* @__PURE__ */ new Set([
      ...Object.keys(REGLAGES_GLOBAUX),
      ...Object.keys(REGLAGES_COLLECTION)
    ])) {
      const regle = reglageDe(champ);
      if (regle.tri) TRI_VALEURS[champ] = regle.tri;
      if (Array.isArray(regle.ordre) && regle.ordre.length) {
        ORDRE_VALEURS[champ] = regle.ordre;
      }
    }
    oublierCouleursImposees();
    oublierFormesImposees();
    imposerCouleurs((reglages2 || {}).couleurs);
    imposerFormes((reglages2 || {}).formes);
    enregistrerTitres(LIBELLES_AKENEO);
    const CHAMPS_DES_JEUX = [...new Set(
      [
        ...Object.values(jeuxParCollection || {}).flat(),
        ...surcharge.champs || [],
        ...surcharge.ordre || []
      ].filter((c) => typeof c === "string" && c)
    )];
    const MASQUES = new Set((surcharge.masques || []).filter((c) => typeof c === "string" && c));
    const nonMasque = (champ) => !MASQUES.has(champ);
    const PARITE_SERVIE = FACETTES_PARITE.filter(nonMasque);
    const CANDIDATS_METIER = FILTRES_AKENEO.filter(nonMasque);
    const FACETTES_LISTE = [.../* @__PURE__ */ new Set(
      [...FACETTES_PARITE, ...FILTRES_AKENEO, ...CHAMPS_DES_JEUX]
    )].filter(nonMasque);
    const {
      ordre: GROUPES,
      membres: MEMBRES,
      groupeDe: GROUPE_DE,
      titres: TITRES_GROUPES
    } = grouperFacettes(
      FACETTES_LISTE,
      { libelles: LIBELLES_AKENEO, styles: STYLE_VALEURS }
    );
    enregistrerTitres(TITRES_GROUPES);
    imposerTitres(titresDansLaLangue(surcharge, langue()));
    const groupeDe = (champ) => GROUPE_DE[champ] || champ;
    const membresDe = (groupe) => MEMBRES[groupe] || [groupe];
    const EN_CURSEUR = GROUPES.filter(
      (groupe) => styleDuGroupe(membresDe(groupe), STYLE_VALEURS) === "curseur"
    );
    const parMembre = (parGroupe) => {
      const sortie = {};
      for (const [groupe, valeur] of Object.entries(parGroupe || {})) {
        for (const champ of membresDe(groupe)) sortie[champ] = valeur;
      }
      return sortie;
    };
    const jeuDuPerimetre = () => surcharge.champs && surcharge.champs.length ? [...surcharge.champs] : (jeuxParCollection || {})[String(etat.perimetre.collectionId || "")] || [];
    const jeuCourant = surcharge.champs && surcharge.champs.length ? [...surcharge.champs] : (jeuxParCollection || {})[String(perimetreInitial || "")] || [];
    const ordreSurcharge = surcharge.ordre || [];
    const ordreBase = ordreSurcharge.length ? [...ordreSurcharge, ...jeuCourant, ...ordre || ORDRE_PARITE] : jeuCourant.includes(CLE) ? [
      CLE,
      "price",
      ...jeuCourant.filter((c) => c !== CLE && c !== "price"),
      ...ordre || ORDRE_PARITE
    ] : [...ordre || ORDRE_PARITE, ...ORDRE_PARITE];
    const ORDRE = [...new Set([
      ...ordreBase,
      ...ORDRE_PARITE,
      ...FILTRES_AKENEO,
      ...CHAMPS_DES_JEUX
    ].filter(nonMasque).map(groupeDe))];
    const ouvertesTransverse = jeuCourant.includes(CLE) ? [CLE] : [];
    const OUVERTES = [...new Set(
      [...(pliage || {}).ouvertes || [], ...ouvertesTransverse].map(groupeDe)
    )];
    const optionsPliage = (champ) => pliage ? { replie: estRepliee(
      champ,
      etat.replies,
      OUVERTES,
      reglageDe(champ).replie
    ) } : null;
    const etat = {
      /* Non vide quand l'hôte restaure une recherche portée par l'URL — le
         « précédent » depuis une fiche produit, voir `memoire-recherche.js`. Elle
         doit être en état AVANT le premier cycle, sinon ce cycle part sur le
         catalogue entier. */
      q: String(etatInitial.q || "").trim(),
      /* Le terme réellement ENVOYÉ au moteur, quand il diffère de celui que le
         visiteur a tapé — voir le repli des frontières dans `chercher()`. Vide
         le reste du temps, et il fait alors office de drapeau : un repli par
         requête, jamais deux. */
      qMoteur: "",
      tri: String(etatInitial.tri || ""),
      // Copies : l'hôte garde son objet, et l'empreinte de l'histogramme se
      // compare par sérialisation d'un état qu'on ne partage avec personne.
      selection: { ...etatInitial.selection || {} },
      // { champ: [valeurs cochées] }
      prix: etatInitial.prix || null,
      // { de, a } ou null
      note: (_a = etatInitial.note) != null ? _a : null,
      // seuil d'étoiles ou null
      deplie: {},
      // { champ: nombre de paliers dépliés }
      replies: {},
      // { champ: bool } — bloc replié par le visiteur (PLP)
      termeFacette: {},
      // { champ: "fen" } — recherche dans les valeurs
      /* Plages numériques choisies, en unité canonique : `{champ: {de, a}}`.
         Portées par l'URL sous `?m=champ:min-max` (`U-02`). */
      mesures: { ...etatInitial.mesures || {} },
      /* Combien de produits par lot, et sur quelle page on entre.
       *
       * `parPage` est mutable : sur une PLP le visiteur choisit 40, 70 ou 100.
       * `pageDepart` porte le `?page=N` d'un lien — celui que la pagination servie
       * par Liquid offre au crawl. Il vaut 1 dès le premier changement de filtre :
       * cocher une marque doit rendre la première page du nouvel ensemble, pas sa
       * troisième, qui serait souvent vide. */
      parPage: Math.max(1, Number(parPage) || PAR_PAGE_DEFAUT),
      pageDepart: Math.max(1, Number(etatInitial.page) || 1),
      page: Math.max(1, Number(etatInitial.page) || 1),
      cumul: 0,
      // cartes actuellement dans la grille
      epuise: false,
      enCours: false,
      sequence: 0,
      // jeton d'annulation des réponses hors délai
      etendueConnue: null,
      // "min:max" du dernier histogramme peint
      /* Périmètre de page de catégorie. Fixé à la construction sur une PLP : le
         passer par `definirPerimetre` après coup lancerait un cycle sur le
         catalogue entier avant de le restreindre. */
      perimetre: { collectionId: String(perimetreInitial || "") },
      facettesRetenues: null,
      // facettes non vides du périmètre courant
      /* Filtres Akeneo non vides, par périmètre et requête. Établi par UNE recherche
         de découverte, puis réutilisé : la liste ne dépend pas de ce que
         l'utilisateur coche. */
      utilesParPerimetre: /* @__PURE__ */ new Map()
    };
    const termeMoteur = () => etat.qMoteur || etat.q || "*";
    const cleDecouverte = () => `${etat.perimetre.collectionId || ""}|${etat.qMoteur || etat.q || ""}`;
    let candidatsDecouverte = [];
    const candidatsDuPerimetre = () => ecarterCreuses(
      candidatsDecouverte,
      facettesCreuses,
      etat.perimetre.collectionId || ""
    );
    const SEL = { ...SELECTEURS_DEFAUT, ...selecteurs };
    const noeud = (id) => racine.getElementById(noeuds[id] || id);
    const boutonReset = () => noeud("wb-overlay-reset") || (SEL.reset ? racine.querySelector(SEL.reset) : null);
    const conteneurDefilant = () => SEL.defilant ? racine.querySelector(SEL.defilant) : null;
    const conteneurs = {};
    let curseur = null;
    const curseursNombre = /* @__PURE__ */ new Map();
    const unitesMesurees = {};
    function construireColonne() {
      const hote = noeud("wb-facets-dynamic");
      hote.innerHTML = ORDRE.map((champ) => `<div class="ais-DynamicWidgets-widget" data-facette="${champ}"></div>`).join("");
      for (const champ of ORDRE) {
        conteneurs[champ] = hote.querySelector(`[data-facette="${champ}"]`);
      }
      const bloc2 = racine.createElement("div");
      bloc2.className = "wb-facet-block";
      bloc2.innerHTML = interieurBloc("price", `<div data-curseur></div>`, optionsPliage("price"));
      conteneurs.price.appendChild(bloc2);
      curseur = creerCurseur(bloc2.querySelector("[data-curseur]"), {
        devise,
        surChangement: (bornes) => {
          etat.prix = bornes.de == null && bornes.a == null ? null : bornes;
          noterFiltre(etat.prix ? "filter_applied" : "filter_removed", "Prix", bornes);
          chercher({ structurel: false });
        }
      });
      for (const champ of EN_CURSEUR) {
        if (!conteneurs[champ]) continue;
        const blocNombre = racine.createElement("div");
        blocNombre.className = "wb-facet-block";
        blocNombre.innerHTML = interieurBloc(
          champ,
          `<div data-curseur></div>`,
          optionsPliage(champ)
        );
        conteneurs[champ].appendChild(blocNombre);
        curseursNombre.set(champ, {
          bloc: blocNombre,
          curseur: creerCurseurNombre(blocNombre.querySelector("[data-curseur]"), {
            champ,
            surChangement: (plage) => {
              const reste = { ...etat.mesures };
              if (plage) reste[champ] = plage;
              else delete reste[champ];
              etat.mesures = reste;
              noterFiltre(
                plage ? "filter_applied" : "filter_removed",
                TITRES_GROUPES[champ] || LIBELLES_AKENEO[champ] || champ,
                plage || ""
              );
              chercher({ structurel: false });
            }
          })
        });
      }
    }
    function parametresBase(page) {
      const tri = etat.tri ? traduireTri(etat.tri, champPrix) : triMarqueExacte(triMarque, termeMoteur());
      return {
        ...parametres,
        per_page: etat.parPage,
        page,
        ...tri ? { sort_by: tri } : {}
      };
    }
    function plafondsDeValeurs() {
      const plafonds = {};
      for (const [champ, paliers] of Object.entries(etat.deplie)) {
        const niveau = Number(paliers) || 0;
        if (niveau > 0) {
          plafonds[champ] = LIMITE_VISIBLE + (niveau + 1) * PALIER_VALEURS;
        }
      }
      return plafonds;
    }
    function decalageDepart() {
      return (etat.pageDepart - 1) * etat.parPage;
    }
    let cyclesStructurels = 0;
    let interactionsAnalytics = [];
    let requeteAnalytics = null;
    function noterFiltre(event, libelle, valeur) {
      interactionsAnalytics.push({ event, extra: {
        element_label: String(libelle || "filtre"),
        element_value: typeof valeur === "string" ? valeur : JSON.stringify(valeur)
      } });
    }
    function emettreAnalytics(resultat) {
      const total = Math.max(0, Number(resultat && resultat.found) || 0);
      const gestes = interactionsAnalytics;
      interactionsAnalytics = [];
      if (gestes.length) temoin.confirmer();
      for (const geste of gestes) {
        analytics(geste.event, contexteAnalytics(etat, total, geste.extra));
      }
      const estRequeteAttendue = requeteAnalytics !== null && requeteAnalytics === etat.q && !!etat.q;
      if (estRequeteAttendue) {
        temoin.resultat(etat.q, {
          contexte: contexteAnalytics(etat, total),
          zero: total === 0 && !gestes.length,
          recherche: rechercheTemoin({
            collection,
            q: termeMoteur(),
            parametres,
            perimetre: filtrePerimetre(etat.perimetre)
          })
        });
        requeteAnalytics = null;
      }
      if (total === 0 && gestes.length) {
        analytics("search_no_results", contexteAnalytics(etat, total, {
          zero_context: "filters"
        }));
      }
    }
    async function chercher({
      structurel = true,
      suite = false,
      decouvrir = true,
      facettesSeules = false
    } = {}) {
      if (!demarree) return;
      if (!suite && !facettesSeules) {
        if (structurel && cyclesStructurels > 0) etat.pageDepart = 1;
        if (structurel) cyclesStructurels += 1;
        etat.page = etat.pageDepart;
        etat.epuise = false;
      }
      const sequence = ++etat.sequence;
      etat.enCours = true;
      const plan = planDeCycle({
        candidats: candidatsDuPerimetre(),
        parite: PARITE_SERVIE,
        utiles: etat.utilesParPerimetre.get(cleDecouverte()),
        retenues: etat.facettesRetenues,
        structurel,
        suite,
        decouvrir,
        jeuImpose: jeuDuPerimetre()
      });
      const lot = lotDeRecherches({
        collection,
        champPrix,
        q: termeMoteur(),
        perimetre: filtrePerimetre(etat.perimetre),
        parametres: facettesSeules ? { ...parametresBase(etat.page), per_page: 0 } : parametresBase(etat.page),
        facettes: plan.facettes,
        decouverte: plan.decouverte,
        selection: etat.selection,
        prix: etat.prix,
        note: etat.note,
        /* Le visiteur cherche et déplie un BLOC ; le moteur, lui, entend parler
           de chaque champ. `parMembre` redistribue le terme et le plafond du
           groupe à tous ses membres, sans quoi un « Matériau » déplié ne
           rapporterait ses valeurs supplémentaires que du premier des trois. */
        rechercheFacette: parMembre(etat.termeFacette),
        maxValeursParChamp: parMembre(plafondsDeValeurs()),
        membresParGroupe: MEMBRES,
        groupeDe: GROUPE_DE,
        triParChamp: TRI_VALEURS,
        stylesParChamp: STYLE_VALEURS,
        numeriquesParChamp: NUMERIQUES,
        mesures: etat.mesures,
        notes: suite ? [] : seuils,
        bornes: !suite && structurel,
        segments: [],
        /* ⚡ La carte ne lit qu'une vingtaine de champs : ne demander qu'eux.
           `WB_SPECS` n'existe que sur les PLP (mode liste) ; l'overlay n'en
           demande donc aucun attribut Akeneo. */
        champsGrille: champsDeCarte({ specs: globalThis.WB_SPECS })
      });
      let resultats;
      try {
        resultats = await moteur.multi(lot.recherches, {
          critiques: [lot.index.hits],
          chaudes: positionsChaudes(lot.index, { champPrix })
        });
      } catch (erreur) {
        if (sequence === etat.sequence) {
          if (suite) {
            etat.epuise = true;
            noeud("wb-infinite-status").textContent = `Chargement interrompu : ${erreur.message}`;
          } else {
            afficherPanne(erreur);
          }
        }
        etat.enCours = false;
        return;
      }
      if (sequence !== etat.sequence) return;
      const principal = resultats[lot.index.hits];
      if (!suite && !facettesSeules && !etat.qMoteur && etat.q && Number(principal && principal.found) === 0) {
        const variante = separerFrontieres(etat.q);
        if (variante) {
          etat.qMoteur = variante;
          etat.enCours = false;
          chercher({ structurel, suite: false, decouvrir });
          return;
        }
      }
      if (!facettesSeules) rendreGrille(principal, suite);
      if (!suite) {
        if (!facettesSeules) {
          rendreEntete(principal);
          await ceder();
          if (sequence !== etat.sequence) return;
        }
        rendreFacettes(resultats, lot.index);
        majEtendue(resultats, lot.index);
        if (!facettesSeules) emettreAnalytics(principal);
      }
      etat.enCours = false;
      if (!facettesSeules) surRendu();
      if (lot.index.decouverte !== null) {
        const reponse = resultats[lot.index.decouverte];
        const utiles = reponse && reponse.error ? await elaguerPuisDecouvrir(reponse.error, sequence) : facettesDecouvertes(reponse);
        if (utiles === null) return;
        etat.utilesParPerimetre.set(cleDecouverte(), utiles);
        if (utiles.length) {
          etat.facettesRetenues = null;
          chercher({ structurel: false, facettesSeules: true });
        }
      }
    }
    async function elaguerPuisDecouvrir(premiereErreur, sequence) {
      let message = premiereErreur;
      for (let essai = 0; essai < MAX_ELAGAGES; essai += 1) {
        const absent = champAbsent(message);
        if (!absent || !candidatsDecouverte.includes(absent)) {
          console.error(`[facettes] découverte abandonnée : ${message}`);
          return [];
        }
        candidatsDecouverte = candidatsDecouverte.filter((c) => c !== absent);
        console.warn(`[facettes] ${absent} est exposé comme filtre mais absent de ${collection} : écarté pour cette page.`);
        const candidats = candidatsDuPerimetre();
        if (!candidats.length) return [];
        const lot = lotDeRecherches({
          collection,
          q: termeMoteur(),
          perimetre: filtrePerimetre(etat.perimetre),
          parametres: { ...parametresBase(1), per_page: 0 },
          facettes: [],
          decouverte: candidats,
          notes: [],
          segments: []
        });
        let resultats;
        try {
          resultats = await moteur.multi(lot.recherches, { critiques: [] });
        } catch (erreur) {
          console.error(`[facettes] découverte indisponible : ${erreur.message}`);
          return [];
        }
        if (sequence !== etat.sequence) return null;
        const reponse = resultats[lot.index.decouverte];
        if (!reponse || !reponse.error) return facettesDecouvertes(reponse);
        message = reponse.error;
      }
      console.error(`[facettes] découverte abandonnée après ${MAX_ELAGAGES} champs écartés — la colonne se limite aux facettes de parité.`);
      return [];
    }
    function viderFacettesMetier() {
      for (const champ of FILTRES_AKENEO) {
        if (conteneurs[champ]) conteneurs[champ].innerHTML = "";
      }
    }
    function facettesDecouvertes(resultat) {
      const candidats = candidatsDuPerimetre();
      const tous = champsNonVides(resultat, candidats, candidats.length);
      const retenus = champsNonVides(resultat, candidats, MAX_FACETTES);
      if (tous.length > retenus.length) {
        console.info(
          `[facettes] ${tous.length} filtres métier non vides dans ce périmètre, ${retenus.length} affichés (plafond).`
        );
      }
      return retenus;
    }
    function majEtendue(resultats, index) {
      if (!index.bornes) {
        curseur.positionner(etat.prix);
        return;
      }
      const min = premierPrix(resultats[index.bornes[0]], champPrix);
      const max = premierPrix(resultats[index.bornes[1]], champPrix);
      curseur.definirMoyenne(moyennePrix(resultats[index.statsPrix], champPrix));
      curseur.definirEtendue(min != null ? min : 0, max != null ? max : 0);
      conteneurs.price.querySelector(".wb-facet-block").hidden = !curseur.exploitable();
      curseur.positionner(etat.prix);
      if (curseur.exploitable()) peindreHistogramme(min, max);
    }
    async function peindreHistogramme(min, max) {
      const empreinte = `${min}:${max}:${JSON.stringify(etat.selection)}:${etat.q}:${etat.note}`;
      if (etat.etendueConnue === empreinte) return;
      const sequence = etat.sequence;
      const lot = lotDeRecherches({
        collection,
        champPrix,
        q: termeMoteur(),
        perimetre: filtrePerimetre(etat.perimetre),
        parametres: { ...parametres, per_page: 0, page: 1 },
        facettes: [],
        selection: etat.selection,
        note: etat.note,
        segments: curseur.segments(),
        notes: []
      });
      let resultats;
      try {
        resultats = await moteur.multi(lot.recherches);
      } catch (erreur) {
        console.warn("[recherche] histogramme des prix indisponible :", erreur.message);
        return;
      }
      if (sequence !== etat.sequence) return;
      etat.etendueConnue = empreinte;
      curseur.dessinerHistogramme(
        lot.index.segments.map((i) => (resultats[i] || {}).found || 0)
      );
    }
    function rendreEntete(resultat) {
      noeud("wb-stats").innerHTML = `<b>${formaterEntier(resultat.found)}</b> ${esc(t(resultat.found > 1 ? "resultats" : "resultat"))}`;
      majBarre(racine.querySelector(SEL.colonne), resultat.found);
    }
    function listeDeCartes() {
      const hote = noeud("wb-hits");
      const classe = SEL.grille.replace(/^\./, "");
      if (hote.classList.contains(classe)) return hote;
      let liste2 = hote.querySelector(SEL.grille);
      if (!liste2) {
        hote.classList.add("wb-hits-root");
        liste2 = racine.createElement("ol");
        liste2.className = `ais-Hits-list ${classe}`;
        hote.appendChild(liste2);
      }
      return liste2;
    }
    function rendreGrille(resultat, suite) {
      const cartes = resultat.hits || [];
      const html = cartes.map((h) => {
        var _a2, _b;
        return carte(
          h.document,
          (_b = (_a2 = h.highlight) == null ? void 0 : _a2.title) == null ? void 0 : _b.snippet,
          baliseItem,
          void 0,
          { alma }
        );
      }).join("");
      if (surAlmaEligible && cartes.some((h) => almaEligible(h.document))) {
        surAlmaEligible();
      }
      const grille = listeDeCartes();
      if (grille.dataset) {
        grille.dataset.searchProvider = "typesense";
        grille.dataset.searchTerm = etat.q;
      }
      if (suite) {
        grille.insertAdjacentHTML("beforeend", html);
      } else {
        grille.innerHTML = html || messageVide({
          q: etat.q,
          selection: etat.selection,
          prix: etat.prix,
          note: etat.note
        });
      }
      etat.cumul = cartes.length + (suite ? etat.cumul : 0);
      const plafond = Number(parametres.limit_hits) || Infinity;
      etat.epuise = decalageDepart() + etat.cumul >= Math.min(resultat.found, plafond) || suite && cartes.length === 0;
      const bouton = noeud("wb-load-more");
      if (bouton) {
        bouton.classList.toggle("wb-load-more--disabled", etat.epuise);
        bouton.hidden = etat.epuise;
        if ("disabled" in bouton) bouton.disabled = etat.epuise;
      }
      surRenduHote({
        suite,
        cartes: cartes.length,
        cumul: etat.cumul,
        total: Number(resultat.found) || 0,
        montres: decalageDepart() + etat.cumul,
        page: etat.page,
        pageSuivante: etat.epuise ? null : etat.page + 1,
        parPage: etat.parPage,
        epuise: etat.epuise
      });
    }
    function rendreCurseurNombre(groupe, lectures) {
      const fiche = curseursNombre.get(groupe);
      if (!fiche) return false;
      const stats = fondreStats((lectures || []).map(
        ({ champ, reponse }) => statsFacette(reponse, champMesure(champ, NUMERIQUES))
      ));
      if (!stats) {
        fiche.bloc.hidden = true;
        return false;
      }
      fiche.bloc.hidden = !(stats.max > stats.min) && !etat.mesures[groupe];
      const premiere = (lectures || []).map(({ champ, reponse }) => (valeursFacette(reponse, champ)[0] || {}).value).find((valeur) => valeur !== void 0 && valeur !== null);
      unitesMesurees[groupe] = uniteCanonique(uniteDepuisValeur(premiere));
      fiche.curseur.definirEtendue(stats.min, stats.max, unitesMesurees[groupe]);
      fiche.curseur.positionner(etat.mesures[groupe] || null);
      return true;
    }
    function rendreFacettes(resultats, index) {
      var _a2, _b;
      const aPeindre = [];
      const curseursRetenus = [];
      const arbresRetenus = [];
      for (const groupe of GROUPES) {
        const presents = membresDe(groupe).filter((champ) => index.facettes[champ] !== void 0);
        if (STYLE_VALEURS[groupe] === "arbre") {
          if (!conteneurs[groupe]) continue;
          const ouverte = (etat.selection[CLE] || [])[0] || "";
          const champNiveau = champAFacetter(ouverte);
          const valeurs2 = champNiveau && index.facettes[groupe] !== void 0 ? valeursFacette(resultats[index.facettes[groupe]], champNiveau) : [];
          const total = (_a2 = (resultats[index.hits] || {}).found) != null ? _a2 : null;
          const utile2 = blocArbreUtile({ ouverte, valeurs: valeurs2, total });
          conteneurs[groupe].innerHTML = utile2 ? listeArbre({
            ouverte,
            valeurs: valeurs2,
            deplie: Number(etat.deplie[groupe]) || 0,
            total,
            pliage: optionsPliage(groupe)
          }) : "";
          arbresRetenus.push(groupe);
          continue;
        }
        if (!presents.length) {
          const fiche = curseursNombre.get(groupe);
          if (fiche) fiche.bloc.hidden = true;
          continue;
        }
        if (curseursNombre.has(groupe)) {
          const lectures = presents.map(
            (champ) => ({ champ, reponse: resultats[index.facettes[champ]] })
          );
          if (rendreCurseurNombre(groupe, lectures)) {
            curseursRetenus.push(...presents);
          }
          continue;
        }
        const valeursFondues = fusionnerValeurs(presents.map((champ) => ({
          champ,
          valeurs: valeursFacette(resultats[index.facettes[champ]], champ)
        })));
        const reference = presents.length === 1 ? presents[0] : groupe;
        const valeurs = ordonnerValeurs(
          valeursFondues,
          ORDRE_VALEURS[reference],
          {
            alpha: TRI_VALEURS[reference] === "alpha",
            mesure: TRI_VALEURS[reference] === "mesure",
            langue: langue()
          }
        );
        const distinctes = presents.reduce(
          (total, champ) => total + nombreDeValeurs(resultats[index.facettes[champ]], champ),
          0
        );
        const cochees = [...new Set(
          membresDe(groupe).flatMap((champ) => etat.selection[champ] || [])
        )];
        const enRecherche = !!String(etat.termeFacette[groupe] || "").trim();
        const utile = valeurs.length > 0 || cochees.length > 0 || enRecherche;
        const metier = membresDe(groupe).some(
          (champ) => FILTRES_AKENEO.includes(champ) || CHAMPS_DES_JEUX.includes(champ)
        );
        if (metier && !utile) {
          conteneurs[groupe].innerHTML = "";
          continue;
        }
        aPeindre.push({
          groupe,
          reference,
          presents,
          valeurs,
          cochees,
          utile,
          distinctes
        });
      }
      for (const { groupe, reference, presents, valeurs, cochees, distinctes } of aPeindre) {
        const terme = etat.termeFacette[groupe] || "";
        const enPastilles = pastilles.includes(reference);
        const style = styleDuGroupe(presents, STYLE_VALEURS);
        const enVignettes = style === "vignettes";
        const enBooleen = style === "booleen";
        conteneurs[groupe].innerHTML = listeAcocher({
          champ: groupe,
          valeurs,
          cochees,
          deplie: Number(etat.deplie[groupe]) || 0,
          recherchable: estRecherchable({
            champ: groupe,
            nombreValeurs: distinctes,
            terme,
            pastilles: enPastilles,
            vignettes: enVignettes,
            impose: reglageDe(groupe).recherchable
          }),
          terme,
          pastilles: enPastilles,
          vignettes: enVignettes,
          booleen: enBooleen,
          pliage: optionsPliage(groupe),
          /* Le total de la GRILLE, pour qu'une valeur unique qui n'en couvre
             qu'une partie reste un choix (`facettes.offreUnChoix`, 05/10). */
          total: (_b = (resultats[index.hits] || {}).found) != null ? _b : null
        });
      }
      etat.facettesRetenues = [
        ...aPeindre.filter((f) => f.utile).flatMap((f) => f.presents),
        ...curseursRetenus,
        ...arbresRetenus
      ];
      const comptes = {};
      index.notes.forEach((position, i) => {
        comptes[seuils[i]] = (resultats[position] || {}).found || 0;
      });
      conteneurs.review_rating.innerHTML = facetteAvis({
        comptes,
        selection: etat.note,
        seuils,
        pliage: optionsPliage("review_rating")
      });
      const { jetonsHtml, effacerHtml } = jetons({
        selection: etat.selection,
        prix: etat.prix,
        note: etat.note,
        formaterPrix,
        mesures: etat.mesures,
        unites: unitesMesurees
      });
      noeud("wb-current-refinements").innerHTML = jetonsHtml;
      noeud("wb-clear-refinements").innerHTML = effacerHtml;
      restaurerFocusRecherche();
    }
    let focusFacette = null;
    function restaurerFocusRecherche() {
      if (!focusFacette) return;
      const champ = racine.querySelector(`[data-recherche-facette="${focusFacette}"]`);
      if (!champ) return;
      champ.focus();
      champ.setSelectionRange(champ.value.length, champ.value.length);
    }
    function afficherPanne(erreur) {
      listeDeCartes().innerHTML = messagePanne(erreur);
      noeud("wb-stats").textContent = "moteur hors ligne";
    }
    function cadreDeDefilement() {
      const defilant = conteneurDefilant();
      if (defilant) return defilant.getBoundingClientRect();
      const hauteur = (racine.documentElement || {}).clientHeight;
      return hauteur ? { top: 0, bottom: hauteur } : null;
    }
    function sentinelleProche() {
      const sentinelle = noeud("wb-infinite-sentinel");
      const cadre = cadreDeDefilement();
      if (!sentinelle || !cadre) return false;
      return sentinelle.getBoundingClientRect().top <= cadre.bottom + prechargePx;
    }
    function chargerSuite() {
      if (etat.enCours || etat.epuise) return;
      etat.page += 1;
      noeud("wb-infinite-status").textContent = "Chargement…";
      chercher({ structurel: false, suite: true });
    }
    function surRendu() {
      const etiquette = noeud("wb-infinite-status");
      if (etat.epuise) {
        etiquette.textContent = etat.cumul > etat.parPage ? t("tout_vu") : "";
        return;
      }
      etiquette.textContent = "";
      if (chargementAuto && sentinelleProche()) chargerSuite();
    }
    function installerScrollInfini() {
      if (!chargementAuto) return;
      const sentinelle = noeud("wb-infinite-sentinel");
      if (typeof IntersectionObserver !== "function") {
        racine.body.classList.add("wb-no-infinite");
        return;
      }
      new IntersectionObserver((entrees2) => {
        if (entrees2.some((e) => e.isIntersecting)) chargerSuite();
      }, { root: conteneurDefilant(), rootMargin: `0px 0px ${prechargePx}px 0px` }).observe(sentinelle);
    }
    function basculerValeur(champ, valeur) {
      if (champ === CLE) {
        const ouverte = (etat.selection[CLE] || [])[0] || "";
        const suivante2 = descendre(ouverte, valeur);
        const { [CLE]: _retire, ...reste } = etat.selection;
        etat.selection = suivante2 ? { ...reste, [CLE]: [suivante2] } : reste;
        return;
      }
      const groupe = groupeDe(champ);
      const cles = membresDe(groupe);
      const dejaCochee = cles.some((c) => (etat.selection[c] || []).includes(valeur));
      const suivante = {};
      for (const [cle, valeurs] of Object.entries(etat.selection)) {
        const restantes = cles.includes(cle) ? valeurs.filter((v) => v !== valeur) : valeurs;
        if (restantes.length) suivante[cle] = restantes;
      }
      if (!dejaCochee) {
        suivante[groupe] = [...suivante[groupe] || [], valeur];
      }
      etat.selection = suivante;
    }
    function effacerTout() {
      const nombreAvant = nombreFiltresActifs(etat);
      etat.selection = {};
      etat.prix = null;
      etat.note = null;
      etat.mesures = {};
      for (const { curseur: c } of curseursNombre.values()) c.positionner(null);
      etat.termeFacette = {};
      etat.deplie = {};
      if (nombreAvant) noterFiltre("filter_removed", "Tous les filtres", nombreAvant);
      chercher();
    }
    function basculerRepli(champ) {
      if (!pliage || !champ) return;
      const replie = !estRepliee(champ, etat.replies, OUVERTES);
      etat.replies = { ...etat.replies, [champ]: replie };
      const conteneur = conteneurs[champ];
      const blocDom = conteneur && typeof conteneur.querySelector === "function" ? conteneur.querySelector(".wb-facet-block") : null;
      appliquerRepli(blocDom, replie);
    }
    function refleterRequete() {
      const champ = noeud("wb-overlay-input");
      if (champ && String(champ.value).trim() !== etat.q) champ.value = etat.q;
      const reset = boutonReset();
      if (reset) reset.hidden = !etat.q;
    }
    function definirRequete(q, { immediat = false } = {}) {
      etat.q = String(q || "").trim();
      etat.qMoteur = "";
      requeteAnalytics = etat.q || null;
      temoin.saisie(etat.q);
      refleterRequete();
      if (immediat) {
        chercher();
        return;
      }
      programmerFrappe();
    }
    function definirEtat({
      q = "",
      selection = null,
      prix = null,
      note = null,
      tri = ""
    } = {}) {
      clearTimeout(minuteur);
      clearTimeout(minuteurDecouverte);
      etat.q = String(q || "").trim();
      etat.qMoteur = "";
      requeteAnalytics = etat.q || null;
      temoin.saisie(etat.q);
      etat.selection = { ...selection || {} };
      etat.prix = prix || null;
      etat.note = note != null ? note : null;
      etat.tri = String(tri || "");
      etat.termeFacette = {};
      etat.deplie = {};
      etat.etendueConnue = null;
      etat.pageDepart = 1;
      refleterRequete();
      chercher();
    }
    let minuteur;
    let minuteurDecouverte;
    function programmerFrappe() {
      clearTimeout(minuteur);
      clearTimeout(minuteurDecouverte);
      minuteur = setTimeout(() => chercher({ decouvrir: false }), DELAI_FRAPPE_MS);
      minuteurDecouverte = setTimeout(() => chercher(), DELAI_DECOUVERTE_MS);
    }
    function definirParPage(nombre2) {
      const valeur = Math.trunc(Number(nombre2));
      if (!Number.isInteger(valeur) || valeur <= 0 || valeur === etat.parPage) return;
      etat.parPage = valeur;
      etat.pageDepart = 1;
      chercher({ structurel: false });
    }
    function definirPerimetre(collectionId) {
      etat.perimetre = { collectionId: collectionId || "" };
      etat.etendueConnue = null;
      etat.facettesRetenues = null;
      viderFacettesMetier();
      chercher({ structurel: true });
    }
    function brancherInteractions() {
      const colonne = racine.querySelector(SEL.colonne);
      if (!colonne) {
        throw new Error(`creerRecherche : colonne de facettes ${SEL.colonne} absente de l'hôte — aucun filtre ne serait cliquable`);
      }
      tiroir(racine);
      poserBarre(colonne, SEL.bascule);
      colonne.addEventListener("change", (e) => {
        const cible = e.target;
        if (cible.classList.contains("wb-facet-cb")) {
          const groupe = groupeDe(cible.dataset.facette);
          basculerValeur(cible.dataset.facette, cible.dataset.valeur);
          noterFiltre(
            cible.checked ? "filter_applied" : "filter_removed",
            TITRES_GROUPES[groupe] || LIBELLES_AKENEO[groupe] || groupe,
            cible.dataset.valeur
          );
          chercher();
        }
      });
      colonne.addEventListener("click", (e) => {
        const basculer = e.target.closest("[data-basculer-facette]");
        if (basculer) {
          basculerRepli(basculer.dataset.basculerFacette);
          return;
        }
        const descendre_ = e.target.closest("[data-arbre-descendre]");
        if (descendre_) {
          basculerValeur(CLE, descendre_.dataset.arbreDescendre);
          noterFiltre(
            "filter_applied",
            TITRES_GROUPES[CLE] || CLE,
            descendre_.dataset.arbreDescendre
          );
          chercher();
          return;
        }
        const remonterCran = e.target.closest("[data-arbre-remonter]");
        if (remonterCran) {
          const niveau = Number(remonterCran.dataset.arbreRemonter);
          const ouverte = (etat.selection[CLE] || [])[0] || "";
          const cible = remonter(ouverte, niveau);
          const { [CLE]: _retire, ...reste } = etat.selection;
          etat.selection = cible ? { ...reste, [CLE]: [cible] } : reste;
          noterFiltre(
            cible ? "filter_applied" : "filter_removed",
            TITRES_GROUPES[CLE] || CLE,
            cible
          );
          chercher();
          return;
        }
        const deplier = e.target.closest("[data-deplier]");
        if (deplier) {
          const champ2 = deplier.dataset.deplier;
          etat.deplie = {
            ...etat.deplie,
            [champ2]: (Number(etat.deplie[champ2]) || 0) + 1
          };
          chercher({ structurel: false });
          return;
        }
        const replier = e.target.closest("[data-replier]");
        if (replier) {
          const champ2 = replier.dataset.replier;
          const { [champ2]: _retire, ...reste } = etat.deplie;
          etat.deplie = reste;
          chercher({ structurel: false });
          return;
        }
        const note = e.target.closest("[data-note]");
        if (note) {
          e.preventDefault();
          const seuil = Number(note.dataset.note);
          etat.note = etat.note === seuil ? null : seuil;
          noterFiltre(
            etat.note == null ? "filter_removed" : "filter_applied",
            "Avis clients",
            seuil
          );
          chercher();
          return;
        }
        const retirer = e.target.closest("[data-retirer]");
        if (retirer) {
          const [genre, champ2, ...reste] = retirer.dataset.retirer.split(":");
          if (genre === "facette") basculerValeur(champ2, reste.join(":"));
          if (genre === "prix") etat.prix = null;
          if (genre === "note") etat.note = null;
          if (genre === "mesure") {
            const { [champ2]: _retiree, ...reste2 } = etat.mesures;
            etat.mesures = reste2;
            const fiche = curseursNombre.get(champ2);
            if (fiche) fiche.curseur.positionner(null);
          }
          const libelle = genre === "prix" ? "Prix" : genre === "note" ? "Avis clients" : TITRES_GROUPES[groupeDe(champ2)] || LIBELLES_AKENEO[champ2] || champ2;
          noterFiltre("filter_removed", libelle, reste.join(":") || genre);
          chercher();
          return;
        }
        if (e.target.closest("[data-effacer]")) effacerTout();
      });
      const zoneResultats = conteneurDefilant() || noeud("wb-hits");
      if (zoneResultats) {
        zoneResultats.addEventListener("click", (e) => {
          if (e.target.closest("[data-effacer]")) effacerTout();
        });
        for (const type of ["pointerdown", "click"]) {
          zoneResultats.addEventListener(type, () => temoin.confirmer(), { capture: true });
        }
      }
      const partir = () => temoin.quitter();
      if (fenetre && typeof fenetre.addEventListener === "function") {
        fenetre.addEventListener("pagehide", partir);
        const doc = fenetre.document;
        if (doc && typeof doc.addEventListener === "function") {
          doc.addEventListener("visibilitychange", () => {
            if (doc.visibilityState === "hidden") partir();
          });
        }
      }
      let minuteurFacette;
      colonne.addEventListener("input", (e) => {
        const champ2 = e.target.dataset.rechercheFacette;
        if (!champ2) return;
        focusFacette = champ2;
        etat.termeFacette = { ...etat.termeFacette, [champ2]: e.target.value.trim() };
        clearTimeout(minuteurFacette);
        minuteurFacette = setTimeout(() => chercher({ structurel: false }), 160);
      });
      colonne.addEventListener("focusout", (e) => {
        if (e.target.dataset.rechercheFacette) focusFacette = null;
      });
      const champ = noeud("wb-overlay-input");
      if (champ) {
        champ.addEventListener("input", (e) => definirRequete(e.target.value));
      }
      const reset = boutonReset();
      if (reset) {
        reset.addEventListener("click", () => {
          etat.q = "";
          etat.qMoteur = "";
          temoin.saisie("");
          noeud("wb-overlay-input").value = "";
          reset.hidden = true;
          chercher();
        });
      }
      const plus = noeud("wb-load-more");
      if (plus) {
        plus.addEventListener("click", (e) => {
          if (e.preventDefault) e.preventDefault();
          chargerSuite();
        });
      }
      const tri = selecteurDeTri();
      if (tri) {
        tri.addEventListener("change", (e) => {
          etat.tri = e.target.value;
          chercher();
        });
      }
      racine.querySelectorAll("[data-essai]").forEach((lien) => {
        lien.addEventListener("click", (e) => {
          e.preventDefault();
          definirRequete(lien.dataset.essai, { immediat: true });
        });
      });
    }
    function selecteurDeTri() {
      const existant = noeud("wb-sort-select");
      if (existant) return existant;
      const conteneur = noeud("wb-sort");
      if (!conteneur) return null;
      let select = conteneur.querySelector("select");
      if (!select) {
        select = racine.createElement("select");
        select.id = "wb-sort-select";
        select.className = "wb-sort-select";
        select.setAttribute("aria-label", "Trier");
        conteneur.appendChild(select);
      }
      return select;
    }
    function remplirTris() {
      if (!tris) return;
      const select = selecteurDeTri();
      if (!select) return;
      select.innerHTML = optionsDeTri(tris).map(({ valeur, libelle }) => `<option value="${esc(valeur)}">${esc(libelle)}</option>`).join("");
      select.value = etat.tri;
    }
    var demarree = false;
    async function demarrer({ premierCycle = true } = {}) {
      if (demarree) return true;
      try {
        await gabarit3();
      } catch (erreur) {
        afficherPanne(erreur);
        return false;
      }
      const schema = await moteur.schema(collection);
      if (schema) {
        candidatsDecouverte = champsFacettables(schema, CANDIDATS_METIER);
        if (CANDIDATS_METIER.length && !candidatsDecouverte.length) {
          console.warn(`[facettes] aucun filtre métier facettable dans ${collection} : la colonne se limite aux facettes de parité.`);
        } else if (candidatsDecouverte.length < CANDIDATS_METIER.length) {
          console.info(`[facettes] ${candidatsDecouverte.length} filtres facettables sur ${CANDIDATS_METIER.length} exposés — les autres n'ont aucune valeur dans cet index.`);
        }
      } else {
        candidatsDecouverte = [...CANDIDATS_METIER];
        console.info(`[facettes] schéma de ${collection} illisible — normal avec la clé du thème, qui ne porte que \`documents:search\`. Les ${CANDIDATS_METIER.length} filtres exposés sont tous tentés, et ceux que l'index ne connaît pas seront écartés au premier échec.`);
      }
      construireColonne();
      remplirTris();
      refleterRequete();
      brancherInteractions();
      installerScrollInfini();
      demarree = true;
      if (premierCycle) {
        requeteAnalytics = etat.q || null;
        chercher();
      }
      return true;
    }
    return {
      etat,
      demarrer,
      chercher,
      definirRequete,
      definirEtat,
      definirPerimetre,
      definirParPage,
      basculerValeur,
      basculerRepli,
      effacerTout,
      chargerSuite,
      /** L'hôte ferme sa recherche (overlay) : la requête vue compte. */
      quitter: () => temoin.quitter(),
      get demarree() {
        return demarree;
      }
    };
  }

  // frontend/js/config-theme.js
  var LANGUES = ["fr", "en", "de", "es", "it", "nl", "pt"];
  var MIN_CHARS = 2;
  function aliasPourLocale(iso, langues = LANGUES) {
    const langue2 = String(iso || "").toLowerCase().split("-")[0];
    return `parity_${langues.includes(langue2) ? langue2 : "fr"}`;
  }
  var CHAMP_PRIX_DE_BASE = "price";
  var CHAMP_COMPARE_DE_BASE = "compare_at";
  var PREFIXE_CDN = /^(?:https?:)?\/\/[^/]+\/cdn\/shop\/$/;
  function lirePrefixeCdn(brut) {
    return typeof brut === "string" && PREFIXE_CDN.test(brut) ? brut : "";
  }
  function champPrixPourPays(pays, table, defaut = CHAMP_PRIX_DE_BASE) {
    const code = String(pays || "").trim().toUpperCase();
    const champ = table && typeof table === "object" ? table[code] : void 0;
    return typeof champ === "string" && /^[a-z_][a-z0-9_]*$/.test(champ) ? champ : defaut;
  }
  function lireConfig(brut, marchesPrix = void 0, marchesCompare = void 0) {
    const c = brut || {};
    if (!c.host || typeof c.host !== "string") {
      throw new Error("WB_SEARCH.host manquant (hôte Typesense)");
    }
    if (!c.key || typeof c.key !== "string") {
      throw new Error("WB_SEARCH.key manquante (clé search-only)");
    }
    const minChars = Number.isInteger(c.minChars) && c.minChars >= 1 ? c.minChars : MIN_CHARS;
    const ttl = Number.isFinite(Number(c.ttl)) && Number(c.ttl) >= 0 ? Number(c.ttl) : 60;
    const locale = typeof c.locale === "string" && c.locale ? c.locale : "fr";
    const country = String(c.country || "").trim().toUpperCase();
    const devise = String(c.currency || "").trim().toUpperCase();
    return {
      host: c.host.replace(/^https?:\/\//, "").replace(/\/+$/, ""),
      key: c.key,
      alias: c.alias || aliasPourLocale(c.locale),
      root: typeof c.root === "string" && c.root ? c.root : "/",
      locale,
      country,
      currency: /^[A-Z]{3}$/.test(devise) ? devise : "EUR",
      champPrix: champPrixPourPays(country, marchesPrix),
      /* Le prix BARRÉ du même marché. L'index n'émet `compare_at_ch` que là où il
         émet `price_ch` (`marches.compare_par_marche`) : le couple ne se
         dépareille pas, et un prix en euros n'est jamais barré en francs. */
      champCompare: champPrixPourPays(
        country,
        marchesCompare,
        CHAMP_COMPARE_DE_BASE
      ),
      /* Le préfixe CDN de la boutique, pour aligner l'URL d'image de nos cartes
         sur celle de la carte serveur (245 Ko par PLP). Absent ou mal formé : pas
         de réalignement, et l'image reste juste. */
      filesUrl: lirePrefixeCdn(c.filesUrl),
      /* Les libellés de stock de la fiche, dans la langue de la page
         (`definirLibellesTheme`, qui les filtre). Absents (snippet d'avant le
         30/10/2026) : la table de `libelles.js` sert seule. */
      stock: c.stock && typeof c.stock === "object" ? c.stock : null,
      minChars,
      ttl
    };
  }
  function lireConfigCarte(dataset, repli = {}) {
    const d = dataset || {};
    let badges = [];
    try {
      badges = JSON.parse(d.customBadges || "[]");
    } catch (e) {
      badges = [];
    }
    return {
      moneyFormat: d.moneyFormat || repli.moneyFormat || "{{amount_with_comma_separator}} €",
      saleBadge: d.saleBadge || repli.saleBadge || "percentage",
      saleLabel: d.saleLabel || repli.saleLabel || "Promo",
      customBadges: Array.isArray(badges) ? badges : []
    };
  }

  // frontend/js/ab-alma.js
  var EXPERIENCE = "alma_plp_1";
  var COOKIE = "wb_ab_alma";
  var ATTRIBUT_PANIER = "_ab_alma";
  var PARAMETRE_FORCAGE = "wb_ab_alma";
  var DUREE_SECONDES = 90 * 24 * 3600;
  var FIN = "2026-11-20";
  var VERSION_APRES_FIN = "B";
  var VARIANTES = /* @__PURE__ */ new Set(["A", "B"]);
  var CLE_PANIER_SESSION = "wb_ab_alma_panier";
  function lireCookie(doc) {
    const brut = String(doc.cookie || "");
    for (const morceau of brut.split(";")) {
      const [nom, ...reste] = morceau.trim().split("=");
      if (nom === COOKIE) return reste.join("=");
    }
    return "";
  }
  function ecrireCookie(doc, valeur) {
    doc.cookie = `${COOKIE}=${valeur}; max-age=${DUREE_SECONDES}; path=/; samesite=lax`;
  }
  function forcage(recherche) {
    const valeur = new URLSearchParams(recherche || "").get(PARAMETRE_FORCAGE);
    const variante = String(valeur || "").toUpperCase();
    return VARIANTES.has(variante) ? variante : null;
  }
  function groupeAlma({
    doc = globalThis.document,
    recherche = ((_a) => (_a = globalThis.location) == null ? void 0 : _a.search)(),
    maintenant = /* @__PURE__ */ new Date(),
    hasard = Math.random,
    navigateur = globalThis.navigator || {}
  } = {}) {
    if (maintenant.getTime() >= (/* @__PURE__ */ new Date(`${FIN}T00:00:00+01:00`)).getTime()) {
      return { variante: VERSION_APRES_FIN, enTest: false };
    }
    if (navigateur.webdriver) return { variante: "B", enTest: false };
    try {
      const force = forcage(recherche);
      if (force) {
        ecrireCookie(doc, `force.${force}`);
        return { variante: force, enTest: false };
      }
      const [prefixe, variante] = lireCookie(doc).split(".");
      if (prefixe === "force" && VARIANTES.has(variante)) {
        return { variante, enTest: false };
      }
      if (prefixe === EXPERIENCE && VARIANTES.has(variante)) {
        return { variante, enTest: true };
      }
      const tiree = hasard() < 0.5 ? "A" : "B";
      ecrireCookie(doc, `${EXPERIENCE}.${tiree}`);
      return { variante: tiree, enTest: true };
    } catch (e) {
      return { variante: hasard() < 0.5 ? "A" : "B", enTest: false };
    }
  }
  function planifierAuRepos(f) {
    if (typeof globalThis.requestIdleCallback === "function") {
      globalThis.requestIdleCallback(f, { timeout: 5e3 });
    } else {
      setTimeout(f, 2e3);
    }
  }
  var attendreMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  var DELAI_VERIFICATION_MS = 4e3;
  var ECRITURES_MAX = 3;
  var jetonSeul = (jeton) => decodeURIComponent(String(jeton || "")).split("?")[0];
  function jetonDuCookie(doc) {
    try {
      const m = String((doc == null ? void 0 : doc.cookie) || "").match(/(?:^|;\s*)cart=([^;]+)/);
      return m ? jetonSeul(m[1]) : "";
    } catch (e) {
      return "";
    }
  }
  async function assurerAttribut({ valeur, fenetre, fetchFn, attendre }) {
    var _a, _b;
    const session = fenetre.sessionStorage;
    const racine = ((_b = (_a = fenetre.Shopify) == null ? void 0 : _a.routes) == null ? void 0 : _b.root) || "/";
    const memo = (jeton) => {
      try {
        session == null ? void 0 : session.setItem(CLE_PANIER_SESSION, `${jeton}|${valeur}`);
      } catch (e) {
      }
    };
    try {
      const courant = jetonDuCookie(fenetre.document);
      if (courant && (session == null ? void 0 : session.getItem(CLE_PANIER_SESSION)) === `${courant}|${valeur}`) return;
    } catch (e) {
      return;
    }
    const lire = () => fetchFn(`${racine}cart.js`, { credentials: "same-origin" }).then((r) => r.json());
    const ecrire = () => fetchFn(`${racine}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      keepalive: true,
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { [ATTRIBUT_PANIER]: valeur } })
    });
    try {
      let panier = await lire();
      for (let ecritures = 0; ; ecritures += 1) {
        if ((panier.attributes || {})[ATTRIBUT_PANIER] === valeur) {
          memo(jetonSeul(panier.token));
          return;
        }
        if (ecritures >= ECRITURES_MAX) return;
        await ecrire();
        await attendre(DELAI_VERIFICATION_MS);
        panier = await lire();
      }
    } catch (e) {
    }
  }
  function creerExposition({
    variante,
    enTest,
    fenetre = globalThis,
    fetchFn = ((_a) => (_a = globalThis.fetch) == null ? void 0 : _a.bind(globalThis))(),
    planifier = planifierAuRepos,
    attendre = attendreMs
  }) {
    let fait = false;
    return function exposer() {
      if (!enTest || fait) return Promise.resolve();
      fait = true;
      pousserDataLayer("wb_ab_exposition", { experience: EXPERIENCE, variante }, fenetre);
      if (typeof fetchFn !== "function") return Promise.resolve();
      return new Promise((resolve) => {
        planifier(() => {
          assurerAttribut({ valeur: `${EXPERIENCE}.${variante}`, fenetre, fetchFn, attendre }).finally(resolve);
        });
      });
    };
  }

  // frontend/js/client-pro.js
  var CIBLE = { CONNECTE: 1, NON_CONNECTE: 2, IDS: 3, TAGS: 4, SITE_ENTREPRISE: 5 };
  var EXCLUSION = { TAGS: 1, IDS: 2, SITE_ENTREPRISE: 3 };
  var MARCHE_PAR_IDS = 1;
  var champBss = (regle, court, long) => {
    var _a;
    return (_a = regle[court]) != null ? _a : regle[long];
  };
  var listeBss = (valeur) => (valeur == null ? [] : String(valeur).split(",")).map((v) => v.trim().toLowerCase()).filter(Boolean);
  function lireClient(bss, doc) {
    var _a, _b;
    if ((_a = bss.shopData) == null ? void 0 : _a.customer) return bss.shopData.customer;
    try {
      const noeud = (_b = doc == null ? void 0 : doc.getElementById) == null ? void 0 : _b.call(doc, "bss-b2b-store-data");
      return noeud ? JSON.parse(noeud.textContent).customer || null : null;
    } catch (e) {
      return null;
    }
  }
  function profil(client) {
    return {
      id: client.id == null ? null : String(client.id),
      tags: (Array.isArray(client.tags) ? client.tags : []).map((t2) => String(t2).trim().toLowerCase()),
      site: client.current_location == null ? null : String(client.current_location)
    };
  }
  function cibleLeClient(regle, { id, tags, site }) {
    switch (Number(champBss(regle, "at", "apply_to"))) {
      case CIBLE.CONNECTE:
        return id !== null;
      case CIBLE.NON_CONNECTE:
        return id === null;
      case CIBLE.IDS:
        return id !== null && listeBss(champBss(regle, "ci", "customer_ids")).includes(id);
      case CIBLE.TAGS:
        return listeBss(champBss(regle, "ct", "customer_tags")).some((t2) => tags.includes(t2));
      case CIBLE.SITE_ENTREPRISE:
        return site !== null && listeBss(champBss(regle, "cli", "company_location_ids")).includes(site);
      default:
        return true;
    }
  }
  function exclutLeClient(regle, { id, tags, site }) {
    switch (Number(champBss(regle, "ef", "exclude_from"))) {
      case EXCLUSION.TAGS:
        return listeBss(champBss(regle, "ect", "exc_customer_tags")).some((t2) => tags.includes(t2));
      case EXCLUSION.IDS:
        return id !== null && listeBss(champBss(regle, "ec", "exc_customers")).includes(id);
      case EXCLUSION.SITE_ENTREPRISE:
        return site !== null && listeBss(champBss(regle, "ecli", "exc_company_location_ids")).includes(site);
      default:
        return false;
    }
  }
  function dansLeMarche(regle, fenetre) {
    var _a;
    if (Number(champBss(regle, "mct", "market_condition_type")) !== MARCHE_PAR_IDS) return true;
    const marches = listeBss(champBss(regle, "mi", "market_ids"));
    if (marches.length) {
      return fenetre.bssB2bMarketId != null && marches.includes(String(fenetre.bssB2bMarketId));
    }
    const regions = champBss(regle, "ar", "allowed_regions");
    return Array.isArray(regions) && regions.includes((_a = fenetre.Shopify) == null ? void 0 : _a.country);
  }
  function enVigueur(regle, maintenant) {
    const debut = champBss(regle, "sd", "start_date");
    if (!debut || !champBss(regle, "st", "start_time")) return true;
    if (maintenant < new Date(debut)) return false;
    const fin = champBss(regle, "ed", "end_date");
    const finActive = champBss(regle, "eed", "enable_end_date") && fin && champBss(regle, "et", "end_time");
    return !(finActive && maintenant > new Date(fin));
  }
  function clientPro({
    fenetre = globalThis,
    doc = globalThis.document,
    maintenant = /* @__PURE__ */ new Date()
  } = {}) {
    var _a, _b;
    const bss = fenetre == null ? void 0 : fenetre.BSS_B2B;
    if (!bss || typeof bss !== "object") return false;
    const regles = (_b = (_a = bss.cp) == null ? void 0 : _a.cpRules) != null ? _b : bss.configData;
    const client = lireClient(bss, doc);
    if (!Array.isArray(regles) || !client) return true;
    const qui = profil(client);
    return regles.some((regle) => regle && typeof regle === "object" && cibleLeClient(regle, qui) && !exclutLeClient(regle, qui) && dansLeMarche(regle, fenetre) && enVigueur(regle, maintenant));
  }

  // frontend/js/stopwords.js
  function langueDepuisLocale(locale) {
    return String(locale || "fr").toLowerCase().split("-")[0] || "fr";
  }
  function avecStopwords(parametres, locale, jeux = {}) {
    const langue2 = langueDepuisLocale(locale);
    const jeu = jeux && jeux[langue2];
    return jeu ? { ...parametres, stopwords: jeu } : { ...parametres };
  }

  // frontend/js/barre-outils.js
  var SELECTEUR_COLLANT = ".sticky-element";
  var SELECTEUR_BARRE = ".collection__toolbar-left";
  var SELECTEUR_BARRE_DROITE = ".collection__toolbar-right";
  var CLASSE_ENTETE_OUTILS = "wb-plp-entete-outils";
  var SELECTEUR_COMPTEUR_THEME = "[data-facet-loading]";
  var SELECTEUR_BASCULE2 = "layout-switcher";
  var POINT_DE_RUPTURE2 = 1024;
  function estEtroit(fenetre) {
    if (!fenetre) return null;
    if (typeof fenetre.matchMedia === "function") {
      return fenetre.matchMedia(`(max-width: ${POINT_DE_RUPTURE2}px)`).matches;
    }
    const largeur = Number(fenetre.innerWidth);
    return Number.isFinite(largeur) ? largeur <= POINT_DE_RUPTURE2 : null;
  }
  var ATTR_BASCULER = "data-wb-plp-basculer";
  var ID_COLONNE = "wb-plp-facettes-colonne";
  var ATTR_EN_ATTENTE = "data-wb-plp-en-attente";
  var PREFIXE_FILTRE_SHOPIFY = "filter.";
  function aDesFiltresShopify(chaine) {
    const p = new URLSearchParams(String(chaine || "").replace(/^\?/, ""));
    for (const cle of p.keys()) {
      if (cle.startsWith(PREFIXE_FILTRE_SHOPIFY)) return true;
    }
    return false;
  }
  function echapper(texte) {
    return String(texte).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }
  function boutonFiltresHtml(libelle, { enAttente = false } = {}) {
    return `<button type="button" class="btn btn--outline wb-plp-bouton-filtres" ${ATTR_BASCULER}${enAttente ? ` ${ATTR_EN_ATTENTE}` : ""} aria-expanded="false" aria-controls="${ID_COLONNE}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg><span class="btn__text">${echapper(libelle)}</span></button>`;
  }
  function adopterBoutonFiltres(barre, html) {
    const deja = barre.querySelector(`[${ATTR_BASCULER}]`);
    if (deja) {
      deja.removeAttribute(ATTR_EN_ATTENTE);
      return deja;
    }
    barre.insertAdjacentHTML("afterbegin", html);
    return null;
  }
  function degarnirBarreCollante(doc = document, fenetre = void 0) {
    if (estEtroit(fenetre) === false) {
      return { restaurer: () => {
      } };
    }
    const collant = doc.querySelector(SELECTEUR_COLLANT);
    const posee = doc.querySelector(`.${CLASSE_ENTETE_OUTILS}`);
    if (collant && posee) return { restaurer: () => defaireLigne(posee, placesPrecoces(posee, collant)) };
    const sortants = [
      doc.querySelector(SELECTEUR_COMPTEUR_THEME),
      doc.querySelector(SELECTEUR_BASCULE2)
    ].filter((n) => n && typeof n.closest === "function" && n.closest(SELECTEUR_COLLANT));
    if (!collant || !collant.parent && !collant.parentNode || !sortants.length) {
      return { restaurer: () => {
      } };
    }
    const parent = collant.parentNode || collant.parent;
    const ligne = doc.createElement("div");
    ligne.className = CLASSE_ENTETE_OUTILS;
    const origines = sortants.map((n) => ({ n, parent: n.parentNode || n.parent }));
    parent.insertBefore(ligne, collant);
    sortants.forEach((n) => ligne.appendChild(n));
    return { restaurer: () => defaireLigne(ligne, origines) };
  }
  function placesPrecoces(ligne, collant) {
    return [
      [SELECTEUR_COMPTEUR_THEME, SELECTEUR_BARRE],
      [SELECTEUR_BASCULE2, SELECTEUR_BARRE_DROITE]
    ].map(([noeud, place]) => ({
      n: ligne.querySelector(noeud),
      parent: collant.querySelector(place)
    })).filter(({ n, parent }) => n && parent);
  }
  function defaireLigne(ligne, origines) {
    origines.forEach(({ n, parent }) => parent && parent.appendChild(n));
    const hote = ligne.parentNode || ligne.parent;
    if (hote && typeof hote.retirer === "function") hote.retirer(ligne);
    else if (typeof ligne.remove === "function") ligne.remove();
  }

  // frontend/js/pagination.js
  var SELECTEUR_NAV = "nav.wb-pagination";
  var ID_GABARIT2 = "wb-pagination-parts";
  var JETON_NUMERO = "__N__";
  var VOISINS = 2;
  var PARTIES = Object.freeze(["previous", "page", "current", "gap", "next"]);
  function fenetreDePages(courante, derniere) {
    const total = Number.isInteger(derniere) ? derniere : 0;
    if (total < 2) return [];
    const page = Number.isInteger(courante) ? courante : 1;
    const bornes = /* @__PURE__ */ new Set([1, total]);
    for (let n = page - VOISINS; n <= page + VOISINS; n += 1) {
      if (n >= 1 && n <= total) bornes.add(n);
    }
    const numeros = [...bornes].sort((a, b) => a - b);
    return numeros.reduce((parties, numero, index) => {
      const saute = index > 0 && numero - numeros[index - 1] > 1;
      return saute ? [...parties, { trou: true }, { page: numero }] : [...parties, { page: numero }];
    }, []);
  }
  function dernierePage({ total = 0, parPage = 1, plafond = Infinity } = {}) {
    const trouves = Math.max(0, Number(total) || 0);
    const borne = Number(plafond) || Infinity;
    const lot = Math.max(1, Number(parPage) || 1);
    return Math.max(1, Math.ceil(Math.min(trouves, borne) / lot));
  }
  function partie(gabarit3, nom, numero = null, href = null) {
    const modele = gabarit3 && gabarit3.content && gabarit3.content.querySelector(`[data-wb-part="${nom}"]`);
    if (!modele) return null;
    const item = modele.cloneNode(true);
    if (typeof item.removeAttribute === "function") item.removeAttribute("data-wb-part");
    const lien = typeof item.querySelector === "function" ? item.querySelector("a") : null;
    if (!lien) return item;
    if (href) lien.setAttribute("href", href);
    const etiquette = lien.getAttribute("aria-label");
    if (etiquette && etiquette.includes(JETON_NUMERO)) {
      lien.setAttribute("aria-label", etiquette.split(JETON_NUMERO).join(String(numero)));
    }
    const texte = String(lien.textContent || "");
    if (texte.includes(JETON_NUMERO)) {
      lien.textContent = texte.split(JETON_NUMERO).join(String(numero));
    }
    return item;
  }
  function rendreNav(nav, gabarit3, { courante = 1, derniere = 1, lien } = {}) {
    if (!nav || typeof nav.querySelector !== "function") return false;
    const liste2 = nav.querySelector(".pagination__list");
    if (!liste2 || typeof liste2.replaceChildren !== "function") return false;
    if (!Number.isInteger(derniere) || derniere <= 1) {
      nav.hidden = true;
      liste2.replaceChildren();
      return false;
    }
    const page = Math.min(Math.max(1, Number(courante) || 1), derniere);
    const href = typeof lien === "function" ? lien : (n) => `?page=${n}`;
    const pages = fenetreDePages(page, derniere).map((bloc2) => {
      if (bloc2.trou) return partie(gabarit3, "gap");
      const courant = bloc2.page === page;
      return partie(
        gabarit3,
        courant ? "current" : "page",
        bloc2.page,
        courant ? null : href(bloc2.page)
      );
    });
    const items = [
      page > 1 ? partie(gabarit3, "previous", page - 1, href(page - 1)) : null,
      ...pages,
      page < derniere ? partie(gabarit3, "next", page + 1, href(page + 1)) : null
    ].filter(Boolean);
    if (items.length === 0) return false;
    liste2.replaceChildren(...items);
    nav.hidden = false;
    return true;
  }

  // frontend/js/plp.js
  var SELECTEUR_HOTE = "[data-wb-collection-id]";
  var ID_TEMPLATE_CARTE = "algolia-card-tpl";
  var SELECTEUR_CONTENU = ".collection__content";
  var ID_GRILLE = "ProductsList";
  var ID_COMPTEUR = "ProductCount";
  var ID_TRI = "SortBy-collection";
  var ID_CONTENEUR_GRILLE = "ProductGridContainer";
  var SELECTEUR_PAGINATION = ".pagination";
  var ID_FILTRES_NATIFS = "FacetFiltersContainer";
  var PREFIXE_FACETTE = "f.";
  var PREFIXE_MESURE = "m.";
  var ID_FACETTES = "wb-plp-facettes";
  var ID_JETONS = "wb-plp-jetons";
  var ID_EFFACER = "wb-plp-effacer";
  var ID_SENTINELLE = "wb-plp-sentinelle";
  var ID_STATUT = "wb-plp-statut";
  var ID_PROGRESSION = "wb-plp-progression";
  var ID_CHARGER = "wb-plp-charger";
  var ID_PAR_PAGE = "wb-plp-par-page";
  var CLASSE_TIROIR_OUVERT = "wb-plp-filtres-ouverts";
  var NOEUDS_PLP = Object.freeze({
    "wb-hits": ID_GRILLE,
    "wb-stats": ID_COMPTEUR,
    "wb-sort-select": ID_TRI,
    "wb-facets-dynamic": ID_FACETTES,
    "wb-current-refinements": ID_JETONS,
    "wb-clear-refinements": ID_EFFACER,
    "wb-infinite-sentinel": ID_SENTINELLE,
    "wb-infinite-status": ID_STATUT,
    "wb-load-more": ID_CHARGER,
    "wb-overlay-input": "wb-plp-sans-champ",
    "wb-overlay-reset": "wb-plp-sans-reset",
    "wb-sort": "wb-plp-sans-conteneur-tri"
  });
  var SELECTEURS_PLP = Object.freeze({
    colonne: `#${ID_COLONNE}`,
    // C'est la PAGE qui défile, pas un panneau : le cycle observe le viewport.
    defilant: null,
    // La grille du thème, déjà en place et déjà stylée.
    grille: ".products-list",
    // Voir `boutonReset` dans recherche.js : sans ce `null`, on attraperait
    // l'effacement de l'overlay.
    reset: null,
    /* La bascule du tiroir de CETTE page, celle que `brancherTiroir` écoute — et
       surtout pas `data-wb-toggle-facets`, qui est celle de l'overlay de
       recherche, présent sur la même page. */
    bascule: "data-wb-plp-basculer"
  });
  var PAR_PAGE_REPLI = 48;
  var MULTIPLES_PAR_PAGE = Object.freeze([1, 2, 3]);
  function optionsParPage(parPageServi = null) {
    const base = Number.isInteger(parPageServi) && parPageServi > 0 ? parPageServi : PAR_PAGE_REPLI;
    return Object.freeze(MULTIPLES_PAR_PAGE.map((facteur) => base * facteur));
  }
  function perimetreDansIndex(collectionId, table) {
    const id = String(collectionId || "");
    if (!id || !table) return id;
    const traduit = table[id];
    return traduit === void 0 || traduit === null ? id : String(traduit);
  }
  function parPageDepuisRequete(chaine, options = optionsParPage()) {
    const p = new URLSearchParams(String(chaine || "").replace(/^\?/, ""));
    const demande = Number.parseInt(p.get("par") || "", 10);
    return options.includes(demande) ? demande : options[0];
  }
  function lireHote(doc, table = null, requete = "") {
    const hote = doc.querySelector(SELECTEUR_HOTE);
    if (!hote) return null;
    const { collectionId } = lirePerimetre(hote);
    const brut = Number.parseInt(hote.dataset.wbParPage || "", 10);
    const parPageServi = Number.isInteger(brut) && brut > 0 ? brut : null;
    const options = optionsParPage(parPageServi);
    return {
      hote,
      collectionId: perimetreDansIndex(collectionId, table),
      parPage: parPageDepuisRequete(requete, options),
      parPageServi,
      optionsParPage: options,
      grilleServie: hote.dataset.wbGrilleServie === "1"
    };
  }
  function etatDepuisRequete(chaine) {
    const p = new URLSearchParams(String(chaine || "").replace(/^\?/, ""));
    const selection = {};
    for (const [cle, valeur] of p.entries()) {
      if (!cle.startsWith(PREFIXE_FACETTE)) continue;
      const champ = cle.slice(PREFIXE_FACETTE.length);
      if (!champ) continue;
      selection[champ] = [...selection[champ] || [], valeur];
    }
    const mesures = {};
    for (const [cle, valeur] of p.entries()) {
      if (!cle.startsWith(PREFIXE_MESURE)) continue;
      const champ = cle.slice(PREFIXE_MESURE.length);
      if (!champ) continue;
      const bornes2 = valeur.split("-");
      if (bornes2.length !== 2 || bornes2[0] === "" || bornes2[1] === "") continue;
      const de = Number(bornes2[0]);
      const a = Number(bornes2[1]);
      if (!Number.isFinite(de) || !Number.isFinite(a)) continue;
      mesures[champ] = { de, a };
    }
    let prix = null;
    const bornes = (p.get("px") || "").split("-");
    if (bornes.length === 2) {
      const lire = (s) => s === "" ? null : Number(s);
      const [de, a] = bornes.map(lire);
      const valide = (v) => v === null || Number.isFinite(v);
      if (valide(de) && valide(a) && (de !== null || a !== null)) prix = { de, a };
    }
    const note = Number.parseInt(p.get("note") || "", 10);
    const page = Number.parseInt(p.get("page") || "", 10);
    return {
      selection,
      mesures,
      prix,
      note: Number.isInteger(note) ? note : null,
      tri: p.get("tri") || "",
      page: Number.isInteger(page) && page > 0 ? page : 1
    };
  }
  function aDesFiltres(chaine) {
    const etat = etatDepuisRequete(chaine);
    return Object.keys(etat.selection).length > 0 || Object.keys(etat.mesures).length > 0 || etat.prix !== null || etat.note !== null || !!etat.tri;
  }
  function requeteDepuisEtat(etat, chaineDeDepart = "", parPageDefaut = PAR_PAGE_REPLI) {
    const p = new URLSearchParams(String(chaineDeDepart || "").replace(/^\?/, ""));
    for (const cle of [...p.keys()]) {
      if (cle.startsWith(PREFIXE_FACETTE) || cle.startsWith(PREFIXE_MESURE)) p.delete(cle);
    }
    ["px", "note", "tri", "par", "page"].forEach((cle) => p.delete(cle));
    for (const [champ, valeurs] of Object.entries((etat || {}).selection || {})) {
      for (const valeur of valeurs || []) {
        p.append(`${PREFIXE_FACETTE}${champ}`, valeur);
      }
    }
    for (const [champ, bornes] of Object.entries((etat || {}).mesures || {})) {
      p.set(`${PREFIXE_MESURE}${champ}`, `${bornes.de}-${bornes.a}`);
    }
    if (etat && etat.prix) {
      const borne = (v) => Number.isFinite(v) ? String(v) : "";
      p.set("px", `${borne(etat.prix.de)}-${borne(etat.prix.a)}`);
    }
    if (etat && etat.note) p.set("note", String(etat.note));
    if (etat && etat.tri) p.set("tri", etat.tri);
    if (etat && etat.parPage && etat.parPage !== parPageDefaut) {
      p.set("par", String(etat.parPage));
    }
    if (etat && etat.pageDepart > 1) p.set("page", String(etat.pageDepart));
    const chaine = p.toString();
    return chaine ? `?${chaine}` : "";
  }
  function colonneHtml() {
    return `<aside id="${ID_COLONNE}" class="wb-plp-facettes" data-wb-facets aria-label="${esc(t("filtres"))}"><div class="wb-plp-facettes-entete"><span>${esc(t("filtres"))}</span><button type="button" class="wb-plp-fermer" data-wb-plp-basculer aria-label="${esc(t("fermer_filtres"))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg></button></div><div id="${ID_EFFACER}"></div><div id="${ID_JETONS}"></div><div id="${ID_FACETTES}"></div></aside>`;
  }
  function boutonTiroirHtml() {
    return boutonFiltresHtml(t("filtres"));
  }
  var ATTR_PRETE = "data-wb-prete";
  function libererGrille(hote) {
    if (!hote || typeof hote.setAttribute !== "function") return false;
    if (typeof hote.hasAttribute === "function" && hote.hasAttribute(ATTR_PRETE)) return false;
    hote.setAttribute(ATTR_PRETE, "1");
    return true;
  }
  function revelerConteneur(conteneur) {
    const servi = conteneur.style.display || "";
    conteneur.style.display = "block";
    return () => {
      conteneur.style.display = servi;
    };
  }
  function lienPage(chaineDeDepart, page) {
    const p = new URLSearchParams(String(chaineDeDepart || "").replace(/^\?/, ""));
    if (Number(page) > 1) p.set("page", String(Math.trunc(Number(page))));
    else p.delete("page");
    const chaine = p.toString();
    return chaine ? `?${chaine}` : "?";
  }
  function piedHtml({ href = "?", parPage = null, options = null } = {}) {
    const choix = options || optionsParPage(parPage);
    const lot = parPage || choix[0];
    return `<div id="${ID_SENTINELLE}" class="wb-plp-pied"><div class="wb-plp-pied-ligne"><p id="${ID_PROGRESSION}" class="wb-plp-progression"></p>` + selecteurParPageHtml(lot, choix) + `</div><a id="${ID_CHARGER}" class="btn btn--primary wb-plp-charger" href="${esc(href)}" rel="next" data-wb-par-page="${parPage}"><span>${esc(t("charger_plus"))}</span></a><p id="${ID_STATUT}" class="wb-plp-statut" role="status" aria-live="polite"></p></div>`;
  }
  function neutraliserTri(select, doc) {
    const rien = { select, restaurer: () => {
    } };
    if (!select || typeof select.getAttribute !== "function") return rien;
    if (!select.getAttribute("is")) return rien;
    const neuf = doc.createElement("select");
    for (const { name, value } of Array.from(select.attributes || [])) {
      if (name !== "is") neuf.setAttribute(name, value);
    }
    neuf.innerHTML = select.innerHTML;
    neuf.value = select.value;
    if (typeof select.replaceWith === "function") select.replaceWith(neuf);
    return {
      select: neuf,
      restaurer: () => {
        if (typeof neuf.replaceWith === "function") neuf.replaceWith(select);
      }
    };
  }
  function selecteurParPageHtml(choisi = null, options = optionsParPage()) {
    const lot = choisi || options[0];
    const items = options.map(
      (n) => `<option value="${n}"${n === lot ? ' selected="selected"' : ""}>${n}</option>`
    ).join("");
    return `<div class="select w-auto wb-plp-par-page flex items-center gap-2"><label for="${ID_PAR_PAGE}" class="text-nowrap font-body-bolder">${esc(t("afficher"))}</label><select id="${ID_PAR_PAGE}" class="form-control form-control--select" aria-label="${esc(t("produits_par_page"))}">${items}</select></div>`;
  }
  function brancherPLP(fenetre = window, doc = document) {
    var _a;
    const requeteDeDepart = fenetre.location ? fenetre.location.search : "";
    const trouve = lireHote(doc, fenetre.WB_COLLECTIONS, requeteDeDepart);
    if (!trouve) return null;
    if (doc.getElementById(ID_COLONNE)) return null;
    if (aDesFiltresShopify(requeteDeDepart)) {
      console.info("[wb-plp] URL filtrée par Shopify : la grille servie est déjà la bonne, on ne repeint rien.");
      libererGrille(trouve.hote);
      return null;
    }
    if (!trouve.collectionId) {
      console.warn("[wb-plp] data-wb-collection-id absent ou non numérique : la grille Liquid est conservée telle quelle.");
      libererGrille(trouve.hote);
      return null;
    }
    const config = lireConfig(
      fenetre.WB_SEARCH,
      fenetre.WB_MARCHES_PRIX,
      fenetre.WB_MARCHES_COMPARE
    );
    definirLangue(config.locale);
    definirLibellesTheme(config.stock);
    const parametres = fenetre.WB_PARITY;
    if (!parametres) {
      libererGrille(trouve.hote);
      throw new Error("WB_PARITY absent : wb-search-params.js n'est pas chargé");
    }
    const contenu = doc.querySelector(SELECTEUR_CONTENU);
    const grille = doc.getElementById(ID_GRILLE);
    const conteneur = doc.getElementById(ID_CONTENEUR_GRILLE);
    if (!contenu || !grille || !conteneur) {
      console.warn(`[wb-plp] le DOM de la section de grille a changé (${SELECTEUR_CONTENU}, #${ID_GRILLE}, #${ID_CONTENEUR_GRILLE}) : rien n'est branché.`);
      libererGrille(trouve.hote);
      return null;
    }
    const grilleServieHtml = grille.innerHTML;
    const cartesServies = grille.children ? grille.children.length : 0;
    const triServi = doc.getElementById(ID_TRI);
    const triServiHtml = triServi ? triServi.innerHTML : "";
    const triServiValeur = triServi ? triServi.value : "";
    const { restaurer: restaurerTri } = neutraliserTri(triServi, doc);
    const depart = etatDepuisRequete(requeteDeDepart);
    contenu.insertAdjacentHTML("afterbegin", colonneHtml());
    const barre = doc.querySelector(SELECTEUR_BARRE);
    if (barre) adopterBoutonFiltres(barre, boutonTiroirHtml());
    const { restaurer: restaurerBarre } = degarnirBarreCollante(doc, fenetre);
    grille.insertAdjacentHTML("afterend", piedHtml({
      href: lienPage(requeteDeDepart, depart.page + 1),
      parPage: trouve.parPage,
      options: trouve.optionsParPage
    }));
    const navPages = conteneur.querySelector(SELECTEUR_NAV);
    const gabaritPages = doc.getElementById(ID_GABARIT2);
    const listePagesServie = navPages && navPages.querySelector(".pagination__list");
    const navPagesServie = listePagesServie ? listePagesServie.innerHTML : null;
    const pagination = conteneur.querySelector(SELECTEUR_PAGINATION);
    if (pagination) pagination.hidden = true;
    const filtresNatifs = doc.getElementById(ID_FILTRES_NATIFS);
    if (filtresNatifs) filtresNatifs.hidden = true;
    const remasquerConteneur = revelerConteneur(conteneur);
    const gabarit3 = doc.getElementById(ID_TEMPLATE_CARTE);
    const racineGabarit = gabarit3 && gabarit3.content && gabarit3.content.firstElementChild;
    configurerCarte({
      racineProduits: config.root,
      config: lireConfigCarte(racineGabarit ? racineGabarit.dataset : null),
      avis: "judgeme",
      champPrix: config.champPrix,
      champCompare: config.champCompare,
      locale: config.locale,
      /* L'hôte CDN de la boutique : sans lui, nos cartes redemandent sur
         `cdn.shopify.com` des images que la carte serveur a déjà prises sur le
         domaine du site — 245 Ko par PLP, et rien à l'écran pour le dire. */
      prefixeImages: config.filesUrl
    });
    const moteur = creerMoteur({
      base: `https://${config.host}`,
      cle: config.key,
      ttl: config.ttl
    });
    function renoncer(raison, { restaurerGrille = true } = {}) {
      console.error(`[wb-plp] ${raison} — la grille servie par Liquid est rétablie.`);
      if (restaurerGrille) grille.innerHTML = grilleServieHtml;
      const colonne = doc.getElementById(ID_COLONNE);
      if (colonne && colonne.remove) colonne.remove();
      const bouton = doc.querySelector("[data-wb-plp-basculer]");
      if (bouton && bouton.remove) bouton.remove();
      const pied = doc.getElementById(ID_SENTINELLE);
      if (pied && pied.remove) pied.remove();
      if (pagination) pagination.hidden = false;
      if (navPages && navPagesServie !== null) {
        navPages.querySelector(".pagination__list").innerHTML = navPagesServie;
        navPages.hidden = false;
      }
      if (filtresNatifs) filtresNatifs.hidden = false;
      remasquerConteneur();
      libererGrille(trouve.hote);
      restaurerTri();
      if (triServi) {
        triServi.innerHTML = triServiHtml;
        triServi.value = triServiValeur;
      }
      restaurerBarre();
    }
    let premierCycleFait = false;
    const marcheAlma = config.country === "FR" && config.currency === "EUR" && String(config.locale || "").toLowerCase().split("-")[0] === "fr" && !clientPro({ fenetre, doc });
    const groupe = marcheAlma ? groupeAlma({ doc, recherche: (_a = fenetre.location) == null ? void 0 : _a.search }) : { variante: "A", enTest: false };
    const recherche = creerRecherche({
      moteur,
      collection: config.alias,
      champPrix: config.champPrix,
      devise: config.currency,
      parametres: avecStopwords(parametres, config.locale, fenetre.WB_STOPWORDS),
      tris: fenetre.WB_SORTS,
      triMarque: fenetre.WB_TRI_MARQUE,
      filtres: fenetre.WB_FILTRES,
      facettesCreuses: fenetre.WB_FACETTES_CREUSES,
      /* Le jeu de filtres IMPOSÉ des collections transverses
         (`reference/filtres_par_collection.csv`). ⚠️ Consulté avec le périmètre
         TRADUIT (`trouve.collectionId`) : la table porte les identifiants de
         production, comme l'index, alors que Liquid rend celui de la boutique
         courante. */
      jeuxParCollection: fenetre.WB_FILTRES_COLLECTION,
      /* La surcharge de colonne de CETTE collection — ordre, libellés, style,
         liste (`reference/surcharges_filtres.json`, asset
         `wb-filtres-collection.js`). ⚠️ Même piège que le jeu imposé, et pour la
         même raison : consultée avec le périmètre TRADUIT. La table porte les
         identifiants de production, comme l'index, quand Liquid rend celui de la
         boutique courante — sur la preprod, lire avant traduction ne trouverait
         jamais rien, en HTTP 200 et sans un mot. */
      surcharge: surchargeDe(fenetre.WB_FILTRES_SURCHARGE, trouve.collectionId),
      /* Le second bloc du même asset : les réglages du FILTRE lui-même,
         valables PARTOUT (tri de ses valeurs, box de recherche, repli,
         couleurs, formes). ⛔ Oublié ici, tout cela serait saisi dans la
         console, enregistré, publié — et sans le moindre effet sur les
         pages de catégorie. Aucune erreur : l'asset est là, il est lu, et
         personne ne le passe au moteur de colonne. */
      reglages: fenetre.WB_FILTRES_REGLAGES || {},
      racine: doc,
      noeuds: NOEUDS_PLP,
      selecteurs: SELECTEURS_PLP,
      // La grille du thème peint des `<div class="f-column product-column">` :
      // un `<li>` y prendrait `display: list-item` et sa puce.
      baliseItem: "div",
      alma: marcheAlma && groupe.variante === "B",
      surAlmaEligible: marcheAlma ? creerExposition({ ...groupe, fenetre }) : null,
      perimetre: trouve.collectionId,
      // L'état du lien partagé est posé AVANT le premier cycle : le poser après
      // ferait deux lots, dont le premier montrerait la catégorie non filtrée.
      etatInitial: depart,
      parPage: trouve.parPage,
      /* ⛔ Pas de chargement automatique sur une PLP. Un défilement infini
         n'offre au crawl aucun lien vers la suite du catalogue, et c'est le
         reproche que le chantier corrige : le lot suivant ne part que sur un clic,
         et le bouton qui le déclenche est une ancre vers `?page=N+1`, que Liquid
         sait rendre. L'overlay de recherche, lui, garde son scroll infini. */
      chargementAuto: false,
      // La colonne à l'image de Globo — voir `colonne-facettes.js`.
      ...optionsColonne(),
      tiroir: () => brancherTiroir(fenetre, doc),
      /* Un moteur muet au PREMIER cycle ne doit pas laisser une page vide là où
         Liquid avait servi une grille : on renonce, et c'est le HTML servi qui
         revient dans la grille — d'où le `restaurerGrille: false`, la valeur de
         retour s'en chargeant juste après. */
      messagePanne: (erreur) => {
        if (!premierCycleFait && cartesServies > 0) {
          premierCycleFait = true;
          renoncer(
            `le moteur ne répond pas (${erreur.message})`,
            { restaurerGrille: false }
          );
          return grilleServieHtml;
        }
        return `<div class="f-column product-column rec-error">
      <strong>Les filtres sont momentanément indisponibles.</strong><br>
      <span class="wb-error-detail">${esc(erreur.message)}</span></div>`;
      },
      surRendu: (info) => {
        const suite = !!(info && info.suite);
        if (!suite && !premierCycleFait) {
          premierCycleFait = true;
          if ((info ? info.cumul : 0) === 0 && cartesServies > 0 && !aDesFiltres(requeteDeDepart) && depart.page <= 1) {
            renoncer(`le périmètre ${trouve.collectionId} ne rend aucun produit alors que la grille servie en portait ${cartesServies}`);
            return;
          }
        }
        libererGrille(trouve.hote);
        if (!suite) memoriserEtat();
        majPied(info);
        if (!suite) majNavPages(info);
        rafraichirApresRendu();
      }
    });
    function memoriserEtat() {
      const histoire = fenetre.history;
      if (!histoire || typeof histoire.replaceState !== "function") return;
      const requete = requeteDepuisEtat(
        recherche.etat,
        fenetre.location ? fenetre.location.search : "",
        trouve.optionsParPage[0]
      );
      const chemin = (fenetre.location ? fenetre.location.pathname : "") + requete;
      histoire.replaceState(histoire.state, "", chemin);
    }
    function majPied(info) {
      const total = Number((info || {}).total) || 0;
      const montres = Number((info || {}).montres) || 0;
      const progression = doc.getElementById(ID_PROGRESSION);
      if (progression) {
        progression.textContent = total ? t("progression", { montres: formaterEntier(montres), total: formaterEntier(total) }) : "";
      }
      const bouton = doc.getElementById(ID_CHARGER);
      const suivante = (info || {}).pageSuivante;
      if (!bouton || !suivante) return;
      bouton.setAttribute("href", lienPage(
        fenetre.location ? fenetre.location.search : "",
        suivante
      ));
    }
    function majNavPages(info) {
      if (!navPages) return;
      const derniere = dernierePage({
        total: (info || {}).total,
        parPage: recherche.etat.parPage,
        plafond: parametres.limit_hits
      });
      rendreNav(navPages, gabaritPages, {
        courante: recherche.etat.pageDepart,
        derniere,
        lien: (n) => lienPage(fenetre.location ? fenetre.location.search : "", n)
      });
    }
    let minuteurJudgeme;
    let minuteurStock;
    let stockSignale2 = false;
    function rafraichirApresRendu() {
      clearTimeout(minuteurJudgeme);
      minuteurJudgeme = setTimeout(() => {
        if (fenetre.jdgm && typeof fenetre.jdgm.customizeBadges === "function") {
          fenetre.jdgm.customizeBadges();
        }
      }, 60);
      clearTimeout(minuteurStock);
      minuteurStock = setTimeout(() => {
        if (fenetre.WBStock && typeof fenetre.WBStock.apply === "function") {
          fenetre.WBStock.apply();
          return;
        }
        if (stockSignale2) return;
        stockSignale2 = true;
        console.warn("[wb-plp] window.WBStock.apply indisponible : le bloc stock des cartes ne sera pas alimenté.");
      }, 60);
    }
    const selecteurParPage = doc.getElementById(ID_PAR_PAGE);
    if (selecteurParPage) {
      selecteurParPage.addEventListener("change", (e) => {
        recherche.definirParPage(Number.parseInt(e.target.value, 10));
      });
    }
    recherche.demarrer();
    return recherche;
  }
  function brancherTiroir(fenetre = window, doc = document) {
    const corps = doc.body;
    if (!corps || corps.dataset.wbPlpTiroir === "1") return;
    corps.dataset.wbPlpTiroir = "1";
    const boutons = () => doc.querySelectorAll("[data-wb-plp-basculer]");
    const appliquer = (ouvert) => {
      corps.classList.toggle(CLASSE_TIROIR_OUVERT, ouvert);
      boutons().forEach((b) => b.setAttribute("aria-expanded", ouvert ? "true" : "false"));
    };
    doc.addEventListener("click", (e) => {
      if (!e.target.closest("[data-wb-plp-basculer]")) return;
      appliquer(!corps.classList.contains(CLASSE_TIROIR_OUVERT));
    });
    doc.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (!corps.classList.contains(CLASSE_TIROIR_OUVERT)) return;
      e.stopPropagation();
      appliquer(false);
    });
    fenetre.addEventListener("resize", () => {
      if (fenetre.innerWidth > POINT_DE_RUPTURE2) appliquer(false);
    }, { passive: true });
    appliquer(false);
  }

  // frontend/js/reco.js
  var NB_RECOS = 8;
  var MAX_PAR_MARQUE = 5;
  var PALIERS_PRIX = [1.25, 1.6, 2.5];
  var TAGS_OCCASION = ["badge-stock-b", "badge-reconditionne"];
  function entreAccents2(valeur) {
    const texte = String(valeur);
    if (texte.includes("`")) {
      throw new Error(`Valeur non filtrable (accent grave) : ${texte}`);
    }
    return `\`${texte}\``;
  }
  function estOccasion(tags) {
    const liste2 = Array.isArray(tags) ? tags : [];
    return liste2.some((tag) => TAGS_OCCASION.includes(String(tag)));
  }
  function fenetrePrix(prix, facteur) {
    const valeur = Number(prix);
    const k = Number(facteur);
    if (!Number.isFinite(valeur) || valeur <= 0) return null;
    if (!Number.isFinite(k) || k <= 1) return null;
    return { de: valeur / k, a: valeur * k };
  }
  function filtreVivier({
    collectionId,
    exclureCollectionId,
    produitId,
    sku,
    occasion,
    productType,
    compatibilite
  } = {}) {
    const morceaux = [];
    const compat = [...compatibilite || []].filter(Boolean);
    if (compat.length) {
      morceaux.push(
        `ak_filtre_categories_acc:=[${compat.map(entreAccents2).join(",")}]`
      );
    }
    if (collectionId) {
      morceaux.push(`collection_ids:=${entreAccents2(collectionId)}`);
    } else if (productType) {
      morceaux.push(`product_type:=${entreAccents2(productType)}`);
    }
    if (exclureCollectionId) {
      morceaux.push(`collection_ids:!=${entreAccents2(exclureCollectionId)}`);
    }
    if (produitId) morceaux.push(`id:!=${entreAccents2(produitId)}`);
    if (sku) morceaux.push(`sku:!=${entreAccents2(sku)}`);
    if (!occasion) {
      morceaux.push(`tags:!=[${TAGS_OCCASION.map(entreAccents2).join(",")}]`);
    }
    return morceaux.join(" && ");
  }
  var TRI = "inventory_available:desc,brand_tier(missing_values: last):desc,ranking_score(missing_values: last):desc";
  var GROUPE = { group_by: "vendor", group_limit: MAX_PAR_MARQUE };
  function lotDeReco({
    collection,
    collectionId,
    parentCollectionId,
    produitId,
    sku,
    prix,
    occasion,
    productType,
    compatibilite,
    limite = NB_RECOS
  } = {}) {
    const commun = {
      collection,
      q: "*",
      query_by: "title",
      per_page: Math.max(limite, NB_RECOS),
      sort_by: TRI,
      ...GROUPE
    };
    const recherche = (perimetre, facteur) => {
      const fenetre = fenetrePrix(prix, facteur);
      const filtres = [filtreVivier({
        ...perimetre,
        produitId,
        sku,
        occasion,
        compatibilite
      })];
      if (fenetre) {
        filtres.push(
          `price:>=${arrondi(fenetre.de)}`,
          `price:<=${arrondi(fenetre.a)}`
        );
      }
      return { ...commun, filter_by: filtres.filter(Boolean).join(" && ") };
    };
    const lots = PALIERS_PRIX.map((f) => recherche({ collectionId, productType }, f));
    if (parentCollectionId && parentCollectionId !== collectionId) {
      lots.push(...PALIERS_PRIX.map((f) => recherche(
        { collectionId: parentCollectionId, exclureCollectionId: collectionId },
        f
      )));
    }
    if (productType && collectionId) {
      lots.push(recherche(
        { productType, exclureCollectionId: collectionId },
        PALIERS_PRIX[PALIERS_PRIX.length - 1]
      ));
    }
    return lots;
  }
  function arrondi(valeur) {
    return Math.round(valeur * 100) / 100;
  }
  function documentsDe(resultat) {
    const bloc2 = resultat || {};
    if (Array.isArray(bloc2.grouped_hits)) {
      return bloc2.grouped_hits.flatMap((groupe) => (groupe || {}).hits || []);
    }
    return Array.isArray(bloc2.hits) ? bloc2.hits : [];
  }
  function selectionner(resultats, { produitId, sku, limite = NB_RECOS } = {}) {
    var _a, _b;
    const vus = /* @__PURE__ */ new Set();
    const parMarque = /* @__PURE__ */ new Map();
    const retenus = [];
    const courant = String(produitId != null ? produitId : "");
    const skuCourant = String(sku != null ? sku : "").trim();
    for (const resultat of resultats || []) {
      for (const hit of documentsDe(resultat)) {
        const doc = (hit || {}).document;
        if (!doc || !doc.id) continue;
        const id = String(doc.id);
        if (id === courant || vus.has(id)) continue;
        if (skuCourant && String((_a = doc.sku) != null ? _a : "") === skuCourant) continue;
        vus.add(id);
        const marque = String((_b = doc.vendor) != null ? _b : "");
        if (marque) {
          const deja = parMarque.get(marque) || 0;
          if (deja >= MAX_PAR_MARQUE) continue;
          parMarque.set(marque, deja + 1);
        }
        retenus.push(doc);
        if (retenus.length >= limite) return retenus;
      }
    }
    return retenus;
  }

  // frontend/js/accessoires.js
  var NB_ACCESSOIRES = 10;
  var MAX_PAR_CATEGORIE = 2;
  var PLANCHER_PLAFOND_PRIX = 30;
  var PAR_PAGE = MAX_PAR_CATEGORIE + 2;
  function categoriesPour(productType, table) {
    const type = String(productType || "").trim();
    if (!type || !table || typeof table !== "object") return [];
    const ligne = table[type];
    if (!Array.isArray(ligne)) return [];
    return ligne.map((entree) => Array.isArray(entree) ? entree[0] : entree).filter(Boolean).map(String);
  }
  function plafondPrix(prix) {
    const valeur = Number(prix);
    if (!Number.isFinite(valeur) || valeur <= 0) return null;
    return Math.max(valeur, PLANCHER_PLAFOND_PRIX);
  }
  function arrondi2(valeur) {
    return Math.round(valeur * 100) / 100;
  }
  function filtreAccessoire({ categorie, sku, occasion, plafond, compatibilite } = {}) {
    const morceaux = [];
    const compat = [...compatibilite || []].filter(Boolean);
    if (compat.length) {
      morceaux.push(`ak_filtre_categories_acc:=[${compat.map(entreAccents2).join(",")}]`);
    }
    morceaux.push(`product_type:=${entreAccents2(categorie)}`);
    if (sku) morceaux.push(`sku:!=${entreAccents2(sku)}`);
    if (plafond !== null && plafond !== void 0) morceaux.push(`price:<=${arrondi2(plafond)}`);
    if (!occasion) {
      morceaux.push(`tags:!=[${TAGS_OCCASION.map(entreAccents2).join(",")}]`);
    }
    return morceaux.join(" && ");
  }
  function lotAccessoires({
    collection,
    productType,
    table,
    prix,
    sku,
    tags,
    compatibilite,
    skusCdp,
    limite = NB_ACCESSOIRES
  } = {}) {
    const places = Math.max(1, Number.parseInt(limite, 10) || NB_ACCESSOIRES);
    const commun = { collection, q: "*", query_by: "title" };
    const lots = [];
    const manuels = [...skusCdp || []].map((s) => String(s).trim()).filter(Boolean);
    if (manuels.length) {
      lots.push({
        ...commun,
        per_page: manuels.length,
        filter_by: `sku:=[${manuels.map(entreAccents2).join(",")}]`
      });
    }
    const occasion = estOccasion(tags);
    const plafond = plafondPrix(prix);
    for (const categorie of categoriesPour(productType, table).slice(0, places)) {
      lots.push({
        ...commun,
        per_page: PAR_PAGE,
        sort_by: TRI,
        group_by: "vendor",
        group_limit: 1,
        filter_by: filtreAccessoire({ categorie, sku, occasion, plafond, compatibilite })
      });
    }
    return lots;
  }
  function selectionnerAccessoires(resultats, {
    skusCdp,
    sku,
    limite = NB_ACCESSOIRES
  } = {}) {
    const places = Math.max(1, Number.parseInt(limite, 10) || NB_ACCESSOIRES);
    const courant = String(sku != null ? sku : "").trim();
    const vus = /* @__PURE__ */ new Set();
    const retenus = [];
    const ajouter = (doc) => {
      var _a, _b;
      if (!doc) return false;
      const cle = String((_a = doc.sku) != null ? _a : "") || String((_b = doc.id) != null ? _b : "");
      if (!cle || cle === courant || vus.has(cle)) return false;
      vus.add(cle);
      retenus.push(doc);
      return true;
    };
    const documents = (resultat) => documentsDe(resultat).map((hit) => (hit || {}).document).filter(Boolean);
    const reste = [...resultats || []];
    const manuels = [...skusCdp || []].map((s) => String(s).trim()).filter(Boolean);
    if (manuels.length) {
      const parSku = new Map(documents(reste.shift()).map((d) => {
        var _a;
        return [String((_a = d.sku) != null ? _a : ""), d];
      }));
      for (const s of manuels) {
        if (retenus.length >= places) return retenus;
        ajouter(parSku.get(s));
      }
    }
    const files = reste.map(documents);
    const pris = files.map(() => 0);
    let progres = true;
    while (retenus.length < places && progres) {
      progres = false;
      for (let i = 0; i < files.length && retenus.length < places; i++) {
        if (pris[i] >= MAX_PAR_CATEGORIE) continue;
        while (files[i].length) {
          if (ajouter(files[i].shift())) {
            pris[i] += 1;
            progres = true;
            break;
          }
        }
      }
    }
    return retenus;
  }

  // frontend/js/apres-rendu.js
  var DELAI_MS = 60;
  var stockSignale = false;
  function rafraichirCartes(fenetre, { delai = DELAI_MS } = {}) {
    const planifier = typeof fenetre.setTimeout === "function" ? fenetre.setTimeout.bind(fenetre) : globalThis.setTimeout;
    planifier(() => {
      if (fenetre.jdgm && typeof fenetre.jdgm.customizeBadges === "function") {
        fenetre.jdgm.customizeBadges();
      }
      if (fenetre.WBStock && typeof fenetre.WBStock.apply === "function") {
        fenetre.WBStock.apply();
        return;
      }
      if (stockSignale) return;
      stockSignale = true;
      console.warn("[wb-search] window.WBStock.apply indisponible : le bloc stock des cartes ne sera pas alimenté.");
    }, delai);
  }

  // frontend/js/reco-pdp.js
  var SELECTEUR_HOTE2 = "wb-reco-produits[data-wb-reco]";
  var SELECTEUR_GABARIT = "template[data-wb-reco-gabarit]";
  var SELECTEUR_REPLI = "[data-reco-fallback]";
  var ID_TEMPLATE_CARTE2 = "algolia-card-tpl";
  var MARQUEUR_CARTES = "<!--WB_CARTES-->";
  var MARQUEUR_NAV = "<!--WB_NAV-->";
  var MARQUEUR_NAV_LISTE = "<!--WB_NAV_LISTE-->";
  var MARQUEUR_NAV_MD = "<!--WB_NAV_MD-->";
  var MARQUEUR_NAV_LG = "<!--WB_NAV_LG-->";
  var CLASSES_SLIDE = "f-column swiper-slide";
  var MODES = Object.freeze(["lies", "accessoires"]);
  var IDENTIFIANT2 = /^\d+$/;
  var liste = (brut) => String(brut || "").split("|").map((s) => s.trim()).filter(Boolean);
  var identifiant = (brut, table) => {
    const valeur = String(brut || "").trim();
    return IDENTIFIANT2.test(valeur) ? perimetreDansIndex(valeur, table) : "";
  };
  function lireContexte(hote, tableCollections = null) {
    const d = hote && hote.dataset || {};
    const mode = String(d.wbReco || "");
    if (!MODES.includes(mode)) {
      throw new Error(`data-wb-reco="${mode}" inconnu (attendu : ${MODES.join(" | ")})`);
    }
    const prix = Number(d.wbPrix);
    const tags = liste(d.wbTags);
    const limite = Number.parseInt(String(d.wbLimite || ""), 10);
    return {
      mode,
      sku: String(d.wbSku || "").trim(),
      prix: Number.isFinite(prix) && prix > 0 ? prix : null,
      tags,
      occasion: estOccasion(tags),
      productType: String(d.wbType || "").trim(),
      collectionId: identifiant(d.wbCollectionId, tableCollections),
      parentCollectionId: identifiant(d.wbParentId, tableCollections),
      compatibilite: liste(d.wbCompat),
      skusCdp: liste(d.wbSkusCdp),
      limite: Number.isInteger(limite) && limite > 0 ? limite : null
    };
  }
  function construireLot(ctx, { collection, tableAccessoires } = {}) {
    if (ctx.mode === "accessoires") {
      return lotAccessoires({
        collection,
        productType: ctx.productType,
        table: tableAccessoires,
        prix: ctx.prix,
        sku: ctx.sku,
        tags: ctx.tags,
        compatibilite: ctx.compatibilite,
        skusCdp: ctx.skusCdp,
        limite: ctx.limite || NB_ACCESSOIRES
      });
    }
    if (!ctx.collectionId && !ctx.productType) return [];
    return lotDeReco({
      collection,
      collectionId: ctx.collectionId || void 0,
      parentCollectionId: ctx.parentCollectionId || void 0,
      sku: ctx.sku,
      prix: ctx.prix,
      occasion: ctx.occasion,
      productType: ctx.productType || void 0,
      compatibilite: ctx.compatibilite,
      limite: ctx.limite || NB_RECOS
    });
  }
  function selectionnerPour(ctx, resultats) {
    if (ctx.mode === "accessoires") {
      return selectionnerAccessoires(resultats, {
        skusCdp: ctx.skusCdp,
        sku: ctx.sku,
        limite: ctx.limite || NB_ACCESSOIRES
      });
    }
    return selectionner(resultats, { sku: ctx.sku, limite: ctx.limite || NB_RECOS });
  }
  function attributNumerique(html, nom) {
    const trouve = new RegExp(`${nom}="(\\d+)"`).exec(html);
    return trouve ? Number.parseInt(trouve[1], 10) : null;
  }
  function composer(gabaritHtml, cartes, { nb, colonnes = null, tablette = null } = {}) {
    var _a, _b, _c, _d;
    const html = String(gabaritHtml || "");
    if (!html.includes(MARQUEUR_CARTES)) {
      throw new Error(`gabarit du carrousel sans ${MARQUEUR_CARTES}`);
    }
    const bureau = (_c = (_b = (_a = colonnes != null ? colonnes : attributNumerique(html, "data-wb-colonnes")) != null ? _a : attributNumerique(html, "data-columns-desktop")) != null ? _b : attributNumerique(html, "data-items")) != null ? _c : nb;
    const tab = (_d = tablette != null ? tablette : attributNumerique(html, "data-tablet-items")) != null ? _d : bureau;
    const deborde = nb > bureau;
    return html.replace(MARQUEUR_CARTES, () => cartes.join("")).replace(/data-total-items="[^"]*"/, `data-total-items="${nb}"`).replace(/data-enable-slider-desktop="[^"]*"/, `data-enable-slider-desktop="${deborde}"`).replace(MARQUEUR_NAV, deborde ? " md:flex" : "").replace(MARQUEUR_NAV_LISTE, deborde ? "" : " md:hidden").replace(MARQUEUR_NAV_MD, nb > tab ? " md:flex" : "").replace(MARQUEUR_NAV_LG, deborde ? " lg:flex" : " lg:hidden");
  }
  function masquerSiVide(hote) {
    if (hote.querySelector && hote.querySelector(SELECTEUR_REPLI)) return;
    const section = hote.closest ? hote.closest(".shopify-section") : null;
    if (section) section.hidden = true;
  }
  async function brancherRecoPDP(fenetre, doc, { rendre = carte, moteur = null } = {}) {
    const hotes = Array.from(doc.querySelectorAll && doc.querySelectorAll(SELECTEUR_HOTE2) || []);
    if (!hotes.length) return null;
    const config = lireConfig(
      fenetre.WB_SEARCH,
      fenetre.WB_MARCHES_PRIX,
      fenetre.WB_MARCHES_COMPARE
    );
    definirLangue(config.locale);
    definirLibellesTheme(config.stock);
    const gabaritCarte = doc.getElementById(ID_TEMPLATE_CARTE2);
    const racine = gabaritCarte && gabaritCarte.content && gabaritCarte.content.firstElementChild;
    configurerCarte({
      racineProduits: config.root,
      config: lireConfigCarte(racine ? racine.dataset : null),
      avis: "judgeme",
      champPrix: config.champPrix,
      champCompare: config.champCompare,
      locale: config.locale,
      /* L'hôte CDN de la boutique : sans lui, nos cartes redemandent sur
         `cdn.shopify.com` des images que la carte serveur a déjà prises sur le
         domaine du site — 245 Ko par PLP, et rien à l'écran pour le dire. */
      prefixeImages: config.filesUrl
    });
    const moteurReel = moteur || creerMoteur({
      base: `https://${config.host}`,
      cle: config.key,
      ttl: config.ttl
    });
    const bilan = { lies: 0, accessoires: 0 };
    for (const hote of hotes) {
      let mode = String((hote.dataset || {}).wbReco || "?");
      try {
        const ctx = lireContexte(hote, fenetre.WB_COLLECTIONS);
        mode = ctx.mode;
        const lot = construireLot(ctx, {
          collection: config.alias,
          tableAccessoires: fenetre.WB_ACCESSOIRES
        });
        if (!lot.length) {
          masquerSiVide(hote);
          continue;
        }
        const resultats = await moteurReel.multi(lot, { critiques: [] });
        const produits = selectionnerPour(ctx, resultats);
        if (!produits.length) {
          masquerSiVide(hote);
          continue;
        }
        const gabarit3 = hote.querySelector(SELECTEUR_GABARIT);
        if (!gabarit3) throw new Error(`gabarit du carrousel absent (${SELECTEUR_GABARIT})`);
        hote.innerHTML = composer(
          gabarit3.innerHTML,
          produits.map((d) => rendre(d, null, "div", CLASSES_SLIDE)),
          { nb: produits.length }
        );
        rafraichirCartes(fenetre);
        bilan[mode] = (bilan[mode] || 0) + produits.length;
      } catch (erreur) {
        console.error(`[wb-reco] bloc « ${mode} » non rendu : ${erreur.message}`);
      }
    }
    return bilan;
  }

  // frontend/js/memoire-recherche.js
  var CLE_REQUETE = "q";
  function hashDepuisEtat(etat) {
    const q = String((etat || {}).q || "").trim();
    if (!q) return "";
    const filtres = requeteDepuisEtat({ ...etat, parPage: null, pageDepart: 1 }, "");
    const p = new URLSearchParams();
    p.set(CLE_REQUETE, q);
    for (const [cle, valeur] of new URLSearchParams(filtres.replace(/^\?/, ""))) {
      p.append(cle, valeur);
    }
    return `#${p.toString()}`;
  }
  function etatDepuisHash(hash) {
    const brut = String(hash || "").replace(/^#/, "");
    if (!brut) return null;
    const q = (new URLSearchParams(brut).get(CLE_REQUETE) || "").trim();
    if (!q) return null;
    const { selection, prix, note, tri } = etatDepuisRequete(brut);
    return { q, selection, prix, note, tri };
  }
  function creerMemoire({
    fenetre = window,
    ouvrir = () => {
    },
    fermer = () => {
    },
    estOuvert = () => false
  } = {}) {
    let aPousse = false;
    const hashCourant = () => (fenetre.location || {}).hash || "";
    const urlBase = () => {
      const l = fenetre.location || {};
      return `${l.pathname || ""}${l.search || ""}`;
    };
    const histoire = () => fenetre.history || {};
    const peutEcrire = () => typeof histoire().pushState === "function" && typeof histoire().replaceState === "function";
    function etatInitial() {
      aPousse = false;
      return etatDepuisHash(hashCourant());
    }
    function memoriser(etat) {
      if (!peutEcrire()) return;
      const cible = hashDepuisEtat(etat);
      if (cible === hashCourant()) return;
      if (!cible) {
        histoire().replaceState(histoire().state, "", urlBase());
        return;
      }
      const url = urlBase() + cible;
      if (aPousse) {
        histoire().replaceState(histoire().state, "", url);
        return;
      }
      histoire().pushState(histoire().state, "", url);
      aPousse = true;
    }
    function oublier() {
      if (!peutEcrire()) return;
      if (aPousse) {
        aPousse = false;
        if (typeof histoire().back === "function") histoire().back();
        return;
      }
      if (!etatDepuisHash(hashCourant())) return;
      histoire().replaceState(histoire().state, "", urlBase());
    }
    function synchroniser() {
      const etat = etatDepuisHash(hashCourant());
      if (etat) {
        if (!estOuvert()) ouvrir(etat);
        return;
      }
      if (estOuvert()) fermer();
    }
    function brancher() {
      if (typeof fenetre.addEventListener !== "function") return;
      fenetre.addEventListener("popstate", () => {
        aPousse = false;
        synchroniser();
      });
      fenetre.addEventListener("pageshow", (e) => {
        if (e && e.persisted) synchroniser();
      });
    }
    return Object.freeze({ etatInitial, memoriser, oublier, synchroniser, brancher });
  }

  // frontend/js/theme.js
  var DEBOUNCE_MS = 150;
  var DOCK_MIN_WIDTH = 1024;
  var SEARCH_FORM_SELECTOR = 'form[action$="/search"]';
  var HEADER_SELECTOR = '.header-section header, header[is="sticky-header"], header[is="basic-header"], header.header';
  var ID_OVERLAY = "wb-overlay";
  var ID_TEMPLATE_OVERLAY = "algolia-overlay-tpl";
  var ID_TEMPLATE_CARTE3 = "algolia-card-tpl";
  function estFormulaireRecherche(cible, selecteur = SEARCH_FORM_SELECTOR) {
    return !!(cible && typeof cible.matches === "function" && cible.matches(selecteur));
  }
  function champVisible(candidats) {
    return Array.from(candidats || []).find((el) => el && el.id !== "wb-overlay-input" && el.offsetParent !== null) || null;
  }
  function basVisibleDuHeader(header) {
    if (!header) return 0;
    const blocs = [".header__top", ".header__bottom"].map((sel) => header.querySelector(sel)).filter(Boolean);
    const bas = Math.max(...(blocs.length ? blocs : [header]).map((el) => el.getBoundingClientRect().bottom));
    return Math.max(0, Math.round(bas));
  }
  function intercepterDefilementHeader(e, { ouvert, estDocke, conteneurResultats } = {}) {
    if (!ouvert || !estDocke || !e || e.ctrlKey) return false;
    const cible = e.target && e.target.nodeType === 3 ? e.target.parentElement : e.target;
    if (cible && typeof cible.closest === "function") {
      if (cible.closest(".wb-overlay-results") || cible.closest(".wb-overlay-facets")) {
        return false;
      }
    }
    if (typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (conteneurResultats && e.deltaY) {
      conteneurResultats.scrollTop += e.deltaY;
    }
    return true;
  }
  function brancherTheme(fenetre = window, doc = document) {
    if (doc.getElementById(ID_OVERLAY)) return null;
    const config = lireConfig(
      fenetre.WB_SEARCH,
      fenetre.WB_MARCHES_PRIX,
      fenetre.WB_MARCHES_COMPARE
    );
    const parametres = fenetre.WB_PARITY;
    if (!parametres) throw new Error("WB_PARITY absent : wb-search-params.js n'est pas chargé");
    definirLangue(config.locale);
    definirLibellesTheme(config.stock);
    const moteur = creerMoteur({
      base: `https://${config.host}`,
      cle: config.key,
      ttl: config.ttl
    });
    const dock = fenetre.matchMedia(`(min-width: ${DOCK_MIN_WIDTH}px)`);
    let overlay = null;
    let ouvert = false;
    let dernierFocus = null;
    let empecherOuvertureAuFocus = false;
    let minuteur;
    const memoire = creerMemoire({
      fenetre,
      ouvrir: (etat) => {
        restaurer(etat);
      },
      fermer: () => fermer({ historique: false }),
      estOuvert: () => ouvert
    });
    const etatRestaure = memoire.etatInitial();
    const gabarit3 = doc.getElementById(ID_TEMPLATE_CARTE3);
    const racineGabarit = gabarit3 && gabarit3.content && gabarit3.content.firstElementChild;
    configurerCarte({
      racineProduits: config.root,
      config: lireConfigCarte(racineGabarit ? racineGabarit.dataset : null),
      avis: "judgeme",
      champPrix: config.champPrix,
      champCompare: config.champCompare,
      locale: config.locale,
      /* L'hôte CDN de la boutique : sans lui, nos cartes redemandent sur
         `cdn.shopify.com` des images que la carte serveur a déjà prises sur le
         domaine du site — 245 Ko par PLP, et rien à l'écran pour le dire. */
      prefixeImages: config.filesUrl
    });
    const recherche = creerRecherche({
      moteur,
      collection: config.alias,
      champPrix: config.champPrix,
      devise: config.currency,
      parametres: avecStopwords(parametres, config.locale, fenetre.WB_STOPWORDS),
      tris: fenetre.WB_SORTS,
      triMarque: fenetre.WB_TRI_MARQUE,
      filtres: fenetre.WB_FILTRES,
      facettesCreuses: fenetre.WB_FACETTES_CREUSES,
      racine: doc,
      etatInitial: etatRestaure || {},
      /* La colonne de l'overlay est celle des pages de catégories (demandé le
         05/09) : mêmes blocs, mêmes deux seuils d'avis, mêmes ronds de couleur.
         Un seul écart, depuis le 26/09 : pas de Type de produit, la descente par
         niveau le rend redondant (`colonne-facettes.MASQUES_OVERLAY`). L'APPARENCE
         vit dans `theme/wb-facettes.css`, scopée sous les deux conteneurs. */
      /* Les réglages du FILTRE lui-même, les mêmes que sur une PLP : un filtre
         réglé « déplié » ou « sans box » qui ne le serait QUE sur les pages de
         catégorie donnerait deux colonnes différentes pour le même filtre, sur
         la même page — l'overlay s'ouvre depuis une PLP. ⚠️ Pas de surcharge de
         COLLECTION ici : une recherche n'en a pas, et lire la table avec un
         périmètre vide appliquerait le réglage d'une autre page. La seule
         `surcharge` passée est le masque fixe d'`optionsOverlay()`. */
      reglages: fenetre.WB_FILTRES_REGLAGES || {},
      ...optionsOverlay(),
      messagePanne: (erreur) => `<li class="rec-error">
      <strong>${esc(t("indisponible"))}</strong><br>
      <span class="wb-error-detail">${esc(erreur.message)}</span></li>`,
      surRendu: rafraichirApresRendu
    });
    let minuteurJudgeme;
    let minuteurStock;
    let stockSignale2 = false;
    function rafraichirApresRendu() {
      if (ouvert) memoire.memoriser(recherche.etat);
      clearTimeout(minuteurJudgeme);
      minuteurJudgeme = setTimeout(() => {
        if (fenetre.jdgm && typeof fenetre.jdgm.customizeBadges === "function") {
          fenetre.jdgm.customizeBadges();
        }
      }, 60);
      clearTimeout(minuteurStock);
      minuteurStock = setTimeout(() => {
        if (fenetre.WBStock && typeof fenetre.WBStock.apply === "function") {
          fenetre.WBStock.apply();
          return;
        }
        if (stockSignale2) return;
        stockSignale2 = true;
        console.warn("[wb-search] window.WBStock.apply indisponible : le bloc stock des cartes ne sera pas alimenté.");
      }, 60);
    }
    function construireOverlay() {
      const tpl = doc.getElementById(ID_TEMPLATE_OVERLAY);
      if (!tpl) throw new Error(`<template id="${ID_TEMPLATE_OVERLAY}"> absent du thème`);
      const conteneur = doc.createElement("div");
      conteneur.id = ID_OVERLAY;
      conteneur.setAttribute("aria-hidden", "true");
      conteneur.appendChild(tpl.content.cloneNode(true));
      doc.body.appendChild(conteneur);
      return conteneur;
    }
    function verrouiller() {
      if (doc.body.classList.contains("wb-overlay-locked")) return;
      doc.body.classList.add("wb-overlay-locked");
      doc.body.style.top = `-${fenetre.scrollY}px`;
    }
    function deverrouiller() {
      if (!doc.body.classList.contains("wb-overlay-locked")) return;
      const y = Math.abs(parseInt(doc.body.style.top || "0", 10));
      doc.body.classList.remove("wb-overlay-locked");
      doc.body.style.top = "";
      fenetre.scrollTo(0, y);
    }
    const header = () => doc.querySelector(HEADER_SELECTOR);
    const basDuHeader = () => basVisibleDuHeader(header());
    const sectionHeader = () => {
      const el = header();
      return el ? el.closest(".shopify-section") || el : null;
    };
    const estDocke = () => doc.body.classList.contains("wb-overlay-docked");
    function placer() {
      const docke = dock.matches;
      doc.body.classList.toggle("wb-overlay-docked", docke);
      const panneau = overlay.querySelector(".wb-overlay-panel");
      if (panneau) panneau.setAttribute("aria-modal", docke ? "false" : "true");
      const section = sectionHeader();
      if (section) section.classList.toggle("wb-header-above-overlay", docke);
      if (docke) {
        deverrouiller();
        doc.body.classList.add("header-pinned");
        if (basDuHeader() > 0)
          overlay.style.setProperty("--wb-overlay-top", `${basDuHeader()}px`);
      } else {
        overlay.style.removeProperty("--wb-overlay-top");
        verrouiller();
      }
    }
    const champsHeader = () => Array.from(doc.querySelectorAll(
      `${SEARCH_FORM_SELECTOR} input[type="search"], ${SEARCH_FORM_SELECTOR} input[name="q"]`
    ));
    async function ouvrir(requete) {
      if (!overlay) overlay = construireOverlay();
      fermerRecherchNative();
      if (!ouvert) {
        dernierFocus = doc.activeElement;
        overlay.classList.add("wb-overlay--open");
        overlay.setAttribute("aria-hidden", "false");
        placer();
        ouvert = true;
        fenetre.requestAnimationFrame(() => {
          const cible = estDocke() ? champVisible(champsHeader()) : doc.getElementById("wb-overlay-input");
          if (!cible || doc.activeElement === cible) return;
          empecherOuvertureAuFocus = true;
          try {
            cible.focus({ preventScroll: true });
          } catch (e) {
          }
          empecherOuvertureAuFocus = false;
        });
      }
      if (requete !== void 0) recherche.definirRequete(requete);
      if (!recherche.demarree) {
        await recherche.demarrer();
      } else if (requete !== void 0) {
        recherche.definirRequete(requete, { immediat: true });
      }
    }
    async function restaurer(etat) {
      if (!etat) return;
      recherche.definirEtat(etat);
      champsHeader().forEach((el) => {
        el.value = etat.q;
      });
      try {
        await ouvrir();
      } catch (erreur) {
        console.warn("[wb-search] recherche non restaurée :", erreur.message);
      }
    }
    function fermer({ historique = true } = {}) {
      if (!overlay || !ouvert) return;
      recherche.quitter();
      fermerRecherchNative();
      overlay.classList.remove("wb-overlay--open");
      overlay.setAttribute("aria-hidden", "true");
      doc.body.classList.remove("wb-facets-open", "wb-overlay-docked");
      overlay.style.removeProperty("--wb-overlay-top");
      const section = sectionHeader();
      if (section) section.classList.remove("wb-header-above-overlay");
      deverrouiller();
      ouvert = false;
      if (historique) memoire.oublier();
      if (dernierFocus && dernierFocus.focus) {
        empecherOuvertureAuFocus = true;
        try {
          dernierFocus.focus({ preventScroll: true });
        } catch (e) {
        }
        empecherOuvertureAuFocus = false;
      }
    }
    function fermerRecherchNative() {
      doc.querySelectorAll("predictive-search").forEach((ps) => {
        try {
          if (typeof ps.close === "function") {
            ps.close();
            return;
          }
        } catch (e) {
        }
        const panneau = ps.querySelector(".search__content");
        if (panneau) panneau.classList.add("hidden");
        const champ = ps.querySelector('input[type="search"], input[name="q"]');
        if (champ) champ.setAttribute("aria-expanded", "false");
      });
    }
    function pieger(e) {
      if (!ouvert || e.key !== "Tab" || estDocke() || !overlay) return;
      const focusables = overlay.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const premier = focusables[0];
      const dernier = focusables[focusables.length - 1];
      if (e.shiftKey && doc.activeElement === premier) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && doc.activeElement === dernier) {
        e.preventDefault();
        premier.focus();
      }
    }
    const estChampHeader = (el) => el && el.tagName === "INPUT" && el.id !== "wb-overlay-input" && typeof el.closest === "function" && !!el.closest(SEARCH_FORM_SELECTOR);
    doc.addEventListener("input", (e) => {
      if (!estChampHeader(e.target)) return;
      e.stopPropagation();
      const v = e.target.value.trim();
      clearTimeout(minuteur);
      minuteur = setTimeout(() => {
        if (v.length >= config.minChars) ouvrir(v);
      }, DEBOUNCE_MS);
    }, true);
    doc.addEventListener("submit", (e) => {
      if (!estFormulaireRecherche(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    doc.addEventListener("focus", (e) => {
      if (!estChampHeader(e.target)) return;
      e.stopPropagation();
      if (empecherOuvertureAuFocus) return;
      const v = e.target.value.trim();
      if (v.length >= config.minChars) ouvrir(v);
    }, true);
    doc.addEventListener("click", (e) => {
      if (e.target.closest("[data-wb-close]")) {
        fermer();
        return;
      }
      if (e.target.closest("[data-wb-reset]")) {
        champsHeader().forEach((el) => {
          el.value = "";
        });
        return;
      }
      if (e.target.closest("[data-wb-toggle-facets]")) {
        doc.body.classList.toggle("wb-facets-open");
      }
    });
    doc.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && ouvert) fermer();
      pieger(e);
    });
    doc.addEventListener("input", (e) => {
      if (e.target && e.target.id === "wb-overlay-input") {
        champsHeader().forEach((el) => {
          el.value = e.target.value;
        });
      }
    });
    let raf = 0;
    const redocker = () => {
      if (!ouvert || raf) return;
      raf = fenetre.requestAnimationFrame(() => {
        raf = 0;
        if (overlay && ouvert) placer();
      });
    };
    fenetre.addEventListener("resize", redocker, { passive: true });
    fenetre.addEventListener("scroll", redocker, { passive: true });
    doc.addEventListener("transitionend", (e) => {
      const cible = e.target;
      if (e.propertyName === "transform" && cible && cible.classList && cible.classList.contains("header__bottom")) redocker();
    }, true);
    const surMolette = (e) => {
      intercepterDefilementHeader(e, {
        ouvert,
        estDocke: estDocke(),
        conteneurResultats: overlay ? overlay.querySelector(".wb-overlay-results") : null
      });
    };
    fenetre.addEventListener("wheel", surMolette, { passive: false });
    fenetre.addEventListener("touchmove", (e) => {
      intercepterDefilementHeader(e, {
        ouvert,
        estDocke: estDocke(),
        conteneurResultats: null
      });
    }, { passive: false });
    memoire.brancher();
    if (etatRestaure) restaurer(etatRestaure);
    return Object.freeze({ ouvrir, fermer, restaurer, recherche, memoire, config });
  }
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const demarrer = () => {
      try {
        window.WBSearch = brancherTheme(window, document);
      } catch (erreur) {
        console.error("[wb-search] non branché :", erreur.message);
      }
      try {
        window.WBPlp = brancherPLP(window, document);
      } catch (erreur) {
        console.error("[wb-plp] non branché :", erreur.message);
        libererGrille(document.querySelector(SELECTEUR_HOTE));
      }
      brancherRecoPDP(window, document).then((bilan) => {
        window.WBReco = bilan;
      }).catch((erreur) => {
        console.error("[wb-reco] non branché :", erreur.message);
      });
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", demarrer, { once: true });
    } else {
      demarrer();
    }
  }
})();
